import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import clsx from "clsx";
import css from "./StateDot.module.css";
import css$1 from "./DisclosureRow.module.css";
import css$2 from "./Button.module.css";
import css$3 from "./Pill.module.css";
import css$4 from "./Input.module.css";
import { Fragment as Fragment$1, cloneElement, createElement, memo, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import css$5 from "./Menu.module.css";
import css$6 from "./HoverCard.module.css";
import css$7 from "./Modal.module.css";
import css$8 from "./OnboardingSurface.module.css";
import css$9 from "./RiskConfirmation.module.css";
import css$10 from "./ConnectionBanner.module.css";
import css$11 from "./Tooltip.module.css";
import css$12 from "./Toast.module.css";
import css$13 from "./JsonTree.module.css";
import Anser from "anser";
import css$14 from "./TerminalBlock.module.css";
import { createCssVariablesTheme, createHighlighterCoreSync } from "shiki/core";
import { createJavaScriptRegexEngine, defaultJavaScriptRegexConstructor } from "shiki/engine/javascript";
import langTs from "@shikijs/langs/typescript";
import langBash from "@shikijs/langs/shellscript";
import langJson from "@shikijs/langs/json";
import css$15 from "./ReadBlock.module.css";
import css$16 from "./DiffBlock.module.css";
import css$17 from "./SearchBlock.module.css";
import { fromMarkdown } from "mdast-util-from-markdown";
import { gfmFromMarkdown } from "mdast-util-gfm";
import { mathFromMarkdown } from "mdast-util-math";
import { gfm } from "micromark-extension-gfm";
import { math } from "micromark-extension-math";
import { attention } from "micromark-core-commonmark";
import { markdownLineEnding, unicodePunctuation } from "micromark-util-character";
import { classifyCharacter } from "micromark-util-classify-character";
import { codes, constants, types } from "micromark-util-symbol";
import { factorySpace } from "micromark-factory-space";
import { normalizeUri } from "micromark-util-sanitize-uri";
import css$18 from "./markdown/CodeBlock.module.css";
import katex from "katex";
import css$19 from "./markdown/MarkdownText.module.css";
import "katex/dist/katex.min.css";
import css$20 from "./WebBlock.module.css";
import css$21 from "./markdown/JsonBlock.module.css";
import css$22 from "./markdown/MessageText.module.css";
//#region lib/types/StateDot.js
/** Outer 3x3 matrix cells (2px pixels on a 10px grid), clockwise from top-left. */
const MATRIX_CELLS = [
	[0, 0],
	[4, 0],
	[8, 0],
	[8, 4],
	[8, 8],
	[4, 8],
	[0, 8],
	[0, 4]
];
/**
* Render a state dot.
* @param props.state - which of the four states to show.
* @param props.size - outer diameter in px (default 10, the figma size).
* @param props.className - extra class for layout placement.
* @returns the dot element (aria-hidden; pair with text for accessibility).
*/
function StateDot({ state, size = 10, className }) {
	if (state === "ongoing") return jsx("svg", {
		className: clsx(css.matrix, className),
		"data-state": "ongoing",
		width: size,
		height: size,
		viewBox: "0 0 10 10",
		shapeRendering: "crispEdges",
		"aria-hidden": "true",
		children: MATRIX_CELLS.map(([x, y], index) => jsx("rect", {
			className: css.cell,
			x,
			y,
			width: "2",
			height: "2",
			style: { animationDelay: `${(index - MATRIX_CELLS.length) * 125}ms` }
		}, `${x}-${y}`))
	});
	return jsx("span", {
		className: clsx(css.dot, className),
		"data-state": state,
		style: {
			width: size,
			height: size
		},
		"aria-hidden": "true"
	});
}
//#endregion
//#region lib/types/icons/index.js
/** ic_ds_new_chat_outline_16 */
const IconNewChatOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M8.00003 0.3237C3.76075 0.3237 0.32373 3.76072 0.32373 8C0.32373 9.17603 0.589121 10.2922 1.0632 11.2901L1.35291 11.8989L2.5705 11.3205L2.28079 10.7117C1.89079 9.89074 1.67301 8.97167 1.67301 8C1.67301 4.50546 4.50549 1.67298 8.00003 1.67298C11.4946 1.67298 14.3271 4.50546 14.3271 8C14.3271 11.4945 11.4946 14.327 8.00003 14.327C7.28473 14.327 6.76077 14.277 6.29621 14.1487C5.83857 14.0224 5.40441 13.8109 4.88514 13.4488C4.12569 12.919 3.03778 12.7316 2.141 13.2978L2.12682 13.307L2.11264 13.3171L1.34886 13.854L1.79659 15.188L2.86122 14.4384C3.19068 14.2305 3.68325 14.2542 4.11326 14.5539C4.72789 14.9826 5.30042 15.2724 5.93762 15.4484C6.56803 15.6224 7.22776 15.6763 8.00003 15.6763C12.2393 15.6763 15.6763 12.2393 15.6763 8C15.6763 3.76072 12.2393 0.3237 8.00003 0.3237ZM7.32033 4.82535V7.32536H4.82538V8.67464H7.32033V11.1747H8.6696V8.67464H11.1747V7.32536H8.6696V4.82535H7.32033Z",
		fill: "currentColor"
	})
});
/** ic_ds_search_outline_16 */
const IconSearchOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsx("path", {
		d: "M11.894845 6.647401C11.894845 3.725463 9.534486 1.356779 6.623219 1.35657C3.711786 1.35657 1.351635 3.725338 1.351635 6.647401C1.351843 9.569296 3.711911 11.938273 6.623219 11.938273C9.534361 11.938064 11.894637 9.569171 11.894845 6.647401ZM13.245462 6.647401C13.245254 10.317935 10.280401 13.293613 6.623219 13.293821C2.965871 13.293821 0.000204 10.31806 0 6.647401C0 2.976574 2.965746 0 6.623219 0C10.280526 0.000205 13.245462 2.9767 13.245462 6.647401Z",
		fill: "currentColor"
	}), jsx("path", {
		d: "M16.000417 15.041079L15.044449 16.000433L11.530434 12.473588L12.486298 11.514234L16.000417 15.041079Z",
		fill: "currentColor"
	})]
});
/** ic_ds_globe_outline_14 — meridian globe (harness-only figma extract). */
const IconGlobeOutline14 = ({ size = 14, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		fillRule: "evenodd",
		clipRule: "evenodd",
		d: "M7.00018 0.353516C10.6708 0.353535 13.6468 3.32958 13.6469 7.00018C13.6468 10.6708 10.6708 13.6468 7.00018 13.6469C3.32957 13.6468 0.353535 10.6708 0.353516 7.00018C0.353535 3.32957 3.32957 0.353531 7.00018 0.353516ZM5.44643 7.59661C5.49463 8.97506 5.70762 10.191 6.02136 11.0793C6.20141 11.5891 6.40328 11.9585 6.59898 12.1889C6.79501 12.4196 6.93213 12.454 7.00018 12.454C7.06822 12.454 7.20533 12.4197 7.40138 12.1889C7.59708 11.9585 7.79895 11.589 7.979 11.0793C8.29274 10.191 8.50574 8.97506 8.55394 7.59661H5.44643ZM1.57861 7.59661C1.80785 9.70467 3.2386 11.4509 5.1715 12.1388C5.07135 11.9317 4.97972 11.7098 4.89746 11.477C4.53084 10.4391 4.30224 9.0828 4.25357 7.59661H1.57861ZM9.74679 7.59661C9.69813 9.0828 9.46952 10.4391 9.1029 11.477C9.0206 11.7099 8.92818 11.9316 8.82797 12.1388C10.7613 11.4511 12.1925 9.70496 12.4218 7.59661H9.74679ZM5.1706 1.8616C3.23814 2.54963 1.80876 4.29604 1.5795 6.40376H4.25357C4.30224 4.91756 4.53083 3.56129 4.89746 2.5234C4.97968 2.29066 5.07051 2.0686 5.1706 1.8616ZM7.00018 1.54637C6.93213 1.54638 6.79503 1.5807 6.59898 1.81145C6.40332 2.04177 6.20139 2.41058 6.02136 2.92012C5.70754 3.80851 5.49461 5.02499 5.44643 6.40376H8.55394C8.50575 5.025 8.29282 3.80851 7.979 2.92012C7.79898 2.41059 7.59705 2.04177 7.40138 1.81145C7.20531 1.58067 7.06823 1.54637 7.00018 1.54637ZM8.82887 1.8616C8.92902 2.0687 9.02064 2.29053 9.1029 2.5234C9.46953 3.56129 9.69812 4.91756 9.74679 6.40376H12.4209C12.1916 4.29575 10.7618 2.54943 8.82887 1.8616Z",
		fill: "currentColor"
	})
});
/** ic_ds_settings_outline_14 */
const IconSettingsOutline14 = ({ size = 14, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsxs("g", {
		clipPath: "url(#clip0_2580_121189)",
		children: [jsx("path", {
			d: "M12.1192 4.91016C11.9392 4.52714 11.7007 4.1292 11.4483 3.78809C11.385 3.70258 11.3517 3.68409 11.2462 3.67383C10.7419 3.6248 10.2318 3.69454 9.72662 3.64551C9.29108 3.60318 8.93739 3.40341 8.67682 3.05176C8.38762 2.66127 8.19217 2.20926 7.90338 1.81934C7.83985 1.73359 7.80848 1.71542 7.70221 1.70508C7.24758 1.6609 6.7511 1.66104 6.29791 1.70508C6.19164 1.71542 6.16027 1.73359 6.09674 1.81934C5.80775 2.20954 5.61248 2.66131 5.3233 3.05176C5.06273 3.40341 4.70904 3.60318 4.2735 3.64551C3.76831 3.69454 3.25825 3.6248 2.75397 3.67383C2.6484 3.68409 2.61509 3.70258 2.55182 3.78809C2.30019 4.12814 2.06125 4.52646 1.88092 4.91016C1.83256 5.01309 1.83242 5.04912 1.88092 5.15235C2.07954 5.57482 2.37449 5.94529 2.5733 6.36817C2.76971 6.78606 2.76964 7.21293 2.5733 7.63086C2.37462 8.05374 2.07947 8.42453 1.88092 8.84668C1.83235 8.95004 1.83257 8.98695 1.88092 9.08985C2.06098 9.47285 2.2994 9.87079 2.55182 10.2119C2.61509 10.2974 2.6484 10.3159 2.75397 10.3262C3.25879 10.3753 3.76834 10.3055 4.2735 10.3545C4.70904 10.3968 5.06273 10.5966 5.3233 10.9482C5.6125 11.3387 5.80795 11.7907 6.09674 12.1807C6.16027 12.2664 6.19164 12.2846 6.29791 12.2949C6.7511 12.339 7.24758 12.3391 7.70221 12.2949C7.80848 12.2846 7.83985 12.2664 7.90338 12.1807C8.19237 11.7905 8.38764 11.3387 8.67682 10.9482C8.93739 10.5966 9.29108 10.3968 9.72662 10.3545C10.2318 10.3055 10.7419 10.3752 11.2462 10.3262C11.3517 10.3159 11.385 10.2974 11.4483 10.2119C11.7007 9.87079 11.9391 9.47285 12.1192 9.08985C12.1675 8.98695 12.1678 8.95004 12.1192 8.84668C11.9205 8.42428 11.6255 8.05377 11.4268 7.63086C11.2305 7.21293 11.2304 6.78606 11.4268 6.36817C11.6256 5.94531 11.9207 5.5746 12.1192 5.15235C12.1677 5.04912 12.1676 5.01309 12.1192 4.91016ZM13.2051 5.66309C13.0064 6.08573 12.7114 6.45579 12.5128 6.87793C12.4642 6.98123 12.4645 7.01829 12.5128 7.1211C12.7112 7.54328 13.0064 7.91405 13.2051 8.33692C13.4015 8.75487 13.4015 9.18169 13.2051 9.59961C12.9911 10.0551 12.7109 10.5221 12.4122 10.9258C12.1522 11.277 11.7974 11.4782 11.3624 11.5205C10.8573 11.5696 10.3477 11.4999 9.84283 11.5488C9.73621 11.5592 9.70429 11.5772 9.64069 11.6631C9.35229 12.0526 9.15705 12.5044 8.86823 12.8945C8.60854 13.2452 8.25275 13.447 7.81842 13.4893C7.28749 13.5409 6.71096 13.5407 6.1817 13.4893C5.74737 13.447 5.39158 13.2452 5.1319 12.8945C4.84312 12.5045 4.64808 12.0529 4.35944 11.6631C4.29583 11.5772 4.26392 11.5592 4.15729 11.5488C3.65283 11.5 3.14295 11.5696 2.63776 11.5205C2.20274 11.4782 1.84796 11.277 1.58795 10.9258C1.28834 10.5209 1.00864 10.0543 0.794982 9.59961C0.598644 9.18169 0.598598 8.75487 0.794982 8.33692C0.993688 7.91405 1.28889 7.54328 1.48737 7.1211C1.53567 7.01829 1.53593 6.98123 1.48737 6.87793C1.28887 6.45603 0.993667 6.08569 0.794982 5.66309C0.598535 5.24516 0.59869 4.81829 0.794982 4.40039C1.00898 3.94492 1.28922 3.47791 1.58795 3.07422C1.84796 2.723 2.20274 2.5218 2.63776 2.47949C3.14295 2.43038 3.65283 2.50003 4.15729 2.45117C4.26391 2.44081 4.29583 2.4228 4.35944 2.33692C4.64783 1.94742 4.84308 1.49557 5.1319 1.10547C5.39158 0.754835 5.74737 0.553005 6.1817 0.510744C6.71263 0.459147 7.28917 0.459309 7.81842 0.510744C8.25275 0.553005 8.60854 0.754835 8.86823 1.10547C9.157 1.49551 9.35204 1.94708 9.64069 2.33692C9.70429 2.4228 9.73621 2.44081 9.84283 2.45117C10.3477 2.50007 10.8573 2.43039 11.3624 2.47949C11.7974 2.5218 12.1522 2.723 12.4122 3.07422C12.7118 3.47909 12.9915 3.94567 13.2051 4.40039C13.4014 4.81829 13.4016 5.24516 13.2051 5.66309Z",
			fill: "currentColor"
		}), jsx("path", {
			d: "M7.9317 7C7.9317 6.48569 7.51438 6.06836 7.00006 6.06836C6.48575 6.06836 6.06842 6.48569 6.06842 7C6.06842 7.51432 6.48575 7.93164 7.00006 7.93164C7.51438 7.93164 7.9317 7.51432 7.9317 7ZM9.13092 7C9.13092 8.17706 8.17712 9.13086 7.00006 9.13086C5.823 9.13086 4.8692 8.17706 4.8692 7C4.8692 5.82294 5.823 4.86914 7.00006 4.86914C8.17712 4.86914 9.13092 5.82294 9.13092 7Z",
			fill: "currentColor"
		})]
	}), jsx("defs", { children: jsx("clipPath", {
		id: "clip0_2580_121189",
		children: jsx("rect", {
			width: 14,
			height: 14,
			fill: "currentColor"
		})
	}) })]
});
/** ic_ds_settings_outline_16 */
const IconSettingsOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsxs("g", {
		clipPath: "url(#clip0_1450_63327)",
		children: [jsx("path", {
			d: "M14.0861 5.51366C13.8717 5.0575 13.588 4.58542 13.2889 4.18108C13.208 4.07172 13.1596 4.04373 13.0243 4.03054C12.4277 3.97255 11.8245 4.05527 11.2269 3.9972C10.7224 3.94816 10.3133 3.71661 10.0115 3.30919C9.66986 2.84777 9.43973 2.31343 9.09824 1.85234C9.01771 1.74365 8.96805 1.71589 8.83354 1.70282C8.29432 1.65044 7.70402 1.65061 7.16656 1.70282C7.03205 1.71589 6.98239 1.74365 6.90186 1.85234C6.56067 2.31303 6.33025 2.84774 5.98855 3.30919C5.68681 3.71661 5.27774 3.94816 4.77317 3.9972C4.17564 4.05527 3.57239 3.97255 2.97585 4.03054C2.84046 4.04373 2.79208 4.07172 2.71115 4.18108C2.41212 4.58542 2.12835 5.0575 1.91403 5.51366C1.85299 5.64359 1.85286 5.7018 1.91403 5.8319C2.14865 6.33077 2.49748 6.76892 2.73237 7.26854C2.9594 7.7515 2.96041 8.24717 2.73338 8.73044C2.49837 9.23061 2.14891 9.66837 1.91403 10.1681C1.85291 10.2982 1.85299 10.3564 1.91403 10.4863C2.12856 10.9429 2.41185 11.4142 2.71115 11.8189C2.79208 11.9283 2.84046 11.9563 2.97585 11.9694C3.57239 12.0274 4.17564 11.9447 4.77317 12.0028C5.27774 12.0518 5.68681 12.2834 5.98855 12.6908C6.33024 13.1522 6.56037 13.6866 6.90186 14.1476C6.98239 14.2563 7.03205 14.2841 7.16656 14.2972C7.70402 14.3494 8.29432 14.3495 8.83354 14.2972C8.96805 14.2841 9.01771 14.2563 9.09824 14.1476C9.43944 13.687 9.66985 13.1522 10.0115 12.6908C10.3133 12.2834 10.7224 12.0518 11.2269 12.0028C11.8244 11.9447 12.4271 12.0275 13.0243 11.9694C13.1596 11.9563 13.208 11.9283 13.2889 11.8189C13.5891 11.4131 13.872 10.942 14.0861 10.4863C14.1471 10.3564 14.1472 10.2982 14.0861 10.1681C13.8513 9.66861 13.5017 9.23061 13.2667 8.73044C13.0397 8.24717 13.0407 7.7515 13.2677 7.26854C13.5026 6.7689 13.8513 6.33106 14.0861 5.8319C14.1472 5.7018 14.1471 5.64359 14.0861 5.51366ZM15.3035 6.40373C15.0685 6.90359 14.7188 7.34119 14.4841 7.84037C14.4231 7.97025 14.423 8.02855 14.4841 8.15861C14.7189 8.65833 15.0685 9.09611 15.3035 9.59626C15.5308 10.0801 15.5308 10.5744 15.3035 11.0582C15.052 11.5933 14.7225 12.1426 14.37 12.6191C14.0685 13.0265 13.6581 13.259 13.1536 13.3081C12.5566 13.366 11.9541 13.2835 11.3573 13.3414C11.2228 13.3545 11.1731 13.3823 11.0926 13.491C10.7511 13.9521 10.521 14.4864 10.1793 14.9478C9.87828 15.3542 9.46719 15.5869 8.96387 15.6358C8.34008 15.6964 7.66194 15.6966 7.03623 15.6358C6.53291 15.5869 6.12182 15.3542 5.82084 14.9478C5.47911 14.4863 5.24878 13.9517 4.90753 13.491C4.82701 13.3823 4.77734 13.3545 4.64284 13.3414C4.04647 13.2835 3.44373 13.366 2.84653 13.3081C2.34201 13.259 1.93164 13.0265 1.63013 12.6191C1.27867 12.144 0.948453 11.5941 0.696621 11.0582C0.469315 10.5744 0.469279 10.0801 0.696621 9.59626C0.931628 9.09613 1.2813 8.65807 1.51597 8.15861C1.57708 8.02855 1.57702 7.97025 1.51597 7.84037C1.28117 7.34095 0.931635 6.9036 0.696621 6.40373C0.469213 5.91992 0.469367 5.42562 0.696621 4.94183C0.948441 4.40587 1.27868 3.85598 1.63013 3.38092C1.93164 2.97349 2.34201 2.74095 2.84653 2.6919C3.44353 2.63397 4.04599 2.71649 4.64284 2.65856C4.77734 2.64549 4.82701 2.61774 4.90753 2.50904C5.24905 2.04792 5.47913 1.51362 5.82084 1.05219C6.12182 0.645806 6.53291 0.413119 7.03623 0.364178C7.66002 0.303556 8.33816 0.303369 8.96387 0.364178C9.46719 0.413119 9.87828 0.645806 10.1793 1.05219C10.521 1.51365 10.7513 2.04828 11.0926 2.50904C11.1731 2.61774 11.2228 2.64549 11.3573 2.65856C11.9541 2.71649 12.5566 2.63397 13.1536 2.6919C13.6581 2.74095 14.0685 2.97349 14.37 3.38092C14.7214 3.85598 15.0517 4.40587 15.3035 4.94183C15.5307 5.42562 15.5309 5.91992 15.3035 6.40373Z",
			fill: "currentColor"
		}), jsx("path", {
			d: "M9.13764 7.99999C9.13764 7.3715 8.62855 6.8624 8.00005 6.8624C7.37155 6.8624 6.86246 7.3715 6.86246 7.99999C6.86246 8.62849 7.37155 9.13759 8.00005 9.13759C8.62855 9.13759 9.13764 8.62849 9.13764 7.99999ZM10.4834 7.99999C10.4834 9.37126 9.37132 10.4833 8.00005 10.4833C6.62878 10.4833 5.51674 9.37126 5.51674 7.99999C5.51674 6.62873 6.62878 5.51669 8.00005 5.51669C9.37132 5.51669 10.4834 6.62873 10.4834 7.99999Z",
			fill: "currentColor"
		})]
	}), jsx("defs", { children: jsx("clipPath", {
		id: "clip0_1450_63327",
		children: jsx("rect", {
			width: 16,
			height: 16,
			fill: "currentColor"
		})
	}) })]
});
/** ic_ds_panel_left_outline_16 */
const IconPanelLeftOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		fillRule: "evenodd",
		clipRule: "evenodd",
		d: "M9.67272 0.522841C10.8339 0.522841 11.76 0.522714 12.4963 0.602493C13.2453 0.683657 13.8789 0.854248 14.4264 1.25197C14.7504 1.48739 15.0355 1.77247 15.2709 2.0965C15.6686 2.64394 15.8392 3.27758 15.9204 4.02655C16.0002 4.7629 16 5.68895 16 6.85014V9.14986C16 10.3111 16.0002 11.2371 15.9204 11.9735C15.8392 12.7224 15.6686 13.3561 15.2709 13.9035C15.0355 14.2275 14.7504 14.5126 14.4264 14.748C13.8789 15.1458 13.2453 15.3163 12.4963 15.3975C11.76 15.4773 10.8339 15.4772 9.67272 15.4772H6.3273C5.16611 15.4772 4.24006 15.4773 3.50371 15.3975C2.75474 15.3163 2.1211 15.1458 1.57366 14.748C1.24963 14.5126 0.964549 14.2275 0.729131 13.9035C0.331407 13.3561 0.160817 12.7224 0.0796529 11.9735C-0.000126137 11.2371 1.25338e-09 10.3111 1.25338e-09 9.14986V6.85014C1.25329e-09 5.68895 -0.000126137 4.7629 0.0796529 4.02655C0.160817 3.27758 0.331407 2.64394 0.729131 2.0965C0.964549 1.77247 1.24963 1.48739 1.57366 1.25197C2.1211 0.854248 2.75474 0.683657 3.50371 0.602493C4.24006 0.522714 5.16611 0.522841 6.3273 0.522841H9.67272ZM5.54303 1.88715V14.1118C5.78636 14.1128 6.04709 14.1169 6.3273 14.1169H9.67272C10.8639 14.1169 11.7032 14.1164 12.3493 14.0465C12.9824 13.9779 13.3497 13.8494 13.6268 13.6482C13.8354 13.4966 14.0195 13.3125 14.1711 13.1039C14.3723 12.8268 14.5007 12.4595 14.5693 11.8264C14.6393 11.1803 14.6398 10.341 14.6398 9.14986V6.85014C14.6398 5.65896 14.6393 4.81967 14.5693 4.1736C14.5007 3.54048 14.3723 3.17318 14.1711 2.89609C14.0195 2.68747 13.8354 2.50337 13.6268 2.35179C13.3497 2.1506 12.9824 2.02212 12.3493 1.95353C11.7032 1.88358 10.8639 1.88307 9.67272 1.88307H6.3273C6.04709 1.88307 5.78636 1.8862 5.54303 1.88715ZM4.1828 1.91166C3.99125 1.9216 3.8148 1.93577 3.65076 1.95353C3.01764 2.02212 2.65034 2.1506 2.37325 2.35179C2.16463 2.50337 1.98052 2.68747 1.82895 2.89609C1.62776 3.17318 1.49928 3.54048 1.43069 4.1736C1.36074 4.81967 1.36023 5.65896 1.36023 6.85014V9.14986C1.36023 10.341 1.36074 11.1803 1.43069 11.8264C1.49928 12.4595 1.62776 12.8268 1.82895 13.1039C1.98052 13.3125 2.16463 13.4966 2.37325 13.6482C2.65034 13.8494 3.01764 13.9779 3.65076 14.0465C3.81478 14.0642 3.99127 14.0774 4.1828 14.0873V1.91166Z",
		fill: "currentColor"
	})
});
/** ic_ds_ellipsis_outline_16 */
const IconEllipsisOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [
		jsx("path", {
			d: "M4.55146 8.00001C4.55146 8.63513 4.03659 9.15001 3.40146 9.15001C2.76634 9.15001 2.25146 8.63513 2.25146 8.00001C2.25146 7.36488 2.76634 6.85001 3.40146 6.85001C4.03659 6.85001 4.55146 7.36488 4.55146 8.00001Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M9.1476 8.00001C9.1476 8.63513 8.63273 9.15001 7.9976 9.15001C7.36248 9.15001 6.8476 8.63513 6.8476 8.00001C6.8476 7.36488 7.36248 6.85001 7.9976 6.85001C8.63273 6.85001 9.1476 7.36488 9.1476 8.00001Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M13.7486 8.00001C13.7486 8.63513 13.2338 9.15001 12.5986 9.15001C11.9635 9.15001 11.4486 8.63513 11.4486 8.00001C11.4486 7.36488 11.9635 6.85001 12.5986 6.85001C13.2338 6.85001 13.7486 7.36488 13.7486 8.00001Z",
			fill: "currentColor"
		})
	]
});
/** ic_ds_plus_outline_16 */
const IconPlusOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M8.64453 1.5V7.34961H14.5V8.65039H8.64453V14.5H7.34473V8.65039H1.5V7.34961H7.34473V1.5H8.64453Z",
		fill: "currentColor"
	})
});
/** ic_ds_check_outline_16 */
const IconCheckOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M15.0498 3.92579L8.49512 12.3818C8.25774 12.6881 8.04517 12.9645 7.84668 13.1689C7.63957 13.3823 7.38732 13.5841 7.04492 13.6719C6.86373 13.7183 6.6757 13.7346 6.48926 13.7197C6.13666 13.6915 5.8528 13.5355 5.6123 13.3604C5.38201 13.1926 5.12573 12.9567 4.83984 12.6953L1.03125 9.21289L1.96875 8.1875L5.77734 11.6699C6.08684 11.9529 6.27773 12.1249 6.43066 12.2363C6.50183 12.2882 6.54699 12.3135 6.57324 12.3252C6.58525 12.3305 6.59269 12.3322 6.5957 12.333C6.59802 12.3336 6.59961 12.334 6.59961 12.334C6.63317 12.3367 6.66758 12.3335 6.7002 12.3252C6.7002 12.3252 6.70211 12.3251 6.7041 12.3242C6.70698 12.3229 6.71348 12.319 6.72461 12.3115C6.74849 12.2956 6.78843 12.2642 6.84961 12.2012C6.98138 12.0654 7.13957 11.8628 7.39648 11.5313L13.9502 3.07422L15.0498 3.92579Z",
		fill: "currentColor"
	})
});
/** ic_ds_check_outline_14 */
const IconCheckOutline14 = ({ size = 14, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M11.5635 4.58984L7.61426 9.07715C7.35154 9.37561 7.11346 9.64812 6.89453 9.84668C6.66593 10.054 6.38519 10.2506 6.01465 10.3164C5.82079 10.3508 5.62207 10.3529 5.42773 10.3213C5.0561 10.2609 4.77266 10.0674 4.54102 9.86328C4.31926 9.66791 4.07752 9.39911 3.81055 9.10449L2.44531 7.59863L3.55664 6.59082L4.92188 8.09766C5.21256 8.41844 5.38878 8.61191 5.53223 8.73828C5.61022 8.80699 5.65253 8.83192 5.66895 8.83984C5.69648 8.84429 5.72449 8.84467 5.75195 8.83984C5.72657 8.84451 5.75564 8.85422 5.88672 8.73535C6.02833 8.60692 6.20225 8.41088 6.48828 8.08594L10.4385 3.59961L11.5635 4.58984Z",
		fill: "currentColor"
	})
});
/** ic_ds_branch_outline_16 */
const IconBranchOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		fillRule: "evenodd",
		clipRule: "evenodd",
		d: "M13.0762 1.37207C14.0846 1.37228 14.9021 2.19077 14.9023 3.19922C14.9022 4.20772 14.0847 5.02518 13.0762 5.02539C12.2967 5.02539 11.6325 4.53691 11.3701 3.84961H4.35547C4.79397 4.26458 5.15861 4.7644 5.41699 5.33496L7.10645 9.06738C7.88526 10.7875 9.55104 11.9228 11.4189 12.0371C11.7085 11.4109 12.3411 10.9756 13.0762 10.9756C14.0843 10.9759 14.9023 11.7936 14.9023 12.8018C14.9023 13.81 14.0843 14.6277 13.0762 14.6279C12.2534 14.6279 11.5574 14.0832 11.3291 13.335C8.9868 13.1879 6.89981 11.7612 5.92285 9.60352L4.23242 5.87109C3.67503 4.64033 2.44878 3.84961 1.09766 3.84961V2.54883C1.10665 2.54883 1.11601 2.54975 1.125 2.5498L11.3701 2.54883C11.6326 1.86151 12.2969 1.37207 13.0762 1.37207ZM13.0762 12.2764C12.7858 12.2764 12.5508 12.5114 12.5508 12.8018C12.5508 13.0921 12.7858 13.3281 13.0762 13.3281C13.3664 13.3279 13.6025 13.092 13.6025 12.8018C13.6025 12.5115 13.3664 12.2766 13.0762 12.2764ZM13.0762 2.67285C12.7855 2.67285 12.55 2.90861 12.5498 3.19922C12.5499 3.48987 12.7855 3.72559 13.0762 3.72559C13.3667 3.72538 13.6024 3.48975 13.6025 3.19922C13.6023 2.90874 13.3666 2.67306 13.0762 2.67285Z",
		fill: "currentColor"
	})
});
/** ic_ds_chevron_down_outline_14 */
const IconChevronDownOutline14 = ({ size = 14, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M11.8486 5.5L11.4238 5.92383L8.69727 8.65137C8.44157 8.90706 8.21562 9.13382 8.01172 9.29785C7.79912 9.46883 7.55595 9.61756 7.25 9.66602C7.08435 9.69222 6.91565 9.69222 6.75 9.66602C6.44405 9.61756 6.20088 9.46883 5.98828 9.29785C5.78438 9.13382 5.55843 8.90706 5.30273 8.65137L2.57617 5.92383L2.15137 5.5L3 4.65137L3.42383 5.07617L6.15137 7.80273C6.42595 8.07732 6.59876 8.24849 6.74023 8.3623C6.87291 8.46904 6.92272 8.47813 6.9375 8.48047C6.97895 8.48703 7.02105 8.48703 7.0625 8.48047C7.07728 8.47813 7.12709 8.46904 7.25977 8.3623C7.40124 8.24849 7.57405 8.07732 7.84863 7.80273L10.5762 5.07617L11 4.65137L11.8486 5.5Z",
		fill: "currentColor"
	})
});
/** ic_ds_chevron_left_outline_14 */
const IconChevronLeftOutline14 = ({ size = 14, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M8.5 2.15137L8.07617 2.57617L5.34863 5.30273C5.09294 5.55843 4.86618 5.78438 4.70215 5.98828C4.53117 6.20088 4.38244 6.44405 4.33398 6.75C4.30778 6.91565 4.30778 7.08435 4.33398 7.25C4.38244 7.55595 4.53117 7.79912 4.70215 8.01172C4.86618 8.21561 5.09294 8.44157 5.34863 8.69727L8.07617 11.4238L8.5 11.8486L9.34863 11L8.92383 10.5762L6.19727 7.84863C5.92268 7.57405 5.75151 7.40124 5.6377 7.25977C5.53096 7.12709 5.52187 7.07728 5.51953 7.0625C5.51297 7.02105 5.51297 6.97895 5.51953 6.9375C5.52187 6.92272 5.53096 6.87291 5.6377 6.74023C5.75152 6.59876 5.92268 6.42595 6.19727 6.15137L8.92383 3.42383L9.34863 3L8.5 2.15137Z",
		fill: "currentColor"
	})
});
/** ic_ds_chevron_right_outline_14 */
const IconChevronRightOutline14 = ({ size = 14, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M5.5 2.15137L5.92383 2.57617L8.65137 5.30273C8.90706 5.55843 9.13382 5.78438 9.29785 5.98828C9.46883 6.20088 9.61756 6.44405 9.66602 6.75C9.69222 6.91565 9.69222 7.08435 9.66602 7.25C9.61756 7.55595 9.46883 7.79912 9.29785 8.01172C9.13382 8.21561 8.90706 8.44157 8.65137 8.69727L5.92383 11.4238L5.5 11.8486L4.65137 11L5.07617 10.5762L7.80273 7.84863C8.07732 7.57405 8.24849 7.40124 8.3623 7.25977C8.46904 7.12709 8.47813 7.07728 8.48047 7.0625C8.48703 7.02105 8.48703 6.97895 8.48047 6.9375C8.47813 6.92272 8.46904 6.87291 8.3623 6.74023C8.24848 6.59876 8.07732 6.42595 7.80273 6.15137L5.07617 3.42383L4.65137 3L5.5 2.15137Z",
		fill: "currentColor"
	})
});
/** ic_ds_triangle_right_fill_14 — tree expand arrow; points right, consumers rotate it 90° for the open state. */
const IconTriangleRightFill14 = ({ size = 14, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M4.25 2.82782L4.25 11.1722C4.25 11.6622 4.84243 11.9076 5.18891 11.5611L9.36109 7.38891C9.57588 7.17412 9.57588 6.82588 9.36109 6.61109L5.18891 2.43891C4.84243 2.09243 4.25 2.33782 4.25 2.82782Z",
		fill: "currentColor"
	})
});
/** ic_ds_chevron_up_outline_14 */
const IconChevronUpOutline14 = ({ size = 14, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M2.15137 8.5L2.57617 8.07617L5.30273 5.34863C5.55843 5.09294 5.78438 4.86618 5.98828 4.70215C6.20088 4.53117 6.44405 4.38244 6.75 4.33398C6.91565 4.30778 7.08435 4.30778 7.25 4.33398C7.55595 4.38244 7.79912 4.53117 8.01172 4.70215C8.21561 4.86618 8.44157 5.09294 8.69727 5.34863L11.4238 8.07617L11.8486 8.5L11 9.34863L10.5762 8.92383L7.84863 6.19727C7.57405 5.92269 7.40124 5.75152 7.25977 5.6377C7.12709 5.53096 7.07728 5.52187 7.0625 5.51953C7.02105 5.51297 6.97895 5.51297 6.9375 5.51953C6.92272 5.52187 6.87291 5.53096 6.74023 5.6377C6.59876 5.75152 6.42595 5.92268 6.15137 6.19727L3.42383 8.92383L3 9.34863L2.15137 8.5Z",
		fill: "currentColor"
	})
});
/** ic_ds_close_outline_16 */
const IconCloseOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsx("path", {
		d: "M14.1168 13.197L13.197 14.1167L1.8833 2.80303L2.80309 1.88324L14.1168 13.197Z",
		fill: "currentColor"
	}), jsx("path", {
		d: "M13.197 1.88326L14.1168 2.80305L2.80309 14.1168L1.8833 13.197L13.197 1.88326Z",
		fill: "currentColor"
	})]
});
/** ic_ds_close_fill_14 */
const IconCloseFill14 = ({ size = 14, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M10.6074 4.40278L8.00975 6.99973L10.6074 9.59739L9.59736 10.6074L6.9997 8.00978L4.40274 10.6074L3.3927 9.59739L5.98966 6.99973L3.3927 4.40278L4.40274 3.39273L6.9997 5.98969L9.59736 3.39273L10.6074 4.40278Z",
		fill: "currentColor"
	})
});
/** ic_ds_copy_outline_16 */
const IconCopyOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M6.14929 4.02032C7.11197 4.02032 7.87983 4.02016 8.49597 4.07598C9.12128 4.13269 9.65792 4.25188 10.1415 4.53106C10.7202 4.8653 11.2008 5.3459 11.535 5.92462C11.8142 6.40818 11.9334 6.94481 11.9901 7.57012C12.0459 8.18625 12.0458 8.95419 12.0458 9.9168C12.0458 10.8795 12.0459 11.6473 11.9901 12.2635C11.9334 12.8888 11.8142 13.4254 11.535 13.909C11.2008 14.4877 10.7202 14.9683 10.1415 15.3025C9.65792 15.5817 9.12128 15.7009 8.49597 15.7576C7.87984 15.8134 7.11196 15.8133 6.14929 15.8133C5.18667 15.8133 4.41874 15.8134 3.80261 15.7576C3.1773 15.7009 2.64067 15.5817 2.1571 15.3025C1.5784 14.9683 1.09778 14.4877 0.76355 13.909C0.484366 13.4254 0.365184 12.8888 0.308472 12.2635C0.252649 11.6473 0.252808 10.8795 0.252808 9.9168C0.252808 8.95418 0.252664 8.18625 0.308472 7.57012C0.365184 6.94481 0.484366 6.40818 0.76355 5.92462C1.09777 5.34589 1.57839 4.86529 2.1571 4.53106C2.64067 4.25188 3.1773 4.13269 3.80261 4.07598C4.41874 4.02017 5.18666 4.02032 6.14929 4.02032ZM6.14929 5.37774C5.16181 5.37774 4.46634 5.37761 3.92566 5.42657C3.39434 5.47472 3.07859 5.56574 2.83582 5.70587C2.4632 5.92106 2.15354 6.2307 1.93835 6.60333C1.79823 6.8461 1.70721 7.16185 1.65906 7.69317C1.6101 8.23385 1.61023 8.92933 1.61023 9.9168C1.61023 10.9043 1.61009 11.5998 1.65906 12.1404C1.70721 12.6717 1.79823 12.9875 1.93835 13.2303C2.15356 13.6029 2.46321 13.9126 2.83582 14.1277C3.07859 14.2679 3.39434 14.3589 3.92566 14.407C4.46634 14.456 5.16182 14.4559 6.14929 14.4559C7.13682 14.4559 7.83224 14.456 8.37292 14.407C8.90425 14.3589 9.21999 14.2679 9.46277 14.1277C9.83535 13.9126 10.145 13.6029 10.3602 13.2303C10.5004 12.9875 10.5914 12.6717 10.6395 12.1404C10.6885 11.5998 10.6884 10.9043 10.6884 9.9168C10.6884 8.92934 10.6885 8.23384 10.6395 7.69317C10.5914 7.16185 10.5004 6.8461 10.3602 6.60333C10.1451 6.23071 9.83536 5.92107 9.46277 5.70587C9.21999 5.56574 8.90424 5.47472 8.37292 5.42657C7.83224 5.3776 7.13682 5.37774 6.14929 5.37774ZM9.80164 0.367975C10.7638 0.367975 11.5314 0.36788 12.1473 0.423639C12.7726 0.480307 13.3093 0.598759 13.7928 0.877741C14.3717 1.21192 14.8521 1.69355 15.1864 2.27227C15.4655 2.75574 15.5857 3.29164 15.6425 3.9168C15.6983 4.53301 15.6971 5.3016 15.6971 6.26446V7.82989C15.6971 8.29264 15.6989 8.58993 15.6649 8.84844C15.4668 10.3525 14.401 11.5738 12.9833 11.9988V10.5467C13.6973 10.1903 14.2105 9.49662 14.3192 8.67169C14.3387 8.52347 14.3407 8.3358 14.3407 7.82989V6.26446C14.3407 5.27706 14.3398 4.58149 14.2909 4.04083C14.2428 3.50968 14.1526 3.19372 14.0126 2.95098C13.7974 2.57849 13.4876 2.26869 13.1151 2.05352C12.8724 1.91347 12.5564 1.82237 12.0253 1.77423C11.4847 1.72528 10.7888 1.7254 9.80164 1.7254H7.71472C6.7562 1.72558 5.92665 2.27697 5.52332 3.07891H4.07019C4.54221 1.51132 5.9932 0.368186 7.71472 0.367975H9.80164Z",
		fill: "currentColor"
	})
});
/** ic_ds_refresh_outline_16 */
const IconRefreshOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M7.92136 0.349152C10.3744 0.349234 12.5564 1.5052 13.9557 3.29894L15.1281 2.12759C15.3303 1.92546 15.6767 2.06943 15.6767 2.35538V5.53923C15.6766 5.71626 15.5329 5.85976 15.3559 5.86002H12.171C11.8854 5.8597 11.7426 5.51465 11.9443 5.31249L12.9641 4.29056C11.8237 2.74305 9.98908 1.74106 7.92136 1.74097C4.46436 1.74097 1.66233 4.543 1.66233 8C1.66233 11.457 4.46436 14.259 7.92136 14.259C11.3782 14.2589 14.1804 11.4569 14.1804 8H15.5722C15.5722 12.2251 12.1465 15.6507 7.92136 15.6508C3.69614 15.6508 0.270508 12.2252 0.270508 8C0.270508 3.77478 3.69614 0.349152 7.92136 0.349152Z",
		fill: "currentColor"
	})
});
/** ic_ds_refresh_outline_14 */
const IconRefreshOutline14 = ({ size = 14, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M1.272 6.21348C1.70645 3.08888 4.59169 0.908064 7.71634 1.34239C8.95495 1.51469 10.0438 2.07331 10.8814 2.87755L11.9458 1.81407C12.1347 1.6255 12.4572 1.75911 12.4575 2.02598V5.08751C12.4574 5.25303 12.3233 5.38731 12.1577 5.38731H9.0972C8.82993 5.38731 8.69629 5.06361 8.88528 4.87462L10.0327 3.72618C9.3732 3.09994 8.52006 2.66569 7.5513 2.53087C5.08313 2.18779 2.80376 3.91044 2.46048 6.37852C2.11747 8.84665 3.84009 11.1261 6.30814 11.4693C8.77612 11.8121 11.0557 10.0896 11.399 7.62169L11.9937 7.70372L12.5874 7.78673C12.153 10.9112 9.26756 13.0919 6.1431 12.6578C3.01854 12.2234 0.837738 9.33809 1.272 6.21348Z",
		fill: "currentColor"
	})
});
/** ic_ds_like_outline_16 */
const IconLikeOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M8.27868 0.811572C8.81991 0.142194 9.79022 0.0421835 10.4538 0.557601L10.5823 0.669306L10.6066 0.693544L10.6097 0.695652L10.6392 0.725159C11.355 1.44679 11.6337 2.49468 11.3716 3.47669L11.3706 3.48091L11.3611 3.51674L11.3601 3.51885L10.889 5.22604C10.8796 5.25997 10.8707 5.29157 10.8627 5.32088C10.8934 5.32095 10.927 5.32194 10.9628 5.32194H11.9007C12.4264 5.32194 12.7831 5.319 13.0651 5.36725C14.8182 5.66719 15.9851 7.34568 15.6565 9.09357C15.6036 9.37487 15.477 9.7092 15.294 10.2022L14.3371 12.7798C14.1402 13.3104 13.9774 13.7518 13.8102 14.1024C13.6376 14.4645 13.4386 14.7793 13.1442 15.0424C12.9712 15.197 12.7802 15.3303 12.5751 15.4386C12.226 15.6231 11.8608 15.7 11.4612 15.7358C11.0743 15.7705 10.6035 15.7695 10.0375 15.7695H4.87377C4.08053 15.7695 3.42928 15.7702 2.90734 15.7137C2.37212 15.6557 1.88991 15.5311 1.46676 15.2237C1.22415 15.0474 1.01078 14.8339 0.834466 14.5914C0.527021 14.1682 0.401373 13.686 0.343384 13.1508C0.286822 12.6287 0.287531 11.9769 0.287531 11.1833V9.51405C0.287531 8.84778 0.281347 8.36714 0.399237 7.9565C0.671152 7.00935 1.41115 6.26832 2.35829 5.99638C2.76894 5.87849 3.24958 5.88573 3.91585 5.88573C4.11983 5.88573 4.14548 5.88319 4.16244 5.88046C4.23532 5.86863 4.30409 5.83663 4.35845 5.78667C4.3711 5.77504 4.38761 5.75604 4.51442 5.59488L8.25655 0.838972L8.2576 0.837918L8.27868 0.811572ZM1.69122 11.1833C1.69122 12.0082 1.69217 12.5711 1.73865 13.0001C1.78371 13.4157 1.86473 13.6221 1.96943 13.7662C2.0592 13.8898 2.16733 13.9989 2.29085 14.0887C2.43501 14.1934 2.64216 14.2744 3.05803 14.3195C3.45897 14.3629 3.97637 14.3656 4.7157 14.3659C4.30801 13.8053 4.06453 13.1171 4.06444 12.371V8.59406H5.46813V12.371C5.46838 13.4733 6.36166 14.3669 7.46407 14.3669H10.0375C10.6286 14.3669 11.0269 14.3663 11.3369 14.3385C11.6339 14.3118 11.7956 14.2638 11.9196 14.1983C12.0241 14.1431 12.1213 14.0747 12.2094 13.996C12.314 13.9025 12.4151 13.7678 12.5435 13.4986C12.6774 13.2176 12.8162 12.845 13.0219 12.2909L13.9788 9.71322C14.1848 9.15816 14.2531 8.96731 14.2781 8.83433C14.4618 7.85692 13.8093 6.91895 12.8291 6.75092C12.6957 6.7281 12.4928 6.72458 11.9007 6.72458H10.9628C10.7737 6.72458 10.5693 6.72657 10.4 6.70666C10.2211 6.68562 9.96702 6.63024 9.74771 6.43161C9.64454 6.33811 9.55957 6.2261 9.4969 6.10177C9.3639 5.83784 9.37799 5.57899 9.40521 5.40097C9.431 5.23261 9.48672 5.03616 9.53694 4.85404L10.008 3.14579L10.0175 3.11102C10.1488 2.61338 10.0078 2.08338 9.64654 1.71681L9.6086 1.67887L9.55064 1.64304C9.48795 1.62043 9.41425 1.63814 9.36938 1.69362L9.35779 1.70627L9.35884 1.70732L5.61672 6.46217C5.51822 6.58735 5.42237 6.7133 5.30689 6.81942C5.05075 7.05471 4.73126 7.20939 4.38796 7.26519C4.23315 7.29032 4.07513 7.28837 3.91585 7.28837C3.15356 7.28837 2.91916 7.2957 2.7461 7.34528C2.26364 7.48379 1.88564 7.86081 1.74708 8.34325C1.69738 8.51636 1.69122 8.7511 1.69122 9.51405V11.1833Z",
		fill: "currentColor"
	})
});
/** ic_ds_like_fill_16 */
const IconLikeFill16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsx("path", {
		d: "M14.0593 12.922L15.0976 10.1247C15.3087 9.5559 15.4143 9.27138 15.4566 9.04658C15.7349 7.56751 14.7472 6.14737 13.2637 5.89357C13.0382 5.85499 12.7348 5.85499 12.1281 5.85499H11.1099C10.6615 5.85499 10.4372 5.85499 10.3034 5.73376C10.2607 5.69508 10.2255 5.64885 10.1995 5.5974C10.1182 5.43613 10.1778 5.21997 10.297 4.78765L10.8081 2.93419L10.819 2.89456C11.0336 2.09024 10.8051 1.23244 10.2189 0.64139L10.1898 0.612405L10.1692 0.592068C9.77357 0.210076 9.13559 0.249344 8.78983 0.676966L8.77186 0.699678L4.71076 5.86083C4.52965 6.09101 4.38573 6.35138 4.38573 6.64427V12.7431C4.38573 14.3601 5.69654 15.6709 7.31351 15.6709L10.1068 15.6709C11.3628 15.6709 11.9908 15.6709 12.5043 15.3995C12.6723 15.3107 12.8289 15.2018 12.9706 15.0752C13.4037 14.6882 13.6222 14.0995 14.0593 12.922Z",
		fill: "currentColor"
	}), jsx("path", {
		d: "M2.91388 13.2113C2.91388 14.6907 4.08499 15.5536 4.08499 15.5536H2.65606C1.46328 15.5536 0.496338 14.5866 0.496338 13.3938V8.34439C0.496338 7.15161 1.46328 6.18467 2.65606 6.18467H2.91388V13.2113Z",
		fill: "currentColor"
	})]
});
/** ic_ds_dislike_outline_16 */
const IconDislikeOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M7.72451 15.1086C7.18929 15.7705 6.22975 15.8694 5.57357 15.3597L5.44643 15.2492L5.42247 15.2253L5.41934 15.2232L5.39016 15.194C4.68239 14.4804 4.40679 13.4441 4.66589 12.473L4.66693 12.4689L4.67631 12.4334L4.67735 12.4314L5.14318 10.7431C5.15243 10.7096 5.1613 10.6783 5.16923 10.6493C5.13878 10.6493 5.10558 10.6483 5.07023 10.6483H4.14274C3.62288 10.6483 3.27015 10.6512 2.9912 10.6035C1.25757 10.3069 0.103662 8.64702 0.42863 6.91854C0.480965 6.64037 0.606164 6.30975 0.787119 5.82223L1.73336 3.27321C1.92812 2.74852 2.08912 2.31209 2.25442 1.96535C2.42515 1.60724 2.62191 1.29594 2.91304 1.03578C3.08408 0.882951 3.273 0.751121 3.47579 0.643944C3.82102 0.461504 4.18214 0.38551 4.57731 0.350066C4.95993 0.315784 5.42553 0.316718 5.98521 0.316718H11.0916C11.876 0.316718 12.52 0.31607 13.0362 0.37195C13.5655 0.429293 14.0423 0.552534 14.4608 0.856536C14.7007 1.03085 14.9117 1.24193 15.086 1.48181C15.3901 1.90027 15.5143 2.37709 15.5717 2.90638C15.6276 3.42269 15.6269 4.06721 15.6269 4.85202V6.50274C15.6269 7.1616 15.633 7.6369 15.5164 8.04299C15.2475 8.97962 14.5158 9.71242 13.5791 9.98133C13.173 10.0979 12.6977 10.0908 12.0389 10.0908C11.8372 10.0908 11.8118 10.0933 11.795 10.096C11.723 10.1077 11.6549 10.1393 11.6012 10.1887C11.5887 10.2002 11.5724 10.219 11.447 10.3784L7.74639 15.0815L7.74535 15.0825L7.72451 15.1086ZM14.2388 4.85202C14.2388 4.03628 14.2379 3.47965 14.1919 3.05541C14.1473 2.64443 14.0672 2.4403 13.9637 2.29779C13.8749 2.17562 13.768 2.06769 13.6458 1.9789C13.5033 1.87532 13.2984 1.79523 12.8872 1.75067C12.4907 1.70773 11.979 1.70511 11.2479 1.70482C11.6511 2.25917 11.8918 2.93968 11.8919 3.67755V7.41251H10.5038V3.67755C10.5036 2.58745 9.62023 1.70378 8.53007 1.70378H5.98521C5.40065 1.70378 5.00679 1.70442 4.70028 1.73192C4.40651 1.7583 4.24662 1.80571 4.12399 1.87052C4.02069 1.92511 3.92452 1.99276 3.8374 2.07061C3.73401 2.16306 3.634 2.2962 3.50705 2.56249C3.37462 2.84027 3.23734 3.20873 3.03393 3.75675L2.08768 6.30578C1.88395 6.85467 1.81646 7.0434 1.79172 7.1749C1.61005 8.14146 2.25533 9.06902 3.22464 9.23517C3.35654 9.25774 3.55717 9.26123 4.14274 9.26123H5.07023C5.25717 9.26123 5.4593 9.25926 5.62672 9.27894C5.80364 9.29975 6.05492 9.35452 6.27179 9.55094C6.37381 9.6434 6.45784 9.75417 6.51982 9.87712C6.65133 10.1381 6.6374 10.3941 6.61048 10.5701C6.58498 10.7366 6.52988 10.9309 6.48022 11.111L6.01439 12.8003L6.00501 12.8347C5.87513 13.3268 6.01464 13.8509 6.37184 14.2134L6.40935 14.2509L6.46667 14.2863C6.52866 14.3087 6.60155 14.2912 6.64591 14.2363L6.65738 14.2238L6.65633 14.2228L10.3569 9.52072C10.4543 9.39693 10.5491 9.27238 10.6633 9.16744C10.9166 8.93476 11.2325 8.7818 11.572 8.72662C11.7251 8.70177 11.8814 8.70369 12.0389 8.70369C12.7927 8.70369 13.0245 8.69645 13.1956 8.64742C13.6727 8.51045 14.0465 8.13761 14.1836 7.66053C14.2327 7.48935 14.2388 7.25721 14.2388 6.50274V4.85202Z",
		fill: "currentColor"
	})
});
/** ic_ds_dislike_fill_16 */
const IconDislikeFill16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsx("path", {
		d: "M1.92838 3.06811L0.88799 5.87104C0.676449 6.44097 0.570628 6.72606 0.52825 6.95131C0.249414 8.43336 1.2391 9.85637 2.72555 10.1107C2.95149 10.1493 3.25549 10.1493 3.86348 10.1493H4.88371C5.33306 10.1493 5.55774 10.1493 5.69187 10.2708C5.73467 10.3096 5.76994 10.3559 5.79593 10.4074C5.87738 10.569 5.81766 10.7856 5.69821 11.2188L5.18609 13.076L5.17522 13.1157C4.9602 13.9217 5.1891 14.7812 5.7765 15.3735L5.80568 15.4025L5.82635 15.4229C6.22273 15.8056 6.862 15.7663 7.20846 15.3378L7.22647 15.315L11.2958 10.1435C11.4772 9.91284 11.6214 9.65195 11.6214 9.35847V3.24734C11.6214 1.62711 10.308 0.313655 8.68776 0.313655L5.88886 0.313654C4.63032 0.313654 4.00105 0.313654 3.48649 0.585577C3.31815 0.674536 3.16127 0.783647 3.01929 0.910507C2.58531 1.29828 2.36633 1.88824 1.92838 3.06811Z",
		fill: "currentColor"
	}), jsx("path", {
		d: "M13.0963 2.77815C13.0963 1.29585 11.9228 0.431205 11.9228 0.431205H13.3546C14.5498 0.431205 15.5187 1.4001 15.5187 2.59529V7.65491C15.5187 8.8501 14.5498 9.81899 13.3546 9.81899H13.0963V2.77815Z",
		fill: "currentColor"
	})]
});
/** ic_ds_share_outline_16 */
const IconShareOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M7.95889 1.52285C7.95888 0.826234 8.76055 0.467983 9.27669 0.875208L9.37524 0.967191L15.1317 7.18358C15.5582 7.64419 15.5582 8.35614 15.1317 8.81676L9.37524 15.0331C8.87034 15.578 7.95888 15.2205 7.95889 14.4775V10.8207C7.10614 10.8432 6.31361 10.9316 5.45468 11.2515C4.39484 11.6463 3.18248 12.413 1.64676 13.9425C1.4533 14.135 1.18329 14.1696 0.969086 14.0908C0.74748 14.0091 0.547307 13.7879 0.54859 13.4844L0.55516 13.1315C0.618924 11.3494 1.11153 9.29838 2.27656 7.63787C3.45289 5.96147 5.29554 4.71635 7.95889 4.54797V1.52285ZM9.20911 5.13366C9.20899 5.50567 8.9031 5.77687 8.56523 5.77755C5.99383 5.78282 4.33736 6.8762 3.29964 8.35496C2.54519 9.43014 2.10739 10.7283 1.9152 11.9939C3.04749 11.0323 4.0569 10.4385 5.01917 10.0801C6.29638 9.60449 7.4406 9.56343 8.56429 9.56295C8.9178 9.5628 9.20894 9.84909 9.20911 10.2068L9.20817 13.3737L14.1837 8.00017L9.20817 2.62571L9.20911 5.13366Z",
		fill: "currentColor"
	})
});
/** ic_ds_edit_outline_16 */
const IconEditOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M9.94076 1.34942C10.7047 0.90231 11.6503 0.902415 12.4143 1.34942C12.7061 1.52015 12.9688 1.79118 13.3104 2.13284C13.6521 2.47448 13.9231 2.73721 14.0939 3.02894C14.5408 3.79294 14.5409 4.73856 14.0939 5.50251C13.9231 5.79415 13.652 6.05704 13.3104 6.39861L6.65932 13.0497C6.28068 13.4284 6.00695 13.7108 5.66543 13.9097C5.32391 14.1085 4.94315 14.2074 4.42705 14.3498L3.24394 14.6761C2.77527 14.8054 2.34538 14.9262 2.00131 14.9684C1.65196 15.0112 1.17964 15.0013 0.810764 14.6325C0.441921 14.2637 0.432107 13.7913 0.47486 13.442C0.517035 13.0979 0.6379 12.668 0.767181 12.1993L1.09352 11.0162C1.23588 10.5001 1.33481 10.1193 1.5336 9.77784C1.7325 9.43632 2.0149 9.1626 2.39355 8.78395L9.04466 2.13284C9.38625 1.79126 9.64911 1.52016 9.94076 1.34942ZM15.5427 14.8398H7.55223L8.96707 13.425H15.5427V14.8398ZM3.39382 9.78422C2.965 10.213 2.84244 10.3436 2.75709 10.49C2.67183 10.6366 2.61862 10.8079 2.45733 11.3925L2.13099 12.5756C2.00183 13.0439 1.92194 13.3419 1.88863 13.5536C2.10041 13.5204 2.39872 13.4416 2.86764 13.3123L4.05075 12.9859C4.63544 12.8246 4.80669 12.7715 4.95323 12.6862C5.09968 12.6008 5.23022 12.4783 5.65905 12.0494L10.721 6.98644L8.45577 4.72121L3.39382 9.78422ZM11.7 2.57079C11.3774 2.38198 10.9777 2.38198 10.6551 2.57079C10.5602 2.62647 10.4487 2.72931 10.0449 3.13311L9.45604 3.72094L11.7213 5.98617L12.3102 5.39833C12.7139 4.99457 12.8168 4.88307 12.8725 4.78818C13.0613 4.46561 13.0612 4.06585 12.8725 3.74326C12.8169 3.64827 12.7146 3.53752 12.3102 3.13311C11.9057 2.72863 11.795 2.6264 11.7 2.57079Z",
		fill: "currentColor"
	})
});
/** ic_ds_think_outline_14 */
const IconThinkOutline14 = ({ size = 14, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsx("path", {
		d: "M7.06431 5.93342C7.68763 5.93342 8.19307 6.43904 8.19322 7.06233C8.19322 7.68573 7.68772 8.19123 7.06431 8.19123C6.44099 8.19113 5.9354 7.68567 5.9354 7.06233C5.93555 6.43911 6.44108 5.93353 7.06431 5.93342Z",
		fill: "currentColor"
	}), jsx("path", {
		fillRule: "evenodd",
		clipRule: "evenodd",
		d: "M8.6815 0.963693C10.1169 0.447019 11.6266 0.374829 12.5633 1.31135C13.5 2.24805 13.4277 3.75776 12.911 5.19319C12.7126 5.74431 12.4386 6.31796 12.0965 6.89729C12.4969 7.54638 12.8141 8.19018 13.036 8.80647C13.5527 10.2419 13.6251 11.7516 12.6883 12.6883C11.7516 13.625 10.242 13.5527 8.8065 13.036C8.19022 12.8141 7.54641 12.4969 6.89732 12.0965C6.31797 12.4386 5.74435 12.7125 5.19322 12.911C3.75777 13.4276 2.2481 13.5 1.31138 12.5633C0.374859 11.6266 0.447049 10.1168 0.963724 8.68147C1.17185 8.10338 1.46321 7.50063 1.82896 6.8924C1.52182 6.35711 1.27235 5.82825 1.08872 5.31819C0.572068 3.88278 0.499714 2.37306 1.43638 1.43635C2.37308 0.499655 3.8828 0.572044 5.31822 1.08869C5.82828 1.27232 6.35715 1.5218 6.89243 1.82893C7.50066 1.46318 8.10341 1.17181 8.6815 0.963693ZM11.3573 8.01154C10.9083 8.62253 10.3901 9.22873 9.80943 9.8094C9.22877 10.3901 8.62255 10.9083 8.01158 11.3572C8.4257 11.5841 8.8287 11.7688 9.21275 11.9071C10.5456 12.3868 11.4246 12.2547 11.8397 11.8397C12.2548 11.4246 12.3869 10.5456 11.9071 9.21272C11.7688 8.82866 11.5841 8.42568 11.3573 8.01154ZM2.56529 8.02912C2.37344 8.39322 2.21495 8.74796 2.09263 9.08772C1.61291 10.4204 1.74512 11.2995 2.16001 11.7147C2.57505 12.1297 3.45415 12.2618 4.78697 11.7821C5.11057 11.6656 5.44786 11.5164 5.7938 11.3367C5.249 10.9223 4.70922 10.4533 4.19029 9.9344C3.57578 9.31987 3.03169 8.67633 2.56529 8.02912ZM6.90708 3.2469C6.24065 3.70479 5.5646 4.26321 4.91392 4.91389C4.26325 5.56456 3.70482 6.24063 3.24693 6.90705C3.72674 7.63325 4.32777 8.37459 5.03892 9.08576C5.64943 9.69627 6.28183 10.2265 6.90806 10.6678C7.59368 10.2025 8.2908 9.63076 8.96079 8.96076C9.6308 8.29075 10.2025 7.59366 10.6678 6.90803C10.2265 6.2818 9.69631 5.6494 9.08579 5.03889C8.37462 4.32773 7.63328 3.72672 6.90708 3.2469ZM11.7147 2.15998C11.2996 1.74509 10.4204 1.61288 9.08775 2.0926C8.74835 2.21479 8.39382 2.37271 8.03013 2.56428C8.67728 3.03065 9.31995 3.5758 9.93443 4.19026C10.4534 4.7092 10.9223 5.24896 11.3368 5.79377C11.5164 5.44785 11.6656 5.11052 11.7821 4.78694C12.2618 3.45416 12.1297 2.57502 11.7147 2.15998ZM4.91197 2.2176C3.57922 1.73788 2.70004 1.86995 2.28501 2.28498C1.87001 2.70003 1.73791 3.5792 2.21763 4.91194C2.31709 5.18822 2.44112 5.47427 2.58677 5.7674C3.01931 5.1887 3.51474 4.6158 4.06529 4.06526C4.61584 3.5147 5.18872 3.01928 5.76743 2.58674C5.47431 2.4411 5.18824 2.31706 4.91197 2.2176Z",
		fill: "currentColor"
	})]
});
/** ic_ds_think_outline_16 */
const IconThinkOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsx("path", {
		d: "M8.00192 6.64454C8.75026 6.64454 9.35732 7.25169 9.35739 8.00001C9.35739 8.74838 8.7503 9.35548 8.00192 9.35548C7.25367 9.35533 6.64743 8.74829 6.64743 8.00001C6.6475 7.25178 7.25371 6.64468 8.00192 6.64454Z",
		fill: "currentColor"
	}), jsx("path", {
		fillRule: "evenodd",
		clipRule: "evenodd",
		d: "M9.97165 1.29981C11.5853 0.718916 13.271 0.642197 14.3144 1.68555C15.3577 2.72902 15.2811 4.41466 14.7002 6.02833C14.4707 6.66561 14.1504 7.32937 13.75 8.00001C14.1504 8.67062 14.4707 9.33444 14.7002 9.97169C15.2811 11.5854 15.3578 13.271 14.3144 14.3145C13.271 15.3579 11.5854 15.2811 9.97165 14.7002C9.3344 14.4708 8.67059 14.1505 7.99997 13.75C7.32933 14.1505 6.66558 14.4708 6.02829 14.7002C4.41461 15.2811 2.72899 15.3578 1.68552 14.3145C0.642155 13.271 0.71887 11.5854 1.29977 9.97169C1.52915 9.33454 1.84865 8.67049 2.24899 8.00001C1.84866 7.32953 1.52915 6.66544 1.29977 6.02833C0.718852 4.41459 0.64207 2.729 1.68552 1.68555C2.72897 0.642112 4.41456 0.718887 6.02829 1.29981C6.66541 1.52918 7.32949 1.8487 7.99997 2.24903C8.67045 1.84869 9.33451 1.52919 9.97165 1.29981ZM12.9404 9.2129C12.4391 9.893 11.8616 10.5681 11.2148 11.2149C10.568 11.8616 9.89296 12.4391 9.21286 12.9404C9.62532 13.1579 10.0271 13.338 10.4121 13.4766C11.9146 14.0174 12.9172 13.8738 13.3955 13.3955C13.8737 12.9173 14.0174 11.9146 13.4765 10.4121C13.3379 10.0271 13.1578 9.62535 12.9404 9.2129ZM3.05856 9.2129C2.84121 9.62523 2.66197 10.0272 2.52341 10.4121C1.98252 11.9146 2.12627 12.9172 2.60446 13.3955C3.08278 13.8737 4.08544 14.0174 5.58786 13.4766C5.97264 13.338 6.37389 13.1577 6.7861 12.9404C6.10624 12.4393 5.43168 11.8614 4.78513 11.2149C4.13823 10.5679 3.55992 9.89313 3.05856 9.2129ZM7.99899 3.792C7.23179 4.31419 6.45306 4.95512 5.70407 5.70411C4.95509 6.45309 4.31415 7.23184 3.79196 7.99903C4.3143 8.76666 4.95471 9.54653 5.70407 10.2959C6.45309 11.0449 7.23271 11.6848 7.99997 12.207C8.76725 11.6848 9.54683 11.0449 10.2959 10.2959C11.0449 9.54686 11.6848 8.76729 12.207 8.00001C11.6848 7.23275 11.0449 6.45312 10.2959 5.70411C9.5465 4.95475 8.76662 4.31434 7.99899 3.792ZM5.58786 2.52344C4.08533 1.98255 3.08272 2.12625 2.60446 2.6045C2.12621 3.08275 1.98252 4.08536 2.52341 5.5879C2.66189 5.97253 2.8414 6.37409 3.05856 6.78614C3.55983 6.10611 4.1384 5.43189 4.78513 4.78516C5.43186 4.13843 6.10606 3.55987 6.7861 3.0586C6.37405 2.84144 5.97249 2.66192 5.58786 2.52344ZM13.3955 2.6045C12.9172 2.12631 11.9146 1.98257 10.4121 2.52344C10.0272 2.66201 9.62519 2.84125 9.21286 3.0586C9.8931 3.55996 10.5679 4.13827 11.2148 4.78516C11.8614 5.43172 12.4392 6.10627 12.9404 6.78614C13.1577 6.37393 13.338 5.97267 13.4765 5.5879C14.0174 4.08549 13.8736 3.08281 13.3955 2.6045Z",
		fill: "currentColor"
	})]
});
/** ic_ds_agent_preset_outline_16 (figma extract): node interiors knock out to transparency via mask, so the glyph sits on any fill. */
const IconAgentPresetOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [
		jsxs("mask", {
			id: "mask0_agent_preset_16",
			maskUnits: "userSpaceOnUse",
			x: "0",
			y: "0",
			width: "16",
			height: "16",
			children: [
				jsx("rect", {
					width: "16",
					height: "16",
					fill: "white"
				}),
				jsx("circle", {
					cx: "7.9995",
					cy: "3.28319",
					r: "1.712",
					fill: "black"
				}),
				jsx("circle", {
					cx: "3.51122",
					cy: "11.3855",
					r: "1.712",
					fill: "black"
				}),
				jsx("circle", {
					cx: "12.4878",
					cy: "11.3855",
					r: "1.712",
					fill: "black"
				})
			]
		}),
		jsx("path", {
			mask: "url(#mask0_agent_preset_16)",
			d: "M12.2881 11.0425C12.6002 11.3723 13.0413 11.5786 13.5312 11.5786L13.5342 11.5776C13.1476 12.3233 12.6119 12.9785 11.9639 13.5005C10.9327 14.3309 9.6199 14.8286 8.19336 14.8286C7.29864 14.8285 6.45056 14.6313 5.6875 14.2808C6.08309 14.0281 6.36707 13.6189 6.45215 13.1392C6.99022 13.3561 7.57767 13.476 8.19336 13.4761C9.30019 13.4761 10.3157 13.0915 11.1152 12.4478C11.5935 12.0626 11.9924 11.5848 12.2881 11.0425ZM4.14746 4.36475C4.25569 4.83228 4.55488 5.2247 4.95898 5.4585C4.07956 6.30639 3.53144 7.49605 3.53125 8.81396C3.53125 9.69534 3.77613 10.5202 4.20117 11.2231C3.74959 11.3817 3.38395 11.7232 3.19531 12.1597C2.5541 11.2032 2.17969 10.052 2.17969 8.81396C2.17989 7.05087 2.93868 5.4646 4.14746 4.36475ZM8.19336 2.80029C8.85717 2.80029 9.49784 2.90834 10.0967 3.10791C12.3237 3.85044 13.9725 5.86061 14.1846 8.28369C13.9832 8.20048 13.7627 8.15382 13.5312 8.15381C13.2802 8.15381 13.042 8.20907 12.8271 8.30615C12.6281 6.47264 11.3666 4.95616 9.66895 4.39014C9.2063 4.236 8.70989 4.15186 8.19336 4.15186C7.96112 4.15189 7.7329 4.16981 7.50977 4.20264C7.51947 4.12886 7.52637 4.05348 7.52637 3.97705C7.52628 3.56604 7.3811 3.18914 7.13965 2.89404C7.48183 2.83352 7.83381 2.80033 8.19336 2.80029Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M9.1123 3.28271C9.11205 2.66858 8.61322 2.17041 7.99902 2.17041C7.38504 2.17067 6.88697 2.66874 6.88672 3.28271C6.88672 3.89691 7.38489 4.39574 7.99902 4.396C8.61338 4.396 9.1123 3.89707 9.1123 3.28271ZM10.3115 3.28271C10.3115 4.55981 9.27612 5.59521 7.99902 5.59521C6.72214 5.59496 5.6875 4.55965 5.6875 3.28271C5.68776 2.00599 6.7223 0.971447 7.99902 0.971191C9.27596 0.971191 10.3113 2.00584 10.3115 3.28271Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M4.62402 11.385C4.62377 10.7709 4.12494 10.2727 3.51074 10.2727C2.89676 10.273 2.39869 10.771 2.39844 11.385C2.39844 11.9992 2.89661 12.498 3.51074 12.4983C4.1251 12.4983 4.62402 11.9994 4.62402 11.385ZM5.82324 11.385C5.82324 12.6621 4.78784 13.6975 3.51074 13.6975C2.23386 13.6973 1.19922 12.6619 1.19922 11.385C1.19947 10.1083 2.23402 9.07374 3.51074 9.07349C4.78768 9.07349 5.82299 10.1081 5.82324 11.385Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M13.6006 11.385C13.6003 10.7709 13.1015 10.2727 12.4873 10.2727C11.8733 10.273 11.3753 10.771 11.375 11.385C11.375 11.9992 11.8732 12.498 12.4873 12.4983C13.1017 12.4983 13.6006 11.9994 13.6006 11.385ZM14.7998 11.385C14.7998 12.6621 13.7644 13.6975 12.4873 13.6975C11.2104 13.6973 10.1758 12.6619 10.1758 11.385C10.176 10.1083 11.2106 9.07374 12.4873 9.07349C13.7642 9.07349 14.7995 10.1081 14.7998 11.385Z",
			fill: "currentColor"
		})
	]
});
/** ic_ds_browse_outline_16 */
const IconBrowseOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [
		jsx("path", {
			d: "M11.2426 4.80473V6.10551H4.75819V4.80473H11.2426Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M9.40858 7.84478V9.14557H4.75819V7.84478H9.40858Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M9.23438 0.546389C10.1941 0.546389 10.9683 0.544914 11.5859 0.611819C12.2161 0.680096 12.7634 0.825745 13.2393 1.17139C13.5172 1.3733 13.7619 1.61812 13.9639 1.896C14.3096 2.37183 14.4551 2.91922 14.5234 3.54932C14.5903 4.16686 14.5889 4.94133 14.5889 5.90088V10.0981C14.5889 11.0576 14.5903 11.8321 14.5234 12.4497C14.4552 13.0798 14.3094 13.6272 13.9639 14.103C13.7619 14.381 13.5172 14.6257 13.2393 14.8276C12.7633 15.1734 12.2163 15.3189 11.5859 15.3872C10.9683 15.4541 10.1942 15.4536 9.23438 15.4536H6.76563C5.80591 15.4536 5.03168 15.4541 4.41407 15.3872C3.78385 15.3189 3.23665 15.1734 2.76074 14.8276C2.48291 14.6257 2.23802 14.3809 2.03614 14.103C1.69066 13.6272 1.54483 13.0798 1.47657 12.4497C1.40973 11.8321 1.41114 11.0576 1.41114 10.0981V5.90088C1.41113 4.94132 1.40966 4.16686 1.47657 3.54932C1.54488 2.91921 1.69042 2.37184 2.03614 1.896C2.2381 1.61807 2.4828 1.37333 2.76074 1.17139C3.23665 0.825682 3.78386 0.680109 4.41407 0.611819C5.03168 0.544905 5.80591 0.546389 6.76563 0.546389H9.23438ZM6.76563 1.896C5.77586 1.896 5.0876 1.89738 4.55957 1.95459C4.0443 2.01043 3.76214 2.11349 3.55469 2.26416C3.39135 2.38284 3.24761 2.52662 3.12891 2.68994C2.97821 2.89736 2.8752 3.17967 2.81934 3.69483C2.76214 4.22279 2.76075 4.91131 2.76074 5.90088V10.0981C2.76074 11.0876 2.76221 11.7762 2.81934 12.3042C2.87516 12.8194 2.97829 13.1026 3.12891 13.3101C3.24754 13.4733 3.39147 13.6172 3.55469 13.7358C3.76213 13.8865 4.04438 13.9896 4.55957 14.0454C5.0876 14.1026 5.77586 14.103 6.76563 14.103H9.23438C10.2242 14.103 10.9124 14.1026 11.4404 14.0454C11.9556 13.9896 12.2379 13.8865 12.4453 13.7358C12.6086 13.6172 12.7525 13.4733 12.8711 13.3101C13.0217 13.1026 13.1248 12.8195 13.1807 12.3042C13.2378 11.7762 13.2393 11.0876 13.2393 10.0981V5.90088C13.2393 4.91131 13.2379 4.22279 13.1807 3.69483C13.1248 3.17969 13.0218 2.89736 12.8711 2.68994C12.7524 2.52667 12.6086 2.38281 12.4453 2.26416C12.2379 2.11355 11.9556 2.01041 11.4404 1.95459C10.9124 1.8974 10.2241 1.896 9.23438 1.896H6.76563Z",
			fill: "currentColor"
		})
	]
});
/** ic_ds_link_outline_14 */
const IconLinkOutline14 = ({ size = 14, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsx("path", {
		d: "M8.19727 5.86969C9.2092 6.90067 9.20969 8.55271 8.19727 9.58338L6.88871 10.8919C5.85801 11.9039 4.20584 11.9037 3.17502 10.8919L3.10873 10.8243C2.09622 9.7934 2.09626 8.14148 3.10873 7.11058L4.36757 5.85174C4.28261 6.33758 4.30355 6.84354 4.44077 7.33362L3.89249 7.88053C3.30043 8.48348 3.30108 9.4507 3.89318 10.0536L3.94566 10.1061C4.54861 10.698 5.51521 10.6981 6.11808 10.1061L7.41283 8.81275C8.00484 8.21002 8.00504 7.24267 7.41352 6.63964L7.35966 6.58716C7.21975 6.44976 7.05995 6.34434 6.89009 6.27089L7.70009 5.4609C7.85176 5.55768 7.99607 5.67091 8.1296 5.80202L8.19727 5.86969Z",
		fill: "currentColor"
	}), jsx("path", {
		d: "M5.80913 8.12648C4.79584 7.09547 4.79591 5.44245 5.80913 4.41141C5.81733 4.40304 5.82707 4.39209 5.8409 4.37826L7.07833 3.14082C7.09224 3.12693 7.10311 3.11729 7.11148 3.10906C8.14253 2.09591 9.79557 2.09579 10.8266 3.10906L10.8908 3.17328C11.9041 4.20425 11.9039 5.85727 10.8908 6.88835L9.63193 8.14581C9.70566 7.66581 9.67564 7.16895 9.53456 6.68948L10.1063 6.11772C10.6989 5.51458 10.6992 4.54691 10.1063 3.94391L10.0552 3.8942C9.45215 3.30157 8.48446 3.30151 7.88142 3.8942L6.59358 5.18204C6.00081 5.78507 6.00092 6.75274 6.59358 7.35584L6.6433 7.40694C6.77998 7.54132 6.93555 7.64528 7.10112 7.71837L6.29251 8.52699C6.14446 8.43127 6.00395 8.31906 5.87335 8.1907L5.80913 8.12648Z",
		fill: "currentColor"
	})]
});
/** ic_ds_link_outline_16 */
const IconLinkOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsx("path", {
		d: "M9.94133 6.50173C11.3218 7.99603 11.3218 10.3011 9.94128 11.7954C9.88691 11.8542 9.82125 11.9196 9.72099 12.0198L7.75707 13.9838C7.65709 14.0838 7.592 14.1491 7.53334 14.2034C6.03906 15.5843 3.7327 15.5854 2.23827 14.2048C2.17933 14.1503 2.11374 14.0844 2.01315 13.9838C1.91318 13.8839 1.84922 13.8188 1.79495 13.7601C0.413857 12.2657 0.413909 9.95948 1.795 8.46503C1.84923 8.4064 1.91335 8.34115 2.01321 8.24129L3.79275 6.46313C3.71814 7.08101 3.75236 7.71445 3.90115 8.33518L3.00344 9.23151C2.89398 9.34097 2.8535 9.38307 2.82251 9.41658C1.93771 10.3744 1.93704 11.8514 2.82179 12.8092C2.85279 12.8427 2.89383 12.884 3.0034 12.9936C3.11272 13.1029 3.15429 13.1442 3.18777 13.1752C4.14561 14.0603 5.62381 14.0608 6.58178 13.1758C6.61532 13.1448 6.65722 13.1032 6.76685 12.9935L8.73077 11.0296C8.83999 10.9204 8.88142 10.8787 8.91238 10.8452C9.79744 9.88728 9.7969 8.40911 8.91173 7.45124C8.88074 7.41775 8.83944 7.3762 8.73011 7.26687C8.62082 7.15757 8.58061 7.11623 8.54712 7.08526C8.37347 6.92477 8.18243 6.79361 7.98088 6.69165L9.00289 5.66964C9.17506 5.78373 9.34035 5.91265 9.49663 6.05703C9.55538 6.11135 9.62026 6.17652 9.72036 6.27662C9.82094 6.3772 9.88686 6.4428 9.94133 6.50173Z",
		fill: "currentColor"
	}), jsx("path", {
		d: "M6.06816 9.49196C4.68626 7.99724 4.68667 5.68942 6.06885 4.19487C6.12268 4.13671 6.18789 4.07306 6.28706 3.9739L8.24541 2.01416C8.34478 1.91479 8.41018 1.85055 8.46845 1.79665C9.96301 0.414902 12.2689 0.414922 13.7635 1.79665C13.8217 1.85051 13.8866 1.91559 13.9858 2.01486C14.0849 2.11394 14.1502 2.17769 14.204 2.23583C15.5861 3.7304 15.5866 6.03823 14.2047 7.53291C14.1508 7.59125 14.0854 7.65638 13.9858 7.75595L12.1994 9.54098C12.2614 8.92982 12.2185 8.30587 12.0634 7.69657L12.9956 6.76573C13.1044 6.65692 13.1458 6.61529 13.1765 6.58205C14.0621 5.62404 14.0621 4.1454 13.1765 3.18738C13.1458 3.15419 13.104 3.1135 12.9956 3.00508C12.8877 2.89716 12.8471 2.85551 12.814 2.82485C11.8559 1.9389 10.376 1.93886 9.41794 2.82485C9.38479 2.85554 9.34381 2.89622 9.23564 3.00439L7.27728 4.96413C7.16875 5.07265 7.12708 5.11322 7.09636 5.14643C6.21074 6.10441 6.21153 7.58236 7.09705 8.5404C7.12775 8.57357 7.16826 8.61575 7.27659 8.72408C7.38456 8.83205 7.42647 8.87227 7.45958 8.90293C7.62849 9.0591 7.81309 9.1881 8.00856 9.28894L6.98795 10.3095C6.82111 10.1978 6.66052 10.0715 6.50872 9.93114C6.45057 9.87733 6.38547 9.81341 6.28637 9.71431C6.1871 9.61504 6.12202 9.55018 6.06816 9.49196Z",
		fill: "currentColor"
	})]
});
/** ic_ds_right_up_outline_14 */
const IconRightUpOutline14 = ({ size = 8, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 8 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M6.54199 8.62824C6.54199 8.44193 6.54146 8.28829 6.53906 8.15851L1.11719 13.5814L0.728516 13.1927L0.339844 12.803L5.76172 7.38019C5.63201 7.3778 5.47812 7.37824 5.29199 7.37824H1.43555V6.27863H5.29199C5.65471 6.27863 5.97167 6.27814 6.22852 6.30597C6.49541 6.33493 6.76232 6.3998 7.00293 6.57452C7.13452 6.67013 7.25108 6.78571 7.34668 6.9173C7.52157 7.15808 7.5863 7.4256 7.61523 7.69269C7.64305 7.94948 7.64258 8.26562 7.64258 8.62824V12.4857H6.54199V8.62824Z",
		fill: "currentColor"
	})
});
/** ic_ds_right_up_outline_16 */
const IconRightUpOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M13.588429 5.147807C13.588429 4.739638 13.587271 4.403003 13.582013 4.118684L1.703098 15.99968L0.85155 15.148178L0 14.294485L11.878915 2.413442C11.594721 2.408199 11.257569 2.409154 10.849776 2.409154H2.400594V0.000001H10.849776C11.644471 0.000001 12.338899 -0.001059 12.901622 0.059909C13.486363 0.123352 14.071136 0.265493 14.598303 0.648292C14.886598 0.857751 15.141981 1.110984 15.351433 1.399281C15.734578 1.926807 15.876362 2.512925 15.939743 3.098105C16.000775 3.660718 15.99968 4.353347 15.99968 5.147807V13.599133H13.588429V5.147807Z",
		fill: "currentColor"
	})
});
/** ic_ds_enhance_outline_16 */
const IconEnhanceOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [
		jsx("path", {
			d: "M14.9943 1.92389V3.32428H1.00598V1.92389H14.9943Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M14.9943 5.50784V6.90823H1.00598V5.50784H14.9943Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M14.9943 9.09177V10.4922H1.00598V9.09177H14.9943Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M8.93274 12.6757V14.0761H1.00598V12.6757H8.93274Z",
			fill: "currentColor"
		})
	]
});
/** ic_ds_trash_outline_16 */
const IconTrashOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M14.4782 4.84067L14.2138 10.1152C14.1102 12.1872 14.067 13.0115 13.3866 13.9607C13.1044 14.3546 12.7498 14.6912 12.3424 14.9535C11.8239 15.2872 11.2415 15.4316 10.5585 15.4998C9.88727 15.5668 9.04946 15.5656 7.99998 15.5656C6.95051 15.5656 6.1127 15.5668 5.44142 15.4998C4.75851 15.4316 4.17602 15.2872 3.65753 14.9535C3.25012 14.6912 2.89559 14.3546 2.61332 13.9607C1.93296 13.0115 1.88979 12.1872 1.78619 10.1152L1.52179 4.84067L2.89006 4.77277L3.15343 10.0463C3.26221 12.2218 3.32452 12.6015 3.72646 13.1624C3.90825 13.4161 4.13686 13.6334 4.39927 13.8023C4.66204 13.9714 5.00263 14.0792 5.57825 14.1367C6.16562 14.1953 6.92298 14.1963 7.99998 14.1963C9.07699 14.1963 9.83434 14.1953 10.4217 14.1367C10.9973 14.0792 11.3379 13.9714 11.6007 13.8023C11.8631 13.6334 12.0917 13.4161 12.2735 13.1624C12.6755 12.6015 12.7378 12.2218 12.8465 10.0463L13.1099 4.77277L14.4782 4.84067ZM5.43011 6.22849H6.7994V11.3909H5.43011V6.22849ZM9.20056 6.22849H10.5699V11.3909H9.20056V6.22849ZM8.53597 0.434431C9.17976 0.434431 9.6522 0.426926 10.0966 0.571258C10.2357 0.616451 10.3717 0.672554 10.502 0.738948C10.9182 0.951107 11.2464 1.29099 11.7015 1.74612L12.4978 2.54136H15.3742V3.91169H0.625732V2.54136H3.50218L4.29845 1.74612C4.75358 1.29099 5.08174 0.951107 5.49801 0.738948C5.62831 0.672554 5.76425 0.616451 5.90334 0.571258C6.34776 0.426926 6.82021 0.434431 7.46399 0.434431H8.53597ZM7.46399 1.80476C6.73208 1.80476 6.51641 1.81187 6.32617 1.87369C6.25545 1.89667 6.18668 1.92533 6.12041 1.95907C5.96398 2.03878 5.82348 2.16253 5.44142 2.54136H10.5585C10.1765 2.16253 10.036 2.03878 9.87955 1.95907C9.81329 1.92533 9.74452 1.89667 9.6738 1.87369C9.48356 1.81187 9.26789 1.80476 8.53597 1.80476H7.46399Z",
		fill: "currentColor"
	})
});
/** ic_ds_warning_outline_16 */
const IconWarningOutline16 = ({ size = 14, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [
		jsx("path", {
			d: "M6.3002 3.32843L7.69986 3.32843L7.69986 7.79657H6.3002L6.3002 3.32843Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M6.3002 9.01935H7.69986V10.6711H6.3002V9.01935Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M12.6328 6.99976C12.6328 3.88874 10.111 1.36694 7 1.36694C3.88899 1.36695 1.3672 3.88875 1.36719 6.99976C1.36719 10.1108 3.88899 12.6326 7 12.6326C10.111 12.6326 12.6328 10.1108 12.6328 6.99976ZM13.8582 6.99976C13.8582 10.7873 10.7876 13.8579 7 13.8579C3.21244 13.8579 0.141846 10.7873 0.141846 6.99976C0.141857 3.2122 3.21245 0.141612 7 0.141602C10.7876 0.141602 13.8581 3.21219 13.8582 6.99976Z",
			fill: "currentColor"
		})
	]
});
/** ic_ds_user_outline_16 */
const IconUserOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsx("path", {
		d: "M11.0307 5.46369C11.0305 3.78995 9.6734 2.43357 7.99961 2.43357C6.32601 2.43379 4.96972 3.79009 4.96949 5.46369C4.96949 7.13748 6.32587 8.49455 7.99961 8.49477C9.67354 8.49477 11.0307 7.13762 11.0307 5.46369ZM12.3163 5.46369C12.3163 7.84777 10.3837 9.78042 7.99961 9.78042C5.61572 9.7802 3.68288 7.84763 3.68288 5.46369C3.6831 3.07993 5.61586 1.14718 7.99961 1.14695C10.3836 1.14695 12.3161 3.0798 12.3163 5.46369Z",
		fill: "currentColor"
	}), jsx("path", {
		d: "M8.00002 10.3316C11.7343 10.3316 14.1864 11.8997 15.0387 14.4445L14.4292 14.6483L13.8197 14.8531C13.1955 12.9893 11.3673 11.6182 8.00002 11.6182C4.63277 11.6182 2.80455 12.9893 2.18031 14.8531L1.5708 14.6483L0.961304 14.4445C1.81368 11.8997 4.26579 10.3316 8.00002 10.3316Z",
		fill: "currentColor"
	})]
});
/** ic_ds_send_outline_16 */
const IconSendOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M8.3125 0.981587C8.66767 1.0545 8.97902 1.20558 9.2627 1.43374C9.48724 1.61438 9.73029 1.85933 9.97949 2.10854L14.707 6.83608L13.293 8.25014L9 3.95717V15.0431H7V3.95717L2.70703 8.25014L1.29297 6.83608L6.02051 2.10854C6.26971 1.85933 6.51277 1.61438 6.7373 1.43374C6.97662 1.24126 7.28445 1.04542 7.6875 0.981587C7.8973 0.94841 8.1031 0.956564 8.3125 0.981587Z",
		fill: "currentColor"
	})
});
/** ic_ds_stop_fill_16 */
const IconStopFill16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M2 4.88C2 3.68009 2 3.08013 2.30557 2.65954C2.40426 2.52371 2.52371 2.40426 2.65954 2.30557C3.08013 2 3.68009 2 4.88 2H11.12C12.3199 2 12.9199 2 13.3405 2.30557C13.4763 2.40426 13.5957 2.52371 13.6944 2.65954C14 3.08013 14 3.68009 14 4.88V11.12C14 12.3199 14 12.9199 13.6944 13.3405C13.5957 13.4763 13.4763 13.5957 13.3405 13.6944C12.9199 14 12.3199 14 11.12 14H4.88C3.68009 14 3.08013 14 2.65954 13.6944C2.52371 13.5957 2.40426 13.4763 2.30557 13.3405C2 12.9199 2 12.3199 2 11.12V4.88Z",
		fill: "currentColor"
	})
});
/** ic_ds_paperclip_outline_16 */
const IconPaperclipOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M5.5498 9.75V5H6.9502V9.75C6.9502 10.3299 7.4201 10.7998 8 10.7998C8.5799 10.7998 9.0498 10.3299 9.0498 9.75V4.5C9.0498 2.9536 7.7964 1.7002 6.25 1.7002C4.7036 1.7002 3.4502 2.9536 3.4502 4.5V9.75C3.4502 12.2629 5.4871 14.2998 8 14.2998C10.5129 14.2998 12.5498 12.2629 12.5498 9.75V4H13.9502V9.75C13.9502 13.0361 11.2861 15.7002 8 15.7002C4.71391 15.7002 2.0498 13.0361 2.0498 9.75V4.5C2.04981 2.1804 3.9304 0.299806 6.25 0.299805C8.5696 0.299805 10.4502 2.1804 10.4502 4.5V9.75C10.4502 11.1031 9.3531 12.2002 8 12.2002C6.6469 12.2002 5.5498 11.1031 5.5498 9.75Z",
		fill: "currentColor"
	})
});
/** ic_ds_loading_outline_16 */
const IconLoadingOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M2.871 13.1286C0.0387669 10.2962 0.0387669 5.70383 2.871 2.87141C5.70341 0.0390029 10.2957 0.0391154 13.1282 2.87141L12.1387 3.86094C9.85292 1.57538 6.1469 1.57596 3.86123 3.86163C1.57573 6.14732 1.57573 9.85269 3.86123 12.1384C6.1469 14.424 9.85292 14.4246 12.1387 12.1391L13.1282 13.1286C10.2957 15.9609 5.70341 15.961 2.871 13.1286Z",
		fill: "currentColor"
	})
});
/** ic_ds_download_outline_16 */
const IconDownloadOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M15.3695 11.411L15.1234 12.8866C14.8869 14.3042 13.6603 15.3436 12.223 15.3436H3.77673C2.33958 15.3434 1.1128 14.3042 0.876343 12.8866L0.630249 11.411L2.05408 11.1747L2.29919 12.6493C2.41973 13.3713 3.04475 13.9001 3.77673 13.9003H12.223C12.9551 13.9002 13.58 13.3713 13.7006 12.6493L13.9457 11.1747L15.3695 11.411ZM8.72205 8.994C8.77717 8.93934 8.83792 8.88106 8.90271 8.81627L12.4828 5.23424L13.5043 6.25572L9.92224 9.8358C9.6395 10.1185 9.38763 10.3732 9.15857 10.5575C8.91892 10.7503 8.63953 10.9224 8.2865 10.9784C8.09711 11.0083 7.90363 11.0083 7.71423 10.9784C7.36106 10.9224 7.0809 10.7503 6.84119 10.5575C6.61215 10.3732 6.36022 10.1185 6.07751 9.8358L2.49646 6.25572L3.51697 5.23424L7.09705 8.81627C7.16219 8.88142 7.22331 8.94006 7.27869 8.99498V1.3065H8.72205V8.994Z",
		fill: "currentColor"
	})
});
/** ic_ds_play_outline_16 */
const IconPlayOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsx("path", {
		d: "M14.1446 8C14.1446 4.6062 11.3938 1.85539 8 1.85539C4.6062 1.85539 1.85539 4.6062 1.85539 8C1.85539 11.3938 4.6062 14.1446 8 14.1446C11.3938 14.1446 14.1446 11.3938 14.1446 8ZM15.511 8C15.511 12.148 12.148 15.511 8 15.511C3.85202 15.511 0.489014 12.148 0.489014 8C0.489014 3.85202 3.85202 0.489014 8 0.489014C12.148 0.489014 15.511 3.85202 15.511 8Z",
		fill: "currentColor"
	}), jsx("path", {
		d: "M10.5617 8.42578C10.852 8.21614 10.852 7.78386 10.5617 7.57422L7.25708 5.18751C6.90974 4.93666 6.42436 5.18484 6.42436 5.61329V10.3867C6.42436 10.8152 6.90974 11.0633 7.25708 10.8125L10.5617 8.42578Z",
		fill: "currentColor"
	})]
});
/** ic_ds_pause_outline_16 */
const IconPauseOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [
		jsx("path", {
			d: "M14.1448 8.00024C14.1448 4.60644 11.394 1.85563 8.00024 1.85563C4.60644 1.85563 1.85563 4.60644 1.85563 8.00024C1.85563 11.394 4.60644 14.1448 8.00024 14.1448C11.394 14.1448 14.1448 11.394 14.1448 8.00024ZM15.5112 8.00024C15.5112 12.1482 12.1482 15.5112 8.00024 15.5112C3.85226 15.5112 0.489258 12.1482 0.489258 8.00024C0.489258 3.85226 3.85226 0.489258 8.00024 0.489258C12.1482 0.489258 15.5112 3.85226 15.5112 8.00024Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M7.14244 5.14258V10.8569H5.71387V5.14258H7.14244Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M10.286 5.14258V10.8569H8.85742V5.14258H10.286Z",
			fill: "currentColor"
		})
	]
});
/** ic_ds_fullscreen_outline_16 */
const IconFullscreenOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsx("path", {
		d: "M2.58875 12.3407L6.59167 8.33777L7.66296 9.40808L3.66003 13.411H7.99988V14.8065H3.05457C2.02633 14.8065 1.19324 13.9734 1.19324 12.9452V7.99988H2.58875V12.3407Z",
		fill: "currentColor"
	}), jsx("path", {
		d: "M12.9452 1.19324C13.9734 1.19324 14.8065 2.02633 14.8065 3.05457V7.99988H13.411V3.66003L9.40808 7.66296L8.33777 6.59167L12.3407 2.58875H7.99988V1.19324H12.9452Z",
		fill: "currentColor"
	})]
});
/** ic_ds_code_outline_16 */
const IconCodeOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		fillRule: "evenodd",
		clipRule: "evenodd",
		d: "M12.3368 1.53569L11.931 4.43172H14.8086V5.79673H11.7404L11.1962 9.67859H14.2839V11.0436H11.0056L10.4994 14.6529L9.14873 14.4643L9.62731 11.0436H5.75876L5.25252 14.6529L3.90186 14.4643L4.38043 11.0436H1.69141V9.67859H4.57104L5.11417 5.79673H2.21609V4.43172H5.30581L5.73724 1.34713L7.08995 1.53569L6.68414 4.43172H10.5527L10.9841 1.34713L12.3368 1.53569ZM5.94937 9.67859H9.81791L10.361 5.79673H6.49353L5.94937 9.67859Z",
		fill: "currentColor"
	})
});
/** ic_ds_cordis_plugin_outline_14 */
const IconCordisPluginOutline14 = ({ size = 14, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsxs("g", {
		clipPath: "url(#clip0_1840_45990)",
		children: [jsx("path", {
			d: "M3.03426 5.66661L1.70084 7.00003L3.0315 8.33069L2.14762 9.21457L-0.0669245 7.00003L2.15038 4.78273L3.03426 5.66661ZM7 14.067L4.77924 11.8462L5.66313 10.9623L7 12.2992L8.33342 10.9658L9.2173 11.8496L7 14.067ZM11.8489 9.21803L10.965 8.33414L12.2992 7.00003L10.9623 5.66316L11.8462 4.77927L14.0669 7.00003L11.8489 9.21803ZM8.33066 3.03153L7 1.70087L5.66589 3.03498L4.782 2.1511L7 -0.0668945L9.21454 2.14765L8.33066 3.03153Z",
			fill: "currentColor"
		}), jsx("rect", {
			x: "5.98535",
			y: "5.98535",
			width: "2.02942",
			height: "2.02942",
			fill: "currentColor"
		})]
	}), jsx("defs", { children: jsx("clipPath", {
		id: "clip0_1840_45990",
		children: jsx("rect", {
			width: "14",
			height: "14",
			fill: "currentColor"
		})
	}) })]
});
/** ic_ds_api_outline (figma extract) */
const IconApiOutline14 = ({ size = 14, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	children: [
		jsx("path", {
			transform: "translate(0.6689 1.073)",
			d: "M11.4818 5.57813C11.4818 4.45301 11.4807 3.66237 11.4075 3.05908C11.3359 2.46953 11.2024 2.13852 10.9939 1.89441C10.9247 1.81341 10.8493 1.73801 10.7683 1.66882C10.5242 1.46033 10.1932 1.32686 9.60364 1.25525C9.00034 1.18198 8.20974 1.18091 7.0846 1.18091L5.57813 1.18091C4.45301 1.18091 3.66238 1.18198 3.05908 1.25525C2.46953 1.32686 2.13852 1.46033 1.89441 1.66882C1.81341 1.73801 1.73801 1.81341 1.66882 1.89441C1.46033 2.13852 1.32686 2.46953 1.25525 3.05908C1.18198 3.66238 1.18091 4.45301 1.18091 5.57813L1.18091 6.2771C1.18091 7.40218 1.18197 8.19288 1.25525 8.79614C1.32687 9.38553 1.46036 9.71674 1.66882 9.96082C1.73797 10.0417 1.81347 10.1173 1.89441 10.1864C2.13851 10.3948 2.46965 10.5275 3.05908 10.5991C3.66238 10.6724 4.45298 10.6735 5.57813 10.6735L7.0846 10.6735C8.20977 10.6735 9.00033 10.6724 9.60364 10.5991C10.1931 10.5275 10.5242 10.3948 10.7683 10.1864C10.8493 10.1173 10.9247 10.0417 10.9939 9.96082C11.2024 9.71674 11.3358 9.38553 11.4075 8.79614C11.4808 8.19288 11.4818 7.40218 11.4818 6.2771L11.4818 5.57813ZM12.6627 6.2771C12.6627 7.37222 12.6637 8.247 12.5798 8.93799C12.4942 9.64284 12.3133 10.2359 11.8928 10.7282C11.7834 10.8562 11.6637 10.9751 11.5356 11.0845C11.0434 11.5049 10.4511 11.6867 9.74634 11.7723C9.05525 11.8563 8.17999 11.8552 7.0846 11.8552L5.57813 11.8552C4.48273 11.8552 3.60747 11.8563 2.91638 11.7723C2.21157 11.6867 1.61933 11.5049 1.12708 11.0845C0.99901 10.9751 0.879281 10.8562 0.769898 10.7282C0.349454 10.2359 0.168506 9.64284 0.0828864 8.93799C-0.00101964 8.247 4.88512e-07 7.37222 6.47206e-07 6.2771L6.47206e-07 5.57813C6.47206e-07 4.48273 -0.00106163 3.60747 0.0828864 2.91638C0.168502 2.21168 0.349594 1.61928 0.769898 1.12708C0.879302 0.998981 0.998981 0.879302 1.12708 0.769898C1.61928 0.349594 2.21168 0.168502 2.91638 0.0828864C3.60747 -0.00106163 4.48273 6.47206e-07 5.57813 6.47206e-07L7.0846 6.47206e-07C8.17999 6.47206e-07 9.05525 -0.00106163 9.74634 0.0828864C10.451 0.168505 11.0434 0.349587 11.5356 0.769898C11.6637 0.879302 11.7834 0.998981 11.8928 1.12708C12.3131 1.61928 12.4942 2.21169 12.5798 2.91638C12.6638 3.60747 12.6627 4.48273 12.6627 5.57813L12.6627 6.2771Z",
			fill: "currentColor"
		}),
		jsx("path", {
			transform: "translate(0.6689 1.073)",
			d: "M6.02607 5.50955L6.44306 5.9274L3.84284 8.52762L3.425 8.11063L3.00715 7.69278L4.77253 5.9274L3.00715 4.16202L3.84284 3.32633L6.02607 5.50955Z",
			fill: "currentColor"
		}),
		jsx("path", {
			transform: "translate(0.6689 1.073)",
			d: "M9.23789 7.35397L9.23789 8.53488L6.96238 8.53488L6.96238 7.35397L9.23789 7.35397Z",
			fill: "currentColor"
		})
	]
});
/** ic_ds_personalization_outline_16 (figma extract) */
const IconPersonalizationOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	children: jsx("path", {
		transform: "translate(1.292 1.3)",
		d: "M10.3232 9.18164C11.2868 9.18164 12.0985 9.82833 12.3506 10.7109L13.415 10.7109L13.415 11.8711L12.3496 11.8711C12.0971 12.7532 11.2864 13.3994 10.3232 13.3994C9.36031 13.3992 8.55012 12.7531 8.29785 11.8711L0 11.8711L0 10.7109L8.29688 10.7109C8.54876 9.82845 9.35988 9.18186 10.3232 9.18164ZM10.3232 10.3418C9.7999 10.3421 9.37534 10.7667 9.375 11.29C9.375 11.8137 9.79969 12.239 10.3232 12.2393C10.847 12.2393 11.2725 11.8138 11.2725 11.29C11.2721 10.7666 10.8468 10.3418 10.3232 10.3418ZM12.4326 11.291C12.4326 11.3549 12.4284 11.418 12.4229 11.4805C12.4287 11.4181 12.4326 11.355 12.4326 11.291ZM8.21484 11.2832C8.21484 11.2856 8.21484 11.2886 8.21484 11.291L8.21484 11.29C8.21484 11.2878 8.21484 11.2855 8.21484 11.2832ZM3.08301 4.59082C4.04605 4.59095 4.85696 5.23717 5.10938 6.11914L13.415 6.11914L13.415 7.2793L5.11035 7.2793C4.85833 8.16202 4.04648 8.80846 3.08301 8.80859C2.11972 8.80843 1.30963 8.16179 1.05762 7.2793L0 7.2793L0 6.11914L1.05762 6.11914C1.30994 5.23728 2.12006 4.59098 3.08301 4.59082ZM3.08301 5.75098C2.55962 5.75117 2.13512 6.17587 2.13477 6.69922C2.13477 7.22287 2.5594 7.64824 3.08301 7.64844C3.60665 7.64828 4.03223 7.2229 4.03223 6.69922C4.03187 6.17585 3.60643 5.75113 3.08301 5.75098ZM5.19238 6.69922C5.19238 6.763 5.18816 6.82633 5.18262 6.88867C5.18846 6.82629 5.19238 6.76313 5.19238 6.69922C5.19236 6.63495 5.18853 6.57152 5.18262 6.50879C5.18826 6.57154 5.19236 6.635 5.19238 6.69922ZM0.982422 6.52344C0.977382 6.58136 0.97463 6.63999 0.974609 6.69922C0.974609 6.75775 0.977496 6.81579 0.982422 6.87305C0.977758 6.81579 0.974609 6.75767 0.974609 6.69922C0.974628 6.64 0.977618 6.58142 0.982422 6.52344ZM10.3232 0C11.2869 0 12.0986 0.646596 12.3506 1.5293L13.415 1.5293L13.415 2.68945L12.3496 2.68945C12.363 2.64266 12.3754 2.59488 12.3857 2.54688C12.1838 3.50118 11.3376 4.21777 10.3232 4.21777C9.36037 4.21756 8.55018 3.57139 8.29785 2.68945L0 2.68945L0 1.5293L8.29688 1.5293C8.5487 0.646717 9.35981 0.00021854 10.3232 0ZM10.3232 1.16016C9.79984 1.16042 9.37524 1.58499 9.375 2.1084C9.375 2.63201 9.79969 3.05735 10.3232 3.05762C10.847 3.05762 11.2725 2.63217 11.2725 2.1084C11.2722 1.58483 10.8469 1.16016 10.3232 1.16016ZM12.4229 2.29883C12.4287 2.23641 12.4326 2.17331 12.4326 2.10938C12.4326 2.17327 12.4284 2.23638 12.4229 2.29883ZM8.21484 2.10938L8.21484 2.1084L8.21484 2.10938ZM8.22266 1.93359C8.21785 1.98897 8.21506 2.04499 8.21484 2.10156C8.21503 2.04501 8.2181 1.98902 8.22266 1.93359ZM8.22266 11.1162C8.2179 11.1713 8.21507 11.227 8.21484 11.2832C8.21504 11.227 8.21814 11.1713 8.22266 11.1162Z",
		fill: "currentColor"
	})
});
/** ic_ds_project_add_outline_16 (figma extract) */
const IconProjectAddOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	children: [jsx("path", {
		transform: "translate(9.52 2.52)",
		d: "M3.55246 0L3.55246 2.44252L6 2.44252L6 3.55748L3.55246 3.55748L3.55246 6L2.43834 6L2.43834 3.55748L0 3.55748L0 2.44252L2.43834 2.44252L2.43834 0L3.55246 0Z",
		fill: "currentColor"
	}), jsx("path", {
		transform: "translate(0.3496 2.35)",
		d: "M4.76367 0C5.36861 1.80598e-05 5.93113 0.310294 6.25488 0.821289L6.78027 1.64941C6.79685 1.67558 6.81791 1.69775 6.83887 1.71973C6.72186 2.15521 6.65702 2.61192 6.65137 3.08301C6.25601 2.96045 5.90909 2.70478 5.68164 2.3457L5.15723 1.5166C5.07183 1.38189 4.92318 1.3008 4.76367 1.30078L2.32422 1.30078C1.7589 1.30078 1.30078 1.7589 1.30078 2.32422L1.30078 10.1338C1.30078 10.6991 1.7589 11.1572 2.32422 11.1572L11.9766 11.1572C12.5419 11.1572 13 10.6991 13 10.1338L13 8.58398C13.4545 8.5135 13.8903 8.38748 14.3008 8.21289L14.3008 10.1338C14.3008 11.4171 13.2598 12.458 11.9766 12.458L2.32422 12.458C1.04093 12.458 0 11.4171 0 10.1338L0 2.32422C0 1.04093 1.04093 0 2.32422 0L4.76367 0Z",
		fill: "currentColor"
	})]
});
/**
* folder_open_16, outline layer only: the duotone original above reads a rung
* heavier than the …Outline16 family, so an icon-button row mixing them looks
* mismatched — this is the same geometry without the 20%-opacity inner fill.
*/
const IconFolderOpenOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	children: jsx("path", {
		d: "M5.19629 1.57104C5.81144 1.5711 6.38623 1.8786 6.72754 2.39038L7.19922 3.09839C7.28454 3.22635 7.42824 3.30344 7.58203 3.30347H12.1699C13.5039 3.30348 14.5859 4.38548 14.5859 5.71948V6.62671C15.2694 7.02689 15.6605 7.85012 15.4385 8.68726L14.3848 12.658C14.1037 13.7164 13.1449 14.4527 12.0498 14.4529H2.91699C1.51651 14.4529 0.451662 13.2814 0.501954 11.9519V3.98706C0.501954 2.65305 1.58396 1.57104 2.91797 1.57104H5.19629ZM3.7793 7.75562C3.30994 7.75562 2.89883 8.07153 2.77832 8.52515L1.91602 11.7722C1.74167 12.4291 2.23734 13.073 2.91699 13.073H12.0498C12.5191 13.0728 12.9304 12.757 13.0508 12.3035L14.1045 8.33374C14.1819 8.04202 13.9619 7.756 13.6602 7.75562H3.7793ZM2.91797 2.9519C2.34625 2.9519 1.88281 3.41534 1.88281 3.98706V7.2937C2.33068 6.7269 3.02249 6.37476 3.7793 6.37476H13.2051V5.71948C13.2051 5.14777 12.7416 4.68434 12.1699 4.68433H7.58203C6.96675 4.6843 6.39209 4.37595 6.05078 3.86401L5.5791 3.15601C5.49379 3.02821 5.34995 2.95196 5.19629 2.9519H2.91797Z",
		fill: "currentColor"
	})
});
/** folder_open_16 (figma extract): outline at full ink + 20%-opacity inner fill riding the same currentColor. */
const IconFolderOpen16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	children: [jsx("path", {
		d: "M5.19629 1.57104C5.81144 1.5711 6.38623 1.8786 6.72754 2.39038L7.19922 3.09839C7.28454 3.22635 7.42824 3.30344 7.58203 3.30347H12.1699C13.5039 3.30348 14.5859 4.38548 14.5859 5.71948V6.62671C15.2694 7.02689 15.6605 7.85012 15.4385 8.68726L14.3848 12.658C14.1037 13.7164 13.1449 14.4527 12.0498 14.4529H2.91699C1.51651 14.4529 0.451662 13.2814 0.501954 11.9519V3.98706C0.501954 2.65305 1.58396 1.57104 2.91797 1.57104H5.19629ZM3.7793 7.75562C3.30994 7.75562 2.89883 8.07153 2.77832 8.52515L1.91602 11.7722C1.74167 12.4291 2.23734 13.073 2.91699 13.073H12.0498C12.5191 13.0728 12.9304 12.757 13.0508 12.3035L14.1045 8.33374C14.1819 8.04202 13.9619 7.756 13.6602 7.75562H3.7793ZM2.91797 2.9519C2.34625 2.9519 1.88281 3.41534 1.88281 3.98706V7.2937C2.33068 6.7269 3.02249 6.37476 3.7793 6.37476H13.2051V5.71948C13.2051 5.14777 12.7416 4.68434 12.1699 4.68433H7.58203C6.96675 4.6843 6.39209 4.37595 6.05078 3.86401L5.5791 3.15601C5.49379 3.02821 5.34995 2.95196 5.19629 2.9519H2.91797Z",
		fill: "currentColor"
	}), jsx("path", {
		opacity: "0.2",
		d: "M13.6602 7.75525C13.9618 7.7556 14.1815 8.04179 14.1045 8.33337L13.0508 12.3031C12.9304 12.7567 12.5191 13.0725 12.0498 13.0726H2.91701C2.23744 13.0725 1.7417 12.4287 1.91603 11.7719L2.77834 8.52478C2.89898 8.07146 3.31018 7.75532 3.77931 7.75525H13.6602ZM5.1963 2.95154C5.34985 2.95159 5.49377 3.02803 5.57912 3.15564L6.0508 3.86365C6.39205 4.37553 6.96685 4.68385 7.58205 4.68396H12.1699C12.7416 4.68396 13.2049 5.14754 13.2051 5.71912V6.37439H3.77931C3.02267 6.37444 2.33067 6.72671 1.88283 7.29333V3.98669C1.88299 3.4152 2.34649 2.95168 2.91798 2.95154H5.1963Z",
		fill: "currentColor"
	})]
});
/** folder_close_16 (figma extract) */
const IconFolderClose16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	children: jsx("path", {
		transform: "translate(1.5 2.429)",
		d: "M5.05582 0.518756L4.50669 0.86654L5.05582 0.518756ZM13 9.4837L13.65 9.4837L13.65 3.53962L13 3.53962L12.35 3.53962L12.35 9.4837L13 9.4837ZM11.3264 1.86603L11.3264 1.21603L6.52313 1.21603L6.52313 1.86603L6.52313 2.51603L11.3264 2.51603L11.3264 1.86603ZM5.58054 1.34727L6.12968 0.999489L5.60495 0.170972L5.05582 0.518756L4.50669 0.86654L5.03141 1.69506L5.58054 1.34727ZM4.11323 1.23058e-13L4.11323 -0.65L1.67359 -0.65L1.67359 5.00699e-14L1.67359 0.65L4.11323 0.65L4.11323 1.23058e-13ZM0 1.67359L-0.65 1.67359L-0.65 9.4837L0 9.4837L0.65 9.4837L0.65 1.67359L0 1.67359ZM11.3264 11.1573L11.3264 10.5073L1.67359 10.5073L1.67359 11.1573L1.67359 11.8073L11.3264 11.8073L11.3264 11.1573ZM0 9.4837L-0.65 9.4837C-0.65 10.767 0.390308 11.8073 1.67359 11.8073L1.67359 11.1573L1.67359 10.5073C1.10828 10.5073 0.65 10.049 0.65 9.4837L0 9.4837ZM1.67359 5.00699e-14L1.67359 -0.65C0.390307 -0.65 -0.65 0.390309 -0.65 1.67359L0 1.67359L0.65 1.67359C0.65 1.10828 1.10828 0.65 1.67359 0.65L1.67359 5.00699e-14ZM5.05582 0.518756L5.60495 0.170972C5.28121 -0.340193 4.71829 -0.65 4.11323 -0.65L4.11323 1.23058e-13L4.11323 0.65C4.27282 0.65 4.4213 0.731715 4.50669 0.86654L5.05582 0.518756ZM6.52313 1.86603L6.52313 1.21603C6.36354 1.21603 6.21507 1.13431 6.12968 0.999489L5.58054 1.34727L5.03141 1.69506C5.35515 2.20622 5.91808 2.51603 6.52313 2.51603L6.52313 1.86603ZM13 3.53962L13.65 3.53962C13.65 2.25634 12.6097 1.21603 11.3264 1.21603L11.3264 1.86603L11.3264 2.51603C11.8917 2.51603 12.35 2.97431 12.35 3.53962L13 3.53962ZM13 9.4837L12.35 9.4837C12.35 10.049 11.8917 10.5073 11.3264 10.5073L11.3264 11.1573L11.3264 11.8073C12.6097 11.8073 13.65 10.767 13.65 9.4837L13 9.4837Z",
		fill: "currentColor"
	})
});
/** tree_corner_8x10 (figma extract; session-tree "L" connector, stroke geometry pre-expanded) */
const IconTreeCorner8x10 = ({ size = 10, className }) => jsx("svg", {
	width: size * 8 / 10,
	height: size,
	className,
	viewBox: "-0.5 0 8.5 10.5",
	fill: "none",
	children: jsx("path", {
		d: "M0 0L-0.5 0L-0.5 7L0 7L0.5 7L0.5 0L0 0ZM3 10L3 10.5L8 10.5L8 10L8 9.5L3 9.5L3 10ZM0 7L-0.5 7C-0.5 8.933 1.067 10.5 3 10.5L3 10L3 9.5C1.61929 9.5 0.5 8.38071 0.5 7L0 7Z",
		fill: "currentColor"
	})
});
/** ic_ds_light_outline_16 */
const IconLightOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [
		jsx("path", {
			d: "M11.3496 8C11.3496 6.14985 9.85015 4.65039 8 4.65039C6.14985 4.65039 4.65039 6.14985 4.65039 8C4.65039 9.85015 6.14985 11.3496 8 11.3496C9.85015 11.3496 11.3496 9.85015 11.3496 8ZM12.6504 8C12.6504 10.5681 10.5681 12.6504 8 12.6504C5.43188 12.6504 3.34961 10.5681 3.34961 8C3.34961 5.43188 5.43188 3.34961 8 3.34961C10.5681 3.34961 12.6504 5.43188 12.6504 8Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M8.65039 0.5V2.5H7.34961V0.5H8.65039Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M8.65039 13.5V15.5H7.34961V13.5H8.65039Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M3.15808 2.24035L4.57229 3.65456L3.6525 4.57435L2.23829 3.16014L3.15808 2.24035Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M12.3505 11.4327L13.7647 12.8469L12.8449 13.7667L11.4307 12.3525L12.3505 11.4327Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M2.24537 12.8469L3.65958 11.4327L4.57937 12.3525L3.16516 13.7667L2.24537 12.8469Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M11.4377 3.65455L12.852 2.24033L13.7718 3.16012L12.3575 4.57434L11.4377 3.65455Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M0.5 7.35461H2.5V8.6554H0.5L0.5 7.35461Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M13.5 7.35461H15.5V8.6554H13.5V7.35461Z",
			fill: "currentColor"
		})
	]
});
/** ic_ds_dark_outline_16 */
const IconDarkOutline16 = ({ size = 16, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M13.2764 9.52324C12.5607 9.97754 11.7177 10.242 10.7812 10.242C8.11386 10.2419 5.95042 8.07997 5.9502 5.41289C5.9502 4.48128 6.21453 3.61071 6.67188 2.87285C4.30332 3.4658 2.54992 5.60845 2.5498 8.16093C2.5498 11.1712 4.99103 13.6102 8 13.6102C10.5383 13.6102 12.6709 11.8724 13.2764 9.52324ZM7.05078 5.41289C7.051 7.47224 8.72116 9.1423 10.7812 9.14238C11.9248 9.14238 12.887 8.63397 13.5781 7.8084C13.7266 7.63106 13.9701 7.56547 14.1875 7.64433C14.4049 7.72329 14.5497 7.9297 14.5498 8.16093C14.5498 11.7766 11.6161 14.7098 8 14.7098C4.38402 14.7098 1.4502 11.7792 1.4502 8.16093C1.45033 4.54322 4.3812 1.61015 8 1.61015C8.23027 1.61015 8.43585 1.75352 8.51562 1.96953C8.59536 2.18554 8.53241 2.42829 8.35742 2.57793C7.55573 3.26311 7.05078 4.27876 7.05078 5.41289Z",
		fill: "currentColor"
	})
});
/** ic_ds_followsystem_outline_16 */
const IconFollowsystemOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsx("path", {
		d: "M12.1665 13.5811V14.7803H3.66651V13.5811H12.1665Z",
		fill: "currentColor"
	}), jsx("path", {
		d: "M13.4453 7.02379C13.4453 6.04702 13.4452 5.3616 13.3887 4.83434C13.3333 4.31828 13.2302 4.02378 13.0723 3.80309C12.9446 3.62475 12.7877 3.46883 12.6094 3.34117C12.3887 3.18328 12.0942 3.08007 11.5781 3.02477C11.0508 2.96829 10.3655 2.96715 9.38867 2.96715H6.61035C5.63359 2.96715 4.94816 2.96827 4.4209 3.02477C3.90486 3.0801 3.61034 3.18321 3.38965 3.34117C3.21143 3.46878 3.05534 3.62487 2.92774 3.80309C2.76977 4.02377 2.66667 4.3183 2.61133 4.83434C2.55483 5.3616 2.55371 6.04702 2.55371 7.02379C2.55371 8.0006 2.55485 8.68596 2.61133 9.21324C2.66663 9.72936 2.76983 10.0238 2.92774 10.2445C3.0554 10.4228 3.21131 10.5797 3.38965 10.7074C3.61034 10.8654 3.90484 10.9685 4.4209 11.0238C4.94816 11.0803 5.63359 11.0804 6.61035 11.0804H9.38867C10.3654 11.0804 11.0508 11.0803 11.5781 11.0238C12.0941 10.9685 12.3887 10.8652 12.6094 10.7074C12.7877 10.5797 12.9446 10.4229 13.0723 10.2445C13.2301 10.0238 13.3334 9.72927 13.3887 9.21324C13.4452 8.68596 13.4453 8.00058 13.4453 7.02379ZM14.6455 7.02379C14.6455 7.97428 14.646 8.73509 14.5811 9.34117C14.5149 9.95828 14.3756 10.4858 14.0479 10.9437C13.8436 11.229 13.5938 11.4788 13.3086 11.683C12.8507 12.0108 12.3232 12.15 11.7061 12.2162C11.1 12.2811 10.3391 12.2806 9.38867 12.2806H6.61035C5.66018 12.2806 4.89991 12.2811 4.29395 12.2162C3.67684 12.15 3.14935 12.0108 2.69141 11.683C2.40613 11.4788 2.15639 11.229 1.95215 10.9437C1.62436 10.4858 1.4841 9.95828 1.41797 9.34117C1.35305 8.73511 1.35449 7.97424 1.35449 7.02379C1.35449 6.07366 1.35308 5.31333 1.41797 4.70738C1.4841 4.09028 1.62436 3.56279 1.95215 3.10485C2.15638 2.81956 2.40613 2.56982 2.69141 2.36559C3.14935 2.03779 3.67684 1.89753 4.29395 1.83141C4.8999 1.76652 5.66022 1.76793 6.61035 1.76793H9.38867C10.3391 1.76793 11.1 1.76649 11.7061 1.83141C12.3232 1.89753 12.8507 2.03779 13.3086 2.36559C13.5939 2.56982 13.8436 2.81957 14.0479 3.10485C14.3756 3.56279 14.5149 4.09028 14.5811 4.70738C14.646 5.31335 14.6455 6.07362 14.6455 7.02379Z",
		fill: "currentColor"
	})]
});
/** ic_ds_data_outline_16 */
const IconDataOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsx("path", {
		fillRule: "evenodd",
		clipRule: "evenodd",
		d: "M12.0997 8.54554C12.2905 8.54989 12.3541 8.58056 12.4535 8.74614L12.8849 9.46387C12.9851 9.63071 13.0464 9.66013 13.2388 9.66447H14.1138C14.3417 9.66448 14.3512 9.66937 14.4686 9.86507L14.892 10.5717C14.9942 10.7422 14.9948 10.8247 14.892 10.9961L14.4756 11.6906C14.3741 11.8677 14.3694 11.9379 14.4756 12.115L14.892 12.8096C14.9942 12.9801 14.9947 13.0625 14.892 13.234L14.4686 13.9406C14.3643 14.1028 14.3063 14.1354 14.1138 14.1412H13.2388C13.0465 14.1456 12.985 14.1752 12.8849 14.3418L12.4535 15.0595C12.353 15.2195 12.2895 15.2558 12.0997 15.2601H11.2237C10.9962 15.2601 10.9871 15.2548 10.8699 15.0595L10.4384 14.3418C10.3383 14.175 10.2767 14.1456 10.0846 14.1412H9.2096C9.01854 14.1355 8.95761 14.1006 8.85477 13.9406L8.43139 13.234C8.32562 13.0576 8.33148 12.9862 8.43139 12.8096L8.84771 12.115C8.95165 11.9416 8.94659 11.863 8.84771 11.6906L8.43139 10.9961C8.32767 10.8232 8.33411 10.7437 8.43139 10.5717L8.85477 9.86507C8.95447 9.69891 9.01875 9.67017 9.2096 9.66447H10.0846C10.2741 9.66441 10.3414 9.62547 10.4384 9.46387L10.8699 8.74614C10.987 8.55106 10.9963 8.54554 11.2237 8.54554H12.0997ZM11.6612 10.232C11.3326 10.7798 10.8155 11.0948 10.1743 11.106C10.4443 11.61 10.4425 12.1976 10.1743 12.6987C10.803 12.7096 11.3391 13.0359 11.6612 13.5727C11.9855 13.0323 12.5131 12.7098 13.148 12.6987C12.879 12.196 12.8789 11.6086 13.148 11.106C12.5076 11.0948 11.9894 10.7794 11.6612 10.232Z",
		fill: "currentColor"
	}), jsx("path", {
		fillRule: "evenodd",
		clipRule: "evenodd",
		d: "M7.51205 0.790627C9.19055 0.790649 10.7401 1.0691 11.892 1.54364C12.4664 1.78029 12.9719 2.07885 13.3436 2.4408C13.7171 2.80467 13.9916 3.27253 13.9918 3.82384V7.90442C13.6067 7.69532 13.1907 7.53597 12.7529 7.43366V5.66454C12.4928 5.82898 12.2028 5.97601 11.892 6.10405C10.74 6.57865 9.19071 6.85706 7.51205 6.85706C5.8337 6.85703 4.285 6.57852 3.13309 6.10405C2.82215 5.97593 2.53164 5.8291 2.27121 5.66454V7.4135C2.27134 7.75678 2.6066 8.27106 3.62502 8.73405C4.58641 9.17097 5.95762 9.45591 7.50499 9.45681C7.24582 9.83133 7.03684 10.2434 6.88706 10.6826C5.44388 10.6162 4.12516 10.3216 3.11192 9.86104C2.81708 9.72698 2.53185 9.56866 2.27121 9.38928V11.2542C2.27158 11.5974 2.60697 12.1109 3.62502 12.5737C4.41933 12.9347 5.4937 13.1898 6.71569 13.2693C6.80349 13.7128 6.9513 14.1345 7.14814 14.5273C5.60324 14.4862 4.18593 14.1889 3.11192 13.7007C2.01039 13.1998 1.03366 12.3814 1.03333 11.2542V3.82384C1.03352 3.27273 1.30721 2.80461 1.68049 2.4408C2.05211 2.07893 2.55887 1.78026 3.13309 1.54364C4.28492 1.06926 5.83393 0.790683 7.51205 0.790627ZM7.51205 2.02851C5.95492 2.02857 4.57354 2.29079 3.60486 2.68979C3.11958 2.88977 2.76667 3.11253 2.5454 3.32788C2.32671 3.54101 2.2714 3.7089 2.27121 3.82384C2.27121 3.93882 2.32624 4.10625 2.5454 4.3198C2.76667 4.53527 3.11927 4.75781 3.60486 4.9579C4.5736 5.35699 5.95467 5.61914 7.51205 5.61918C9.06942 5.61918 10.4505 5.35695 11.4192 4.9579C11.9051 4.75773 12.2584 4.53536 12.4797 4.3198C12.6988 4.10627 12.7529 3.93882 12.7529 3.82384C12.7527 3.70889 12.6984 3.54104 12.4797 3.32788C12.2584 3.11239 11.9049 2.88989 11.4192 2.68979C10.4505 2.29079 9.06925 2.02853 7.51205 2.02851Z",
		fill: "currentColor"
	})]
});
/** ic_send_outline_14 (figma extract): thin-stroke upward send arrow. */
const IconSendOutline14 = ({ size = 14, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M7.24707 1.01771C7.52897 1.07653 7.77619 1.19694 8.00391 1.38001C8.19202 1.53136 8.39884 1.73784 8.61914 1.95814L12.6396 5.9806L11.6299 6.99134L7.71484 3.0763V13.0001H6.28516V3.0763L2.36914 6.99134L1.35938 5.9806L5.38086 1.95814C5.60116 1.73784 5.80798 1.53136 5.99609 1.38001C6.19476 1.22027 6.4385 1.06739 6.75195 1.01771C6.91296 0.992304 7.07471 0.997504 7.24707 1.01771Z",
		fill: "currentColor"
	})
});
/** ic_queue_outline_14 (figma extract): open chat bubble with two queued lines. */
const IconQueueOutline14 = ({ size = 14, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: jsx("path", {
		d: "M7.00049 0.199829C3.24488 0.199829 0.199952 3.24408 0.199707 6.99963C0.199707 8.0414 0.434087 9.03061 0.854004 9.91467L1.11279 10.4576L2.19775 9.94202L1.94092 9.39905L1.81787 9.12268C1.5498 8.46885 1.40186 7.75171 1.40186 6.99963C1.4021 3.90808 3.90888 1.40198 7.00049 1.40198C10.0919 1.40219 12.5979 3.90821 12.5981 6.99963C12.5981 10.0913 10.0921 12.5981 7.00049 12.5983C6.36734 12.5983 5.90348 12.5535 5.49268 12.4401C5.08803 12.3283 4.7041 12.1414 4.24463 11.8209C3.57111 11.3511 2.60588 11.1855 1.81006 11.6881L1.79736 11.6959L1.78467 11.7047L1.25537 12.0778L1.65381 13.2672L2.46045 12.6989C2.75029 12.5214 3.18004 12.5442 3.55615 12.8063C4.10063 13.1861 4.60863 13.4423 5.17334 13.5983C5.73194 13.7525 6.31665 13.8004 7.00049 13.8004C10.7561 13.8002 13.8003 10.7553 13.8003 6.99963C13.8 3.24421 10.7559 0.200041 7.00049 0.199829ZM3.81201 7.47327V8.67542H7.11572V7.47327H3.81201ZM3.81201 6.34924H10.2173V5.14709H3.81201V6.34924Z",
		fill: "currentColor"
	})
});
/** ic_checklist_outline_14 (figma extract): two rings + two list bars. */
const IconChecklistOutline14 = ({ size = 14, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [
		jsx("path", {
			d: "M13.3277 9.69629V10.976H7.28086V9.69629H13.3277Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M13.3277 2.97256V4.25225H7.28086V2.97256H13.3277Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M4.64512 10.336C4.64505 9.62755 4.07081 9.05322 3.3623 9.05322C2.65386 9.05329 2.07956 9.62759 2.07949 10.336C2.07949 11.0445 2.65382 11.6188 3.3623 11.6188C4.07085 11.6188 4.64512 11.0446 4.64512 10.336ZM5.92559 10.336C5.92559 11.7515 4.77777 12.8993 3.3623 12.8993C1.94689 12.8993 0.799805 11.7515 0.799805 10.336C0.799871 8.92066 1.94693 7.7736 3.3623 7.77354C4.77773 7.77354 5.92552 8.92062 5.92559 10.336Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M4.64531 3.6123C4.6453 2.90382 4.07098 2.32949 3.3625 2.32949C2.65403 2.32951 2.0797 2.90383 2.07969 3.6123C2.07969 4.32079 2.65402 4.8951 3.3625 4.89512C4.07099 4.89512 4.64531 4.3208 4.64531 3.6123ZM5.925 3.6123C5.925 5.02772 4.77792 6.1748 3.3625 6.1748C1.9471 6.17479 0.8 5.02771 0.8 3.6123C0.800013 2.19691 1.9471 1.04982 3.3625 1.0498C4.77791 1.0498 5.92499 2.1969 5.925 3.6123Z",
			fill: "currentColor"
		})
	]
});
/** ic_ds_List_Pen_outline_16 */
const IconListPenOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [
		jsx("path", {
			d: "M10.8239 3.54733V4.78443H4.63437V3.54733H10.8239Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M10.8239 6.12629V7.36338H4.63437V6.12629H10.8239Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M9.073 8.70524V9.94234H4.63437V8.70524H9.073Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M9.13321 0.573526C10.0076 0.573525 10.7179 0.572522 11.285 0.63397C11.8645 0.696791 12.3743 0.831648 12.8193 1.1548C13.0776 1.34246 13.3056 1.57047 13.4933 1.82875C13.8164 2.2737 13.9513 2.7836 14.0141 3.36303C14.0755 3.93015 14.0745 4.64049 14.0745 5.51485V6.1757L12.7327 7.5629V5.51485C12.7327 4.61092 12.732 3.9862 12.6803 3.5081C12.6298 3.0427 12.5379 2.79497 12.4083 2.61654C12.3033 2.47211 12.176 2.34472 12.0315 2.23977C11.8531 2.11016 11.6054 2.01823 11.14 1.96777C10.6618 1.91601 10.0372 1.91539 9.13321 1.91539H6.32658C5.42262 1.91539 4.79796 1.91604 4.31983 1.96777C3.85451 2.01819 3.60672 2.11029 3.42827 2.23977C3.28392 2.34465 3.15643 2.47223 3.0515 2.61654C2.9219 2.79496 2.82997 3.04274 2.7795 3.5081C2.72774 3.9862 2.72712 4.61092 2.72712 5.51485V10.023C2.72712 10.9273 2.72773 11.5525 2.7795 12.0307C2.82992 12.4959 2.92205 12.7429 3.0515 12.9213C3.15645 13.0657 3.28384 13.1931 3.42827 13.2981C3.60676 13.4277 3.85408 13.5206 4.31983 13.5711C4.79797 13.6228 5.42259 13.6234 6.32658 13.6234H6.87057L5.57707 14.9593C5.03527 14.9556 4.57031 14.9467 4.17476 14.9039C3.59508 14.841 3.08558 14.7063 2.64048 14.383C2.38215 14.1953 2.15422 13.9684 1.96653 13.7101C1.64319 13.2649 1.50851 12.7546 1.4457 12.1748C1.38432 11.6076 1.38525 10.8974 1.38525 10.023V5.51485C1.38525 4.64049 1.38426 3.93015 1.4457 3.36303C1.50853 2.78363 1.64341 2.27368 1.96653 1.82875C2.15417 1.57059 2.38228 1.34239 2.64048 1.1548C3.08544 0.831805 3.59533 0.696762 4.17476 0.63397C4.74193 0.572552 5.45218 0.573525 6.32658 0.573526H9.13321Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M14.2193 14.9553H10.0124L11.3744 13.6134H14.2193V14.9553Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M8.24493 13.3711L7.49015 14.8806C7.40148 15.058 7.58961 15.2461 7.76695 15.1574L9.27651 14.4027L14.6147 9.09934L13.5832 8.06775L8.24493 13.3711Z",
			fill: "currentColor"
		})
	]
});
/** ic_ds_goal_outline_16 (goal strip leading glyph: dartboard with a landed arrow) */
const IconGoalOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [
		jsx("path", {
			d: "M8 0C8.31451 0 8.62464 0.019379 8.92969 0.0546875C8.48228 0.403371 8.0952 0.825758 7.78809 1.30469C4.18586 1.41664 1.2998 4.37061 1.2998 8C1.2998 11.7003 4.29969 14.7002 8 14.7002C11.6297 14.7002 14.5829 11.8136 14.6943 8.21094C15.1734 7.90377 15.5956 7.51688 15.9443 7.06934C15.9797 7.37473 16 7.68512 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM7.0166 3.6084C7.00658 3.73765 7 3.86817 7 4C7 4.31845 7.03098 4.62973 7.08789 4.93164C5.76489 5.32438 4.7998 6.54958 4.7998 8C4.7998 9.76731 6.23269 11.2002 8 11.2002C9.45065 11.2002 10.6749 10.2345 11.0674 8.91113C11.3696 8.96818 11.6812 9 12 9C12.1315 9 12.2617 8.99239 12.3906 8.98242C11.9423 10.995 10.1477 12.5 8 12.5C5.51472 12.5 3.5 10.4853 3.5 8C3.5 5.85255 5.00435 4.05702 7.0166 3.6084Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M7.5 8.62109L9.12109 7",
			stroke: "currentColor",
			strokeWidth: "1.3"
		}),
		jsx("path", {
			d: "M9.08245 3.35798L11.8651 0.575334C11.895 0.545384 11.9463 0.56391 11.9502 0.606086L12.2362 3.69859C12.2384 3.72259 12.2574 3.74159 12.2814 3.74378L15.3697 4.02583C15.4119 4.02968 15.4305 4.08101 15.4005 4.11098L12.618 6.89351C12.6086 6.90289 12.5959 6.90816 12.5826 6.90816L9.11781 6.90815C9.09019 6.90816 9.06781 6.88577 9.06781 6.85816L9.06781 3.39333C9.06781 3.38007 9.07308 3.36735 9.08245 3.35798Z",
			stroke: "currentColor",
			strokeWidth: "1.3"
		})
	]
});
/** sparkle_16 (Others tool-row leading glyph; hand-authored three-star
*  approximation — the figma 43:31850 glyph is an SF Symbols "sparkles" text glyph,
*  not extractable as vector data) */
const IconSparkle16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [
		jsx("path", {
			d: "M6.1 3.1Q6.6 7.8 11.3 8.3Q6.6 8.8 6.1 13.5Q5.6 8.8 0.9 8.3Q5.6 7.8 6.1 3.1Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M11.9 1Q12.2 3.7 14.9 4Q12.2 4.3 11.9 7Q11.6 4.3 8.9 4Q11.6 3.7 11.9 1Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M12.5 9.4Q12.7 11.4 14.7 11.6Q12.7 11.8 12.5 13.8Q12.3 11.8 10.3 11.6Q12.3 11.4 12.5 9.4Z",
			fill: "currentColor"
		})
	]
});
/** inspect_outline_12 (shared tool-row trajectory affordance glyph) */
const IconInspectOutline12 = ({ size = 12, className }) => jsx("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	"aria-hidden": true,
	children: jsx("path", {
		d: "M16 8L10.8571 12V10.552L14.1383 8L10.8571 5.448V4L16 8ZM5.14286 10.552L1.86171 8L5.14286 5.448V4L0 8L5.14286 12V10.552ZM9.02514 4L5.59657 12H6.84057L10.2691 4H9.02514Z",
		fill: "currentColor"
	})
});
/** skill_outline_16 (skill tool-row glyph; document instructions + sparkle) */
const IconSkillOutline16 = ({ size = 16, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 16 16",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsx("path", {
		d: "M12.5113 15.4067C12.4395 15.6249 12.1308 15.6249 12.059 15.4067L11.643 14.1416C11.454 13.567 11.0033 13.1164 10.4288 12.9274L9.16369 12.5113C8.94544 12.4395 8.94544 12.1308 9.16369 12.059L10.4288 11.643C11.0033 11.454 11.454 11.0033 11.643 10.4288L12.059 9.16369C12.1308 8.94544 12.4395 8.94544 12.5113 9.16369L12.9274 10.4288C13.1164 11.0033 13.567 11.454 14.1416 11.643L15.4067 12.059C15.6249 12.1308 15.6249 12.4395 15.4067 12.5113L14.1416 12.9274C13.567 13.1164 13.1164 13.567 12.9274 14.1416L12.5113 15.4067Z",
		fill: "currentColor"
	}), jsx("path", {
		d: "M9.02246 0.546878C9.9822 0.546878 10.7564 0.545403 11.374 0.612307C12.0042 0.680586 12.5515 0.826244 13.0273 1.17188C13.3052 1.37376 13.5501 1.61868 13.752 1.89649C14.0975 2.37225 14.2432 2.91984 14.3115 3.54981C14.3784 4.16727 14.377 4.94206 14.377 5.90137V8.51367C13.9611 8.29533 13.5071 8.13985 13.0273 8.06055V5.90137C13.0273 4.9121 13.0259 4.22322 12.9688 3.69532C12.9129 3.18044 12.8098 2.89782 12.6592 2.69043C12.5406 2.52724 12.3966 2.38326 12.2334 2.26465C12.026 2.11404 11.7437 2.0109 11.2285 1.95508C10.7005 1.89789 10.0122 1.89649 9.02246 1.89649H6.55371C5.56395 1.89649 4.87569 1.89787 4.34766 1.95508C3.83242 2.01092 3.55022 2.11398 3.34278 2.26465C3.17953 2.38329 3.03564 2.52719 2.91699 2.69043C2.76642 2.89782 2.66325 3.18042 2.60742 3.69532C2.55027 4.22322 2.54883 4.9121 2.54883 5.90137V10.0986C2.54883 11.0878 2.55031 11.7768 2.60742 12.3047C2.66326 12.8196 2.76642 13.1032 2.91699 13.3105C3.03558 13.4736 3.17966 13.6178 3.34278 13.7363C3.5502 13.8869 3.83265 13.9901 4.34766 14.0459C4.87568 14.1031 5.56398 14.1035 6.55371 14.1035H8.08399C8.27443 14.6025 8.55077 15.0585 8.89551 15.4541H6.55371C5.59402 15.4541 4.81976 15.4546 4.20215 15.3877C3.57204 15.3194 3.02468 15.1738 2.54883 14.8281C2.27111 14.6263 2.02606 14.3813 1.82422 14.1035C1.47883 13.6278 1.33293 13.08 1.26465 12.4502C1.19783 11.8327 1.19922 11.0579 1.19922 10.0986V5.90137C1.19922 4.94206 1.1978 4.16727 1.26465 3.54981C1.33295 2.91984 1.47867 2.37225 1.82422 1.89649C2.02613 1.61864 2.27098 1.37379 2.54883 1.17188C3.02472 0.826181 3.57197 0.6806 4.20215 0.612307C4.81976 0.545393 5.594 0.546877 6.55371 0.546878H9.02246ZM9.19629 9.14649H4.5459V7.84571H9.19629V9.14649ZM11.0303 6.10645H4.5459V4.80567H11.0303V6.10645Z",
		fill: "currentColor"
	})]
});
/** ic_ds_question_outline_14 (figma extract): ring + question glyph. */
const IconQuestionOutline14 = ({ size = 14, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 14 14",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [
		jsx("path", {
			d: "M12.5757 7.00012C12.5757 3.92085 10.0794 1.42463 7.00012 1.42456C3.9208 1.42456 1.42456 3.9208 1.42456 7.00012C1.42463 10.0794 3.92085 12.5757 7.00012 12.5757C10.0793 12.5756 12.5756 10.0793 12.5757 7.00012ZM13.8002 7.00012C13.8001 10.7559 10.7559 13.8001 7.00012 13.8002C3.2443 13.8002 0.199291 10.7559 0.199219 7.00012C0.199219 3.24426 3.24426 0.199219 7.00012 0.199219C10.7559 0.199291 13.8002 3.2443 13.8002 7.00012Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M6.18042 8.68184C6.18043 8.09153 6.32893 7.34655 6.92127 6.8481C7.28566 6.54148 7.76104 6.27318 8.0022 6.10811C8.28964 5.91137 8.42234 5.76562 8.48328 5.58944C8.57774 5.31609 8.53121 5.00904 8.34912 4.76741C8.17409 4.53522 7.83879 4.32222 7.28186 4.32222C5.99668 4.32225 5.46969 5.11832 5.46949 5.78939H4.24414C4.24436 4.39942 5.36327 3.09691 7.28186 3.09688C8.17773 3.09688 8.89489 3.45606 9.32752 4.02999C9.75287 4.59438 9.86938 5.32775 9.64026 5.99019C9.44847 6.5444 9.04722 6.87743 8.69434 7.11898C8.29506 7.39226 8.02318 7.52192 7.70996 7.78548C7.51943 7.94582 7.40577 8.24899 7.40577 8.68184V8.75533H6.18042V8.68184Z",
			fill: "currentColor"
		}),
		jsx("path", {
			d: "M7.39455 9.44026V10.8109H6.16921V9.44026H7.39455Z",
			fill: "currentColor"
		})
	]
});
/** ic_ds_archive_outline_20 (figma extract): lidded box + label slot. The export's
*  0.11px stroke ring around the box contour is dropped — it restates the same
*  contour in the same ink, which currentColor already carries. */
const IconArchiveOutline20 = ({ size = 20, className }) => jsxs("svg", {
	width: size,
	height: size,
	className,
	viewBox: "0 0 20 20",
	fill: "none",
	xmlns: "http://www.w3.org/2000/svg",
	children: [jsx("path", {
		fillRule: "evenodd",
		clipRule: "evenodd",
		d: "M15.8659 2.05975C17.2603 2.05995 18.3913 3.19096 18.3914 4.58527V5.4874C18.3914 6.02747 18.2192 6.52672 17.9303 6.93735C17.9336 6.96524 17.9388 6.99318 17.9388 7.02195V12.8884C17.9388 13.6345 17.9395 14.2379 17.8996 14.7254C17.8642 15.1593 17.7936 15.5499 17.6373 15.9141L17.5654 16.0685C17.278 16.6328 16.8405 17.1046 16.3038 17.434L16.0679 17.5661C15.66 17.7739 15.2196 17.8598 14.7237 17.9003C14.2362 17.9401 13.6327 17.9405 12.8867 17.9405H7.11122C6.36511 17.9405 5.76171 17.9401 5.27418 17.9003C4.84051 17.8649 4.44949 17.7952 4.08545 17.6391L3.93104 17.5661C3.36673 17.2785 2.89392 16.8414 2.56465 16.3044L2.43245 16.0685C2.22473 15.6608 2.13878 15.2211 2.09825 14.7254C2.05841 14.2379 2.05912 13.6345 2.05912 12.8884V7.02195C2.05912 6.99284 2.06422 6.96449 2.06758 6.93629C1.77931 6.52592 1.60858 6.02687 1.60858 5.4874V4.58527C1.60876 3.19084 2.73962 2.05975 4.1341 2.05975H15.8659ZM16.4984 7.92936C16.296 7.98169 16.0847 8.01288 15.8659 8.01291H4.1341C3.91478 8.01291 3.70246 7.98194 3.49955 7.92936V12.8884C3.49955 13.6582 3.50053 14.1927 3.53445 14.608C3.56769 15.0146 3.62923 15.244 3.71635 15.415L3.7925 15.5514C3.98339 15.8627 4.25749 16.1165 4.58464 16.2833L4.72529 16.3435C4.88095 16.3993 5.08638 16.4402 5.39158 16.4651C5.80685 16.4991 6.34138 16.5001 7.11122 16.5001H12.8867C13.6564 16.5001 14.1911 16.499 14.6063 16.4651C15.0128 16.432 15.2423 16.3703 15.4133 16.2833L15.5508 16.2061C15.8618 16.0152 16.116 15.7419 16.2827 15.415L16.3429 15.2732C16.3985 15.1177 16.4396 14.9128 16.4645 14.608C16.4985 14.1927 16.4984 13.6583 16.4984 12.8884V7.92936ZM4.1341 3.50019C3.53511 3.50019 3.0492 3.98631 3.04902 4.58527V5.4874C3.04902 6.08649 3.535 6.57248 4.1341 6.57248H15.8659C16.4648 6.57228 16.951 6.08638 16.951 5.4874V4.58527C16.9509 3.98644 16.4647 3.50038 15.8659 3.50019H4.1341Z",
		fill: "currentColor"
	}), jsx("path", {
		d: "M12.7962 12.5661V11.0832H7.20548V12.5661L12.7962 12.5661Z",
		fill: "currentColor"
	})]
});
//#endregion
//#region lib/types/DisclosureRow.js
/**
* Render one disclosure header and its controlled expanded content.
* @param props - Visual content, controlled state, and interaction policy.
* @returns the disclosure row.
*/
function DisclosureRow({ icon, title, open, expandable, onToggle, expandOnRowClick = false, previewChevron = expandable, keepContentWhenOpen = false, collapsedContent, children, className, rowClassName, leadingClassName, chevronClassName, titleClassName }) {
	const rowExpands = expandable && expandOnRowClick;
	const toggleFromLeading = (event) => {
		event.stopPropagation();
		onToggle();
	};
	const toggleFromKeyboard = (event) => {
		if (!rowExpands || event.key !== "Enter" && event.key !== " ") return;
		event.preventDefault();
		onToggle();
	};
	const collapsedLeading = previewChevron ? jsxs(Fragment, { children: [jsx("span", {
		className: css$1.iconIdle,
		children: icon
	}), jsx(IconChevronDownOutline14, { className: clsx(chevronClassName, css$1.chevronHover) })] }) : icon;
	const leading = open ? jsx(IconChevronDownOutline14, { className: chevronClassName }) : collapsedLeading;
	return jsxs("div", {
		className: clsx(css$1.root, className),
		"data-open": open || void 0,
		children: [jsxs("div", {
			className: clsx(css$1.row, rowClassName),
			"data-disclosure-row": true,
			"data-expandable": rowExpands || void 0,
			role: rowExpands ? "button" : void 0,
			tabIndex: rowExpands ? 0 : void 0,
			"aria-expanded": rowExpands ? open : void 0,
			onClick: rowExpands ? onToggle : void 0,
			onKeyDown: rowExpands ? toggleFromKeyboard : void 0,
			children: [
				expandable && !rowExpands ? jsx("button", {
					type: "button",
					className: clsx(css$1.leading, leadingClassName),
					"aria-expanded": open,
					onClick: toggleFromLeading,
					children: leading
				}) : jsx("span", {
					className: clsx(css$1.leading, leadingClassName),
					children: leading
				}),
				jsx("span", {
					className: clsx(css$1.title, titleClassName),
					children: title
				}),
				(keepContentWhenOpen || !open) && collapsedContent
			]
		}), open && children]
	});
}
//#endregion
//#region lib/types/Button.js
/**
* Render a button.
* @param props.variant - visual family (default 'ghost').
* @param props.size - 'md' 36px capsule (figma Button) or 'sm' 28px compact.
* @param props.icon - optional leading 16px icon node.
* @returns the button element; native button attributes pass through.
*/
function Button({ variant = "ghost", size = "md", icon, className, children, ...rest }) {
	return jsxs("button", {
		type: "button",
		className: clsx(css$2.button, css$2[variant], css$2[size], className),
		...rest,
		children: [icon != null && jsx("span", {
			className: css$2.icon,
			children: icon
		}), children]
	});
}
//#endregion
//#region lib/types/Pill.js
/**
* Render a pill chip. Interactive when onClick is supplied (renders a button);
* otherwise a static span.
* @param props.active - selected/active visual state.
* @returns pill element.
*/
function Pill({ active = false, className, children, onClick, ...rest }) {
	if (!onClick) return jsx("span", {
		className: clsx(css$3.pill, active && css$3.active, className),
		children
	});
	return jsx("button", {
		type: "button",
		className: clsx(css$3.pill, css$3.interactive, active && css$3.active, className),
		onClick,
		...rest,
		children
	});
}
//#endregion
//#region lib/types/Input.js
/**
* Render a text input with an optional leading icon.
* @param props.icon - optional 16px leading icon node.
* @returns wrapper span containing the native input; input attributes pass through.
*/
function Input({ icon, className, ...rest }) {
	return jsxs("span", {
		className: clsx(css$4.wrap, className),
		children: [icon != null && jsx("span", {
			className: css$4.icon,
			children: icon
		}), jsx("input", {
			className: css$4.input,
			...rest
		})]
	});
}
/**
* Delay a pointer-dismissed popup's close so the pointer can cross the gap
* between anchor and popup. A pending close is dropped on unmount.
* @param close - runs when the grace elapses with no re-entry; read at fire
* time, so callers may pass a fresh closure each render.
* @returns the {@link PointerGrace} handle.
*/
function usePointerGrace(close) {
	const timerRef = useRef(null);
	const closeRef = useRef(close);
	closeRef.current = close;
	const cancel = useCallback(() => {
		if (timerRef.current === null) return;
		clearTimeout(timerRef.current);
		timerRef.current = null;
	}, []);
	const arm = useCallback(() => {
		cancel();
		timerRef.current = setTimeout(() => {
			timerRef.current = null;
			closeRef.current();
		}, 200);
	}, [cancel]);
	useEffect(() => cancel, [cancel]);
	return {
		arm,
		cancel
	};
}
//#endregion
//#region lib/types/Menu.js
function isSeparator(entry) {
	return "type" in entry && entry.type === "separator";
}
function isLabel(entry) {
	return "type" in entry && entry.type === "label";
}
/** Unplaced portal list: hidden but laid out at a fixed origin so offsetWidth/offsetHeight are real. */
const MEASURE_STYLE = {
	visibility: "hidden",
	left: 0,
	top: 0
};
/**
* Render an anchored dropdown menu.
* @param props.open - whether the list is showing (owner-controlled).
* @param props.anchor - the trigger element (rendered in place).
* @param props.items - selectable rows and optional separators.
* @param props.selectedId - row shown as selected.
* @param props.selectedIds - rows shown as selected when a menu contains independent option groups.
* @param props.onSelect - row click callback (not called for disabled rows or submenu parents that only open children).
* @param props.onClose - invoked on outside click or Escape.
* @param props.align - list alignment against the anchor (default 'start').
* @param props.side - open below (`bottom`, default) or above (`top`) the anchor.
* @param props.portal - render the list into document.body, fixed-positioned
* from the anchor rect (repositions on scroll/resize while open). Use when an
* ancestor's overflow clipping would crop the in-place list; default false
* keeps the pure-CSS in-place behavior.
* @param props.closeOnPointerLeave - close the list once the pointer has left
* both trigger and list for the pointer grace (default false keeps it open
* until outside click/Escape/selection). The grace makes the 4px trigger->list
* gap and a brief overshoot survivable; coming back cancels the close.
* @param props.dense - reduce vertical row spacing without changing the standard typography or card width.
* @param props.compact - use reduced menu typography and spacing.
* @param props.getAnchorRect - portal mode only: supply the anchor rect
* directly (e.g. from a host-owned trigger button) instead of measuring the
* Menu's own wrapper span. Required when the wrapper isn't itself laid out at
* the trigger (render-prop anchors, effect-positioned proxies — measuring the
* wrapper there races the host's layout effects). Called on open and on every
* scroll/resize; return null to skip placement for that frame.
* @param props.footer - rows pinned below the scrolling items area, separated
* by a hairline; they stay visible while the items above scroll.
* @returns anchor wrapper with the conditional list.
*/
function Menu({ open, anchor, items, selectedId, selectedIds, onSelect, onClose, align = "start", side = "bottom", portal = false, closeOnPointerLeave = false, dense = false, compact = false, getAnchorRect, footer, className }) {
	const rootRef = useRef(null);
	const listRef = useRef(null);
	const [openSubmenuId, setOpenSubmenuId] = useState(null);
	const [fixedPos, setFixedPos] = useState(null);
	const { arm: armClose, cancel: cancelClose } = usePointerGrace(onClose);
	useLayoutEffect(() => {
		if (!open || !portal) {
			setFixedPos(null);
			return;
		}
		const place = () => {
			let r;
			if (getAnchorRect !== void 0) r = getAnchorRect();
			else
 /* v8 ignore next 2 -- the ref is attached before the layout effect runs and the listeners die with it. */
			r = rootRef.current?.getBoundingClientRect() ?? null;
			if (r === null) return;
			const MARGIN = 12;
			const vw = window.innerWidth;
			const vh = window.innerHeight;
			const listEl = listRef.current;
			const lw = listEl?.offsetWidth ?? 0;
			const lh = listEl?.offsetHeight ?? 0;
			let x;
			let y;
			if (side === "right") {
				x = r.right + 4;
				y = r.top;
			} else if (align === "start") {
				x = r.left;
				y = side === "bottom" ? r.bottom + 4 : r.top - lh - 4;
			} else {
				x = r.right - lw;
				y = side === "bottom" ? r.bottom + 4 : r.top - lh - 4;
			}
			if (lw > 0) x = Math.min(Math.max(x, MARGIN), vw - lw - MARGIN);
			if (lh > 0) y = Math.min(Math.max(y, MARGIN), vh - lh - MARGIN);
			setFixedPos({
				left: x,
				top: y
			});
		};
		place();
		window.addEventListener("scroll", place, true);
		window.addEventListener("resize", place);
		return () => {
			window.removeEventListener("scroll", place, true);
			window.removeEventListener("resize", place);
		};
	}, [
		open,
		portal,
		align,
		side,
		getAnchorRect
	]);
	useEffect(() => {
		if (!open) {
			setOpenSubmenuId(null);
			return;
		}
		const onPointerDown = (e) => {
			if (!(e.target instanceof Node)) return;
			if (rootRef.current?.contains(e.target) === true) return;
			if (listRef.current?.contains(e.target) === true) return;
			onClose();
		};
		const onKeyDown = (e) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("pointerdown", onPointerDown);
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("pointerdown", onPointerDown);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [open, onClose]);
	useEffect(() => {
		if (!open) cancelClose();
	}, [open, cancelClose]);
	const scrollable = !items.some((entry) => !isSeparator(entry) && !isLabel(entry) && entry.submenu !== void 0 && entry.submenu.length > 0);
	const renderEntry = (entry) => {
		if (isSeparator(entry)) return jsx("div", {
			className: css$5.separator,
			role: "separator"
		}, entry.id);
		if (isLabel(entry)) return jsx("div", {
			className: css$5.label,
			role: "presentation",
			children: entry.text
		}, entry.id);
		const hasSub = entry.submenu !== void 0 && entry.submenu.length > 0;
		const subOpen = hasSub && openSubmenuId === entry.id;
		const selected = entry.id === selectedId || selectedIds?.includes(entry.id) === true;
		return jsxs("div", {
			className: css$5.itemWrap,
			onMouseEnter: () => {
				setOpenSubmenuId(hasSub ? entry.id : null);
			},
			onMouseLeave: () => {
				setOpenSubmenuId(null);
			},
			children: [jsxs("button", {
				type: "button",
				role: "menuitem",
				className: clsx(css$5.item, selected && css$5.selected, entry.danger === true && css$5.danger),
				disabled: entry.disabled,
				"aria-haspopup": hasSub ? "menu" : void 0,
				"aria-expanded": hasSub ? subOpen : void 0,
				onFocus: () => {
					setOpenSubmenuId(hasSub ? entry.id : null);
				},
				onClick: () => {
					if (hasSub) {
						setOpenSubmenuId(entry.id);
						return;
					}
					onSelect(entry.id);
				},
				children: [
					entry.icon !== void 0 && jsx("span", {
						className: css$5.itemIcon,
						children: entry.icon
					}),
					jsx("span", {
						className: css$5.itemLabel,
						children: entry.label
					}),
					selected && jsx(IconCheckOutline16, { className: css$5.check })
				]
			}), subOpen && entry.submenu !== void 0 && jsx("div", {
				className: clsx(css$5.submenu, compact && css$5.compactList),
				role: "menu",
				children: entry.submenu.map((sub) => jsxs("button", {
					type: "button",
					role: "menuitem",
					className: css$5.item,
					disabled: sub.disabled,
					onClick: () => {
						onSelect(sub.id);
					},
					children: [sub.icon !== void 0 && jsx("span", {
						className: css$5.itemIcon,
						children: sub.icon
					}), jsx("span", {
						className: css$5.itemLabel,
						children: sub.label
					})]
				}, sub.id))
			})]
		}, entry.id);
	};
	const list = open && jsxs("div", {
		ref: listRef,
		className: clsx(css$5.list, dense && css$5.denseList, compact && css$5.compactList, scrollable && css$5.scrollable, portal && css$5.portal, side === "top" && !portal && css$5.sideTop, align === "end" && !portal && css$5.alignEnd),
		style: portal ? fixedPos ?? MEASURE_STYLE : void 0,
		role: "menu",
		onClick: (e) => {
			e.stopPropagation();
		},
		children: [jsx("div", {
			className: css$5.viewport,
			role: "presentation",
			children: items.map(renderEntry)
		}), footer !== void 0 && footer.length > 0 && jsx("div", {
			className: css$5.footer,
			role: "presentation",
			children: footer.map(renderEntry)
		})]
	});
	return jsxs("span", {
		ref: rootRef,
		className: clsx(css$5.root, className),
		onPointerEnter: closeOnPointerLeave ? cancelClose : void 0,
		onPointerLeave: closeOnPointerLeave ? () => {
			if (open) armClose();
		} : void 0,
		children: [anchor, portal ? list !== false && createPortal(list, document.body) : list]
	});
}
//#endregion
//#region lib/types/useAnchoredMaxHeight.js
/**
* Viewport-fit hook for bottom-anchored overlays (slash menu, popupSelect):
* the element's bottom edge is laid out independent of its height, so it
* grows upward and only the top edge can collide with the viewport — clamp
* the design cap to the space between that edge and the viewport top.
*/
/** Safe distance kept between the overlay and the viewport top edge (mirrors the Menu portal margin). */
const MARGIN = 12;
/**
* Clamp a bottom-anchored overlay's max-height to the viewport.
* @param ref - the overlay element; a null current (overlay closed) skips measuring.
* @param cap - design max-height in px (the clamp never exceeds it).
* @param signal - re-measure trigger: pass the overlay's render state so anchor
*   moves (composer growth) re-fit; resize/scroll re-fit while mounted.
* @returns the max-height to apply inline, in px.
*/
function useAnchoredMaxHeight(ref, cap, signal) {
	const [maxHeight, setMaxHeight] = useState(cap);
	useLayoutEffect(() => {
		const el = ref.current;
		if (el === null) return;
		const fit = () => {
			setMaxHeight(Math.min(cap, Math.max(0, el.getBoundingClientRect().bottom - MARGIN)));
		};
		fit();
		window.addEventListener("resize", fit);
		window.addEventListener("scroll", fit, true);
		return () => {
			window.removeEventListener("resize", fit);
			window.removeEventListener("scroll", fit, true);
		};
	}, [
		ref,
		cap,
		signal
	]);
	return maxHeight;
}
//#endregion
//#region lib/types/useAnchoredPosition.js
/**
* Keep a fixed-position floating element anchored to a trigger.
*
* A portaled panel is positioned from its anchor's viewport rect, which stops
* being true the moment anything scrolls or the window resizes. This owns that
* one concern: measure the anchor, offset the panel below it, clamp the result
* inside the viewport, and re-run on scroll (capture phase, so scrollers nested
* inside the page are caught too), on resize, and on the panel's own size
* changes while the element is open.
* @module @deepseek-ai/dsh-client-ui-primitives/useAnchoredPosition
*/
/**
* Track an anchor and return the panel's fixed coordinates.
* @param options - the open state, the two refs, and the gap/margin distances.
* @returns `left`/`top` for the panel, or `null` before the first measurement.
*/
function useAnchoredPosition(options) {
	const { open, anchorRef, panelRef, gap, margin } = options;
	const [position, setPosition] = useState(null);
	useLayoutEffect(() => {
		if (!open) {
			setPosition(null);
			return;
		}
		const place = () => {
			/* v8 ignore start -- geometry read from real layout: jsdom reports zero
			offset sizes, so the positive-size clamp arms are exercised by browser
			scenarios rather than unit tests. */
			const rect = anchorRef.current?.getBoundingClientRect();
			if (rect === void 0) return;
			const panel = panelRef.current;
			const width = panel?.offsetWidth ?? 0;
			const height = panel?.offsetHeight ?? 0;
			let left = rect.left;
			let top = rect.bottom + gap;
			if (width > 0) left = Math.min(Math.max(left, margin), window.innerWidth - width - margin);
			if (height > 0) top = Math.min(Math.max(top, margin), window.innerHeight - height - margin);
			/* v8 ignore stop */
			setPosition({
				left,
				top
			});
		};
		place();
		window.addEventListener("scroll", place, true);
		window.addEventListener("resize", place);
		const panel = panelRef.current;
		let observer = null;
		if (typeof ResizeObserver !== "undefined" && panel !== null) {
			observer = new ResizeObserver(place);
			observer.observe(panel);
		}
		return () => {
			observer?.disconnect();
			window.removeEventListener("scroll", place, true);
			window.removeEventListener("resize", place);
		};
	}, [
		open,
		anchorRef,
		panelRef,
		gap,
		margin
	]);
	return position;
}
//#endregion
//#region lib/types/useDismissOnOutsidePointer.js
/**
* Outside-pointer dismissal for trigger-owned popovers (jobs list, Cordis
* panel): while the surface is open, a pointerdown outside the root closes it.
*/
/**
* Close an open popover when a pointerdown lands outside its root element.
* @param root - element containing both the trigger and the open surface.
* @param open - whether the surface is showing; false detaches the listener.
* @param setOpen - state setter invoked with false on an outside pointerdown.
*/
function useDismissOnOutsidePointer(root, open, setOpen) {
	useEffect(() => {
		if (!open) return;
		const closeOutside = (event) => {
			if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
		};
		document.addEventListener("pointerdown", closeOutside);
		return () => {
			document.removeEventListener("pointerdown", closeOutside);
		};
	}, [
		root,
		open,
		setOpen
	]);
}
//#endregion
//#region lib/types/clipboard.js
/**
* Write text to the host clipboard, preferring the async Clipboard API and
* falling back to `execCommand('copy')` on hosts (jsdom, insecure contexts)
* that omit it.
* @param text - the exact text to place on the clipboard.
* @returns true only when the host accepted the write.
*/
async function writeClipboard(text) {
	if (navigator.clipboard?.writeText) try {
		await navigator.clipboard.writeText(text);
		return true;
	} catch {
		return false;
	}
	const exec = typeof document.execCommand === "function" ? document.execCommand.bind(document) : void 0;
	if (exec === void 0) return false;
	const el = document.createElement("textarea");
	el.value = text;
	el.setAttribute("readonly", "");
	el.style.position = "fixed";
	el.style.left = "-9999px";
	document.body.appendChild(el);
	el.select();
	try {
		return exec("copy");
	} catch {
		return false;
	} finally {
		el.remove();
	}
}
//#endregion
//#region lib/types/HoverCard.js
/**
* Render an anchor with a hover-triggered preview card.
* @param props.anchor - the hover target (rendered in place inside a wrapper span).
* @param props.content - card content; the pointer may rest on it, so it is
* readable and selectable, but it carries no dismissal affordance of its own.
* @param props.openDelayMs - hover dwell before the card shows (default 500).
* @param props.disabled - suppress opening; turning true closes an open card.
* @param props.copyText - optional primary value copied by activation and
* included in the card's accessible name.
* @param props.copyLabel - accessible activation-label prefix (default "复制").
* @param props.copiedLabel - visible success label (default "复制成功").
* @returns anchor wrapper with the conditional portaled card.
*/
function HoverCard({ anchor, content, openDelayMs = 500, disabled = false, copyText, copyLabel = "复制", copiedLabel = "复制成功" }) {
	const rootRef = useRef(null);
	const cardRef = useRef(null);
	const timerRef = useRef(null);
	const copyTimerRef = useRef(null);
	const copyHeightRef = useRef(null);
	const copyEpochRef = useRef(0);
	const copyingRef = useRef(false);
	const mountedRef = useRef(true);
	const [open, setOpen] = useState(false);
	const [pos, setPos] = useState(null);
	const [copied, setCopied] = useState(false);
	const clearCopied = useCallback(() => {
		if (copyTimerRef.current !== null) {
			clearTimeout(copyTimerRef.current);
			copyTimerRef.current = null;
		}
		copyHeightRef.current = null;
		setCopied(false);
	}, []);
	const close = useCallback(() => {
		copyEpochRef.current += 1;
		clearCopied();
		setOpen(false);
	}, [clearCopied]);
	const { arm: armClose, cancel: cancelClose } = usePointerGrace(close);
	const clearTimer = () => {
		if (timerRef.current !== null) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	};
	useEffect(() => {
		if (!disabled) return;
		clearTimer();
		cancelClose();
		close();
	}, [
		disabled,
		cancelClose,
		close
	]);
	useEffect(() => {
		const surVisibilite = () => {
			if (document.visibilityState === "hidden") close();
		};
		const surTouche = (e) => {
			if (e.key === "Escape") close();
		};
		document.addEventListener("visibilitychange", surVisibilite);
		document.addEventListener("keydown", surTouche);
		window.addEventListener("blur", close);
		return () => {
			document.removeEventListener("visibilitychange", surVisibilite);
			document.removeEventListener("keydown", surTouche);
			window.removeEventListener("blur", close);
		};
	}, [close]);
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			copyEpochRef.current += 1;
			clearTimer();
			if (copyTimerRef.current !== null) {
				clearTimeout(copyTimerRef.current);
				copyTimerRef.current = null;
			}
		};
	}, []);
	useLayoutEffect(() => {
		if (!open) {
			setPos(null);
			return;
		}
		const place = () => {
			const wrapper = rootRef.current;
			/* v8 ignore next -- the ref is attached before the layout effect runs and the listeners die with it. */
			if (wrapper === null) return;
			const r = wrapper.getBoundingClientRect();
			const h = cardRef.current?.offsetHeight ?? 0;
			const top = r.top + h > window.innerHeight - 8 ? window.innerHeight - h - 8 : r.top;
			setPos({
				left: r.right + 8,
				top
			});
		};
		place();
		window.addEventListener("scroll", place, true);
		window.addEventListener("resize", place);
		return () => {
			window.removeEventListener("scroll", place, true);
			window.removeEventListener("resize", place);
		};
	}, [open]);
	useLayoutEffect(() => {
		if (!open || pos === null) return;
		/* v8 ignore next -- the card is mounted whenever pos is set, so the ref is attached here. */
		const h = cardRef.current?.offsetHeight ?? 0;
		if (pos.top + h > window.innerHeight - 8) setPos({
			left: pos.left,
			top: window.innerHeight - h - 8
		});
	}, [open, pos]);
	const copy = async (text) => {
		if (copied || copyingRef.current) return;
		copyingRef.current = true;
		const copyEpoch = copyEpochRef.current;
		const accepted = await writeClipboard(text);
		copyingRef.current = false;
		const card = cardRef.current;
		if (!accepted || !mountedRef.current || copyEpoch !== copyEpochRef.current || card === null) return;
		const height = card.offsetHeight;
		copyHeightRef.current = height > 0 ? height : null;
		setCopied(true);
		copyTimerRef.current = setTimeout(clearCopied, 1e3);
	};
	const copyable = copyText !== void 0;
	const card = open && pos !== null && jsx("div", {
		ref: cardRef,
		className: `${css$6.card}${copyable ? ` ${css$6.copyable}` : ""}${copied ? ` ${css$6.feedback}` : ""}`,
		style: {
			...pos,
			minHeight: copied && copyHeightRef.current !== null ? copyHeightRef.current : void 0
		},
		role: copyable ? "button" : void 0,
		tabIndex: copyable ? 0 : void 0,
		"aria-label": copyable ? `${copyLabel}: ${copyText}` : void 0,
		onClick: copyable ? (e) => {
			const selection = window.getSelection();
			if (selection !== null && !selection.isCollapsed) {
				for (let i = 0; i < selection.rangeCount; i += 1) if (selection.getRangeAt(i).intersectsNode(e.currentTarget)) return;
			}
			copy(copyText);
		} : void 0,
		onKeyDown: copyable ? (e) => {
			if (e.key !== "Enter" && e.key !== " ") return;
			e.preventDefault();
			copy(copyText);
		} : void 0,
		children: copied ? jsx("span", {
			className: css$6.copied,
			"aria-hidden": "true",
			children: copiedLabel
		}) : content
	});
	return jsxs("span", {
		ref: rootRef,
		className: css$6.root,
		onPointerEnter: () => {
			if (disabled) return;
			cancelClose();
			if (open) return;
			clearTimer();
			timerRef.current = setTimeout(() => {
				setOpen(true);
			}, openDelayMs);
		},
		onPointerLeave: () => {
			clearTimer();
			if (open) armClose();
		},
		onPointerDownCapture: (e) => {
			if (cardRef.current?.contains(e.target)) return;
			clearTimer();
			cancelClose();
			close();
		},
		children: [
			anchor,
			open && copyable && jsx("span", {
				className: css$6.status,
				role: "status",
				children: copied ? copiedLabel : ""
			}),
			card !== false && createPortal(card, document.body)
		]
	});
}
//#endregion
//#region lib/types/Modal.js
/**
* Render a centered modal over a blurred page mask.
* @param props.open - whether the dialog is showing.
* @param props.onClose - Escape or mask click.
* @param props.title - dialog heading (aria-label in every mode).
* @param props.closeLabel - accessible close-button label.
* @param props.description - optional supporting sentence under the title.
* @param props.children - body (inputs, etc.).
* @param props.footer - action row (Cancel / Create).
* @param props.contentClassName - optional class for a scrollable content region.
* @param props.headless - render children directly in the card (no default
* header/close/body chrome) for dialogs whose figma frame owns its own
* header structure; mask, card, Escape, and aria-label remain.
* @param props.closeLabel - close-button aria label; the owner passes
* localized copy (this package is cordis-free, so copy arrives via props).
* @returns null when closed; otherwise the overlay tree.
*/
function Modal({ open, onClose, title, closeLabel = "Close", description, children, footer, className, contentClassName, headless = false }) {
	useEffect(() => {
		if (!open) return;
		const onKeyDown = (e) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [open, onClose]);
	if (!open) return null;
	return createPortal(jsxs("div", {
		className: css$7.root,
		role: "presentation",
		children: [jsx("div", {
			className: css$7.mask,
			"aria-hidden": "true",
			onClick: onClose
		}), jsx("div", {
			className: clsx(css$7.dialog, className),
			role: "dialog",
			"aria-modal": "true",
			"aria-label": title,
			children: headless ? children : jsxs(Fragment, { children: [jsxs("div", {
				className: clsx(css$7.content, contentClassName),
				children: [
					jsxs("div", {
						className: css$7.header,
						children: [jsx("h2", {
							className: css$7.title,
							children: title
						}), jsx("button", {
							type: "button",
							className: css$7.close,
							"aria-label": closeLabel,
							onClick: onClose,
							children: jsx(IconCloseOutline16, { size: 14 })
						})]
					}),
					description !== void 0 && description !== "" && jsx("p", {
						className: css$7.description,
						children: description
					}),
					children !== void 0 && jsx("div", {
						className: css$7.body,
						children
					})
				]
			}), footer !== void 0 && jsx("div", {
				className: css$7.footer,
				children: footer
			})] })
		})]
	}), document.body);
}
//#endregion
//#region lib/types/OnboardingSurface.js
/**
* Render the onboarding takeover chrome (mask + opaque stage) around one
* step's content and keep the application root inert while mounted.
* @param props.children - the step's page content, centered on the stage.
* @returns the body-portaled overlay tree.
*/
function OnboardingSurface({ children }) {
	useEffect(() => {
		const appRoot = document.getElementById("root");
		if (appRoot === null) return;
		appRoot.inert = true;
		return () => {
			appRoot.inert = false;
		};
	}, []);
	return createPortal(jsxs("div", {
		className: css$8.onboardingOverlay,
		role: "presentation",
		children: [jsx("div", {
			className: css$8.onboardingMask,
			"aria-hidden": "true"
		}), jsx("div", {
			className: css$8.onboardingStage,
			children
		})]
	}), document.body);
}
//#endregion
//#region lib/types/RiskConfirmation.js
/**
* Controlled risk acknowledgement dialog shared by product surfaces that
* must gate a sensitive action behind an explicit checkbox.
*/
/**
* Render one in-page confirmation whose primary action is unavailable until
* the caller-controlled acknowledgement is checked.
*/
function RiskConfirmation({ open, title, description, acknowledgeLabel, cancelLabel, confirmLabel, acknowledged, disabled = false, onAcknowledgedChange, onCancel, onConfirm }) {
	return jsxs(Modal, {
		open,
		onClose: onCancel,
		title,
		className: css$9.confirmation ?? "",
		contentClassName: css$9.confirmationContent ?? "",
		footer: jsxs(Fragment, { children: [jsx(Button, {
			variant: "outline",
			className: css$9.modalAction,
			onClick: onCancel,
			children: cancelLabel
		}), jsx(Button, {
			variant: "primary",
			className: css$9.confirmAction,
			disabled: disabled || !acknowledged,
			onClick: onConfirm,
			children: confirmLabel
		})] }),
		children: [jsxs("div", {
			className: css$9.warning,
			children: [jsx(IconWarningOutline16, {
				size: 18,
				className: css$9.warningIcon
			}), jsx("p", { children: description })]
		}), jsxs("label", {
			className: css$9.acknowledgement,
			children: [jsx("input", {
				type: "checkbox",
				checked: acknowledged,
				disabled,
				autoFocus: true,
				onChange: (event) => {
					onAcknowledgedChange(event.currentTarget.checked);
				}
			}), jsx("span", { children: acknowledgeLabel })]
		})]
	});
}
//#endregion
//#region lib/types/ConnectionBanner.js
/**
* Render the reconnecting banner.
* @param props.reconnecting - true while the connection is in backoff/retry.
* @param props.label - banner text; the owner passes localized copy (this
* package is cordis-free, so copy arrives via props).
* @returns the banner, or null when connected.
*/
function ConnectionBanner({ reconnecting, label = "连接已断开，正在重连…" }) {
	if (!reconnecting) return null;
	return jsx("div", {
		className: css$10.banner,
		children: label
	});
}
//#endregion
//#region lib/types/FishLogo.js
/**
* Render the fish logo.
* @param props.size - width in px (default 24; height keeps the 23.16:17.04 ratio).
* @param props.className - extra class for layout placement.
* @returns the logo svg (aria-hidden; pair with the wordmark for accessibility).
*/
function FishLogo({ size = 24, className }) {
	return jsx("svg", {
		width: size,
		height: size * 17.04 / 23.16,
		className,
		viewBox: "0 0 23.16 17.04",
		fill: "none",
		"aria-hidden": "true",
		children: jsx("path", {
			d: "M22.9168 1.43018C22.6713 1.31018 22.5658 1.53918 22.4223 1.65519C22.3733 1.69269 22.3318 1.74169 22.2903 1.78669C21.9317 2.1697 21.5127 2.42121 20.9657 2.39121C20.1657 2.34621 19.4827 2.59771 18.8787 3.20973C18.7502 2.45521 18.3236 2.0047 17.6746 1.71569C17.3351 1.56568 16.9916 1.41518 16.7536 1.08867C16.5876 0.856163 16.5421 0.597155 16.4591 0.341647C16.4061 0.187643 16.3536 0.0301382 16.1761 0.00363739C15.9836 -0.0263635 15.9081 0.135141 15.8326 0.270145C15.5306 0.822162 15.4136 1.43018 15.4251 2.0462C15.4516 3.43174 16.0366 4.53527 17.1991 5.3203C17.3311 5.4103 17.3651 5.5003 17.3236 5.63181C17.2441 5.90231 17.1501 6.16482 17.0671 6.43533C17.0141 6.60784 16.9351 6.64584 16.7501 6.57033C16.1121 6.30383 15.5611 5.90931 15.074 5.4328C14.2475 4.63328 13.5 3.75075 12.568 3.05973C12.349 2.89822 12.13 2.74822 11.9034 2.60522C10.9524 1.68169 12.028 0.923165 12.277 0.833162C12.5375 0.739159 12.3675 0.41615 11.5259 0.42015C10.6844 0.42365 9.91439 0.705658 8.93286 1.08117C8.78935 1.13767 8.63835 1.17867 8.48384 1.21267C7.59332 1.04367 6.66829 1.00617 5.70226 1.11517C3.88321 1.31768 2.43016 2.1777 1.36213 3.64575C0.0790928 5.4103 -0.222916 7.41536 0.146595 9.50642C0.535106 11.7105 1.66014 13.535 3.38869 14.9616C5.18125 16.4406 7.24581 17.1657 9.60138 17.0266C11.0319 16.9441 12.6245 16.7526 14.421 15.2321C14.874 15.4576 15.3496 15.5476 16.1381 15.6151C16.7456 15.6716 17.3306 15.5851 17.7836 15.4911C18.4931 15.3411 18.4441 14.6841 18.1876 14.5636C16.1081 13.595 16.5646 13.9891 16.1496 13.67C17.2061 12.42 18.8202 10.1979 19.3182 7.17235C19.3672 6.83834 19.4297 6.36783 19.4222 6.09732C19.4182 5.93231 19.4562 5.86831 19.6447 5.84931C20.1657 5.78931 20.6712 5.64681 21.1357 5.3913C22.4833 4.65528 23.0268 3.44624 23.1548 1.9972C23.1738 1.77569 23.1508 1.54668 22.9168 1.43018ZM11.1749 14.4736C9.15936 12.889 8.18184 12.3675 7.77832 12.39C7.40081 12.4125 7.46881 12.8445 7.55182 13.126C7.63882 13.404 7.75182 13.5955 7.91033 13.8396C8.01983 14.0011 8.09533 14.2411 7.80083 14.4216C7.15181 14.8231 6.02327 14.2866 5.97027 14.2601C4.65673 13.4865 3.5587 12.4655 2.78467 11.069C2.03715 9.72493 1.60314 8.28289 1.53164 6.74384C1.51264 6.37233 1.62214 6.24082 1.99215 6.17332C2.47916 6.08332 2.98118 6.06432 3.46769 6.13582C5.52476 6.43633 7.27581 7.35586 8.74385 8.8129C9.58188 9.64243 10.2159 10.634 10.8689 11.6025C11.5634 12.631 12.3105 13.611 13.262 14.4146C13.598 14.6961 13.866 14.9101 14.1225 15.0681C13.349 15.1546 12.058 15.1731 11.1749 14.4746L11.1749 14.4736ZM12.141 8.25988C12.141 8.09488 12.273 7.96338 12.439 7.96338C12.4765 7.96338 12.5105 7.97088 12.541 7.98188C12.5825 7.99688 12.6205 8.01938 12.6505 8.05338C12.7035 8.10588 12.7335 8.18088 12.7335 8.25988C12.7335 8.42489 12.6015 8.55639 12.4355 8.55639C12.2695 8.55639 12.141 8.42489 12.141 8.25988ZM15.1415 9.79893C14.949 9.87793 14.7565 9.94544 14.5715 9.95294C14.2845 9.96794 13.9715 9.85143 13.8015 9.70893C13.5375 9.48742 13.3485 9.36342 13.2695 8.97691C13.2355 8.8119 13.2545 8.55639 13.2845 8.40989C13.3525 8.09438 13.277 7.89187 13.0545 7.70787C12.8735 7.55786 12.643 7.51636 12.39 7.51636C12.2955 7.51636 12.209 7.47486 12.1445 7.44136C12.039 7.38886 11.9519 7.25735 12.035 7.09585C12.0615 7.04335 12.19 6.91584 12.22 6.89334C12.5635 6.69784 12.9595 6.76184 13.326 6.90834C13.6655 7.04735 13.9225 7.30236 14.292 7.66287C14.6695 8.09838 14.7375 8.21838 14.9525 8.54539C15.1225 8.8009 15.277 9.06341 15.3831 9.36392C15.4471 9.55142 15.3641 9.70493 15.1415 9.79893Z",
			fill: "currentColor"
		})
	});
}
//#endregion
//#region lib/types/BrandWordmark.js
/**
* Render the full brand wordmark.
* @param props.size - height in px (default 24; width follows the selected artwork).
* @param props.className - extra class for layout placement.
* @param props.includeMark - whether to include the leading whale mark.
* @returns the wordmark svg (aria-hidden decorative brand art).
*/
function BrandWordmark({ size = 24, className, includeMark = true }) {
	return jsxs("svg", {
		width: size * (includeMark ? 182 : 156) / 24,
		height: size,
		className,
		viewBox: includeMark ? "0 0 182 24" : "26 0 156 24",
		fill: "none",
		"aria-hidden": "true",
		children: [
			jsx("path", {
				d: "M68.416 18.2447H67.0501V16.1272H68.416C69.2619 16.1272 70.1166 15.9163 70.6671 15.3304C71.2181 14.7444 71.426 13.8455 71.426 12.9471C71.426 12.0487 71.2268 11.1498 70.6671 10.5643C70.1083 9.97831 69.2619 9.76744 68.416 9.76744C67.5701 9.76744 66.7154 9.97831 66.1639 10.5643C65.6129 11.1503 65.4049 12.0487 65.4049 12.9471V21.6435H63.009V7.6582H65.4049V8.54883H65.8442C65.8918 8.49393 65.9394 8.44728 65.9875 8.40064C66.5871 7.85353 67.5049 7.6582 68.4072 7.6582C69.8212 7.6582 71.2341 8.00998 72.1607 8.98662C73.0868 9.96325 73.4143 11.4632 73.4143 12.9558C73.4143 14.4485 73.0785 15.9406 72.1607 16.925C71.2424 17.9094 69.8212 18.2457 68.416 18.2457V18.2447Z",
				fill: "currentColor"
			}),
			jsx("path", {
				d: "M31.9551 8.03497H33.3204V10.1525H31.9551C31.1087 10.1525 30.2545 10.3633 29.7035 10.9493C29.1525 11.5353 28.945 12.4342 28.945 13.3326C28.945 14.231 29.1447 15.1294 29.7035 15.7154C30.2623 16.3014 31.1087 16.5122 31.9551 16.5122C32.8015 16.5122 33.6562 16.3014 34.2072 15.7154C34.7582 15.1294 34.9657 14.231 34.9657 13.3326V4.62842H37.3611V18.6219H34.9657V17.7313H34.5264C34.4783 17.7857 34.4307 17.8329 34.3826 17.8795C33.7835 18.4261 32.8652 18.6219 31.9629 18.6219C30.5494 18.6219 29.136 18.2707 28.2099 17.294C27.2838 16.3174 26.9563 14.817 26.9563 13.3248C26.9563 11.8327 27.2916 10.34 28.2099 9.35561C29.136 8.37898 30.5494 8.03497 31.9551 8.03497Z",
				fill: "currentColor"
			}),
			jsx("path", {
				d: "M49.3786 13.1431V13.9948H42.9984V12.2996H47.2305C47.1348 11.6825 46.9113 11.1043 46.5119 10.682C45.9371 10.0727 45.0503 9.85409 44.1723 9.85409C43.2943 9.85409 42.4076 10.0727 41.8328 10.682C41.258 11.2913 41.05 12.2213 41.05 13.1435C41.05 14.0658 41.2575 15.003 41.8328 15.6046C42.4076 16.2061 43.2939 16.433 44.1723 16.433C45.0508 16.433 45.9371 16.2143 46.5119 15.6046C46.5916 15.5186 46.6635 15.4248 46.7354 15.331H49.0992C48.8918 16.0657 48.5643 16.7299 48.0691 17.2454C47.111 18.2531 45.6339 18.6205 44.1723 18.6205C42.7108 18.6205 41.2337 18.2609 40.2755 17.2454C39.3174 16.2299 38.9661 14.6828 38.9661 13.1435C38.9661 11.6043 39.3096 10.0494 40.2755 9.04168C41.242 8.03396 42.7108 7.66663 44.1723 7.66663C45.6339 7.66663 47.111 8.02618 48.0691 9.04168C49.0351 10.0572 49.3786 11.6043 49.3786 13.1435V13.1431Z",
				fill: "currentColor"
			}),
			jsx("path", {
				d: "M61.4045 13.1431V13.9948H55.0243V12.2996H59.2564C59.1602 11.6825 58.9372 11.1043 58.5378 10.682C57.963 10.0727 57.0762 9.85409 56.1982 9.85409C55.3202 9.85409 54.4335 10.0727 53.8587 10.682C53.2839 11.2913 53.0759 12.2213 53.0759 13.1435C53.0759 14.0658 53.2834 15.003 53.8587 15.6046C54.4335 16.2061 55.3202 16.433 56.1982 16.433C57.0762 16.433 57.963 16.2143 58.5378 15.6046C58.6179 15.5186 58.6894 15.4248 58.7608 15.331H61.1251C60.9171 16.0657 60.5897 16.7299 60.0945 17.2454C59.1364 18.2531 57.6593 18.6205 56.1982 18.6205C54.7372 18.6205 53.2596 18.2609 52.3014 17.2454C51.3432 16.2299 50.9919 14.6828 50.9919 13.1435C50.9919 11.6043 51.3355 10.0494 52.3014 9.04168C53.2678 8.03396 54.7367 7.66663 56.1982 7.66663C57.6598 7.66663 59.1364 8.02618 60.0945 9.04168C61.061 10.0572 61.4045 11.6043 61.4045 13.1435V13.1431Z",
				fill: "currentColor"
			}),
			jsx("path", {
				d: "M80.242 18.6214C81.7035 18.6214 83.1801 18.4105 84.1383 17.809C85.0965 17.2075 85.4482 16.2931 85.4482 15.3869C85.4482 14.4807 85.1042 13.5585 84.1383 12.9647C83.1801 12.371 81.703 12.1518 80.242 12.1518C79.6186 12.1518 79.0438 12.0658 78.6366 11.8394C78.2294 11.6047 78.0778 11.2534 78.0778 10.9017C78.0778 10.5499 78.2216 10.1908 78.6366 9.9639C79.0438 9.72921 79.6749 9.65147 80.2973 9.65147C80.9198 9.65147 81.5509 9.73747 81.9591 9.9639C82.3663 10.1986 82.5179 10.5499 82.5179 10.9017H84.9531C84.9531 9.99499 84.6421 9.07327 83.7719 8.47951C82.9017 7.88576 81.5679 7.66663 80.2424 7.66663C78.9169 7.66663 77.5837 7.8775 76.713 8.47951C75.8427 9.08104 75.5308 9.99499 75.5308 10.9017C75.5308 11.8083 75.8423 12.73 76.713 13.3238C77.5832 13.9176 78.9165 14.1367 80.2424 14.1367C80.929 14.1367 81.688 14.2227 82.1428 14.4491C82.5985 14.676 82.7579 15.0351 82.7579 15.3869C82.7579 15.7387 82.5985 16.0977 82.1428 16.3246C81.688 16.5511 80.9931 16.6371 80.3066 16.6371C79.62 16.6371 78.9169 16.5511 78.4694 16.3246C78.0224 16.0982 77.8543 15.7387 77.8543 15.3869H75.0435C75.0435 16.2935 75.3865 17.2153 76.3534 17.809C77.3194 18.4028 78.7809 18.6214 80.2424 18.6214H80.242Z",
				fill: "currentColor"
			}),
			jsx("path", {
				d: "M97.4733 13.1431V13.9948H91.0932V12.2996H95.3252C95.23 11.6825 95.006 11.1043 94.6071 10.682C94.0313 10.0727 93.1456 9.85409 92.2666 9.85409C91.3876 9.85409 90.5018 10.0727 89.927 10.682C89.3522 11.2913 89.1452 12.2213 89.1452 13.1435C89.1452 14.0658 89.3522 15.003 89.927 15.6046C90.5018 16.2061 91.3886 16.433 92.2666 16.433C93.1446 16.433 94.0313 16.2143 94.6071 15.6046C94.6863 15.5186 94.7587 15.4248 94.8301 15.331H97.1935C96.9855 16.0657 96.6585 16.7299 96.1639 17.2454C95.2057 18.2531 93.7281 18.6205 92.2666 18.6205C90.805 18.6205 89.3284 18.2609 88.3703 17.2454C87.4121 16.2299 87.0613 14.6828 87.0613 13.1435C87.0613 11.6043 87.4043 10.0494 88.3703 9.04168C89.3367 8.03396 90.806 7.66663 92.2666 7.66663C93.7272 7.66663 95.2057 8.02618 96.1639 9.04168C97.1298 10.0572 97.4729 11.6043 97.4729 13.1435L97.4733 13.1431Z",
				fill: "currentColor"
			}),
			jsx("path", {
				d: "M109.499 13.1431V13.9948H103.119V12.2996H107.351C107.256 11.6825 107.032 11.1043 106.632 10.682C106.057 10.0727 105.172 9.85409 104.293 9.85409C103.414 9.85409 102.528 10.0727 101.953 10.682C101.378 11.2913 101.17 12.2213 101.17 13.1435C101.17 14.0658 101.378 15.003 101.953 15.6046C102.528 16.2061 103.415 16.433 104.293 16.433C105.171 16.433 106.057 16.2143 106.632 15.6046C106.712 15.5186 106.784 15.4248 106.856 15.331H109.22C109.012 16.0657 108.685 16.7299 108.19 17.2454C107.231 18.2531 105.754 18.6205 104.293 18.6205C102.831 18.6205 101.355 18.2609 100.396 17.2454C99.4382 16.2299 99.0864 14.6828 99.0864 13.1435C99.0864 11.6043 99.4295 10.0494 100.396 9.04168C101.362 8.03396 102.832 7.66663 104.293 7.66663C105.754 7.66663 107.231 8.02618 108.19 9.04168C109.156 10.0572 109.499 11.6043 109.499 13.1435V13.1431Z",
				fill: "currentColor"
			}),
			jsx("path", {
				d: "M113.5 4.62817H111.104V18.6217H113.5V4.62817Z",
				fill: "currentColor"
			}),
			jsx("path", {
				d: "M117.589 12.8154L121.517 18.6208H118.554L114.625 12.8154L118.554 8.15088H121.517L117.589 12.8154Z",
				fill: "currentColor"
			}),
			jsx("g", {
				clipPath: "url(#dsh-wordmark-whale-clip)",
				children: jsx("path", {
					d: "M23.0584 4.95203C22.8129 4.83203 22.7074 5.06103 22.5639 5.17704C22.5149 5.21454 22.4734 5.26354 22.4319 5.30854C22.0734 5.69155 21.6543 5.94306 21.1073 5.91306C20.3073 5.86806 19.6243 6.11957 19.0203 6.73158C18.8918 5.97706 18.4652 5.52655 17.8162 5.23754C17.4767 5.08753 17.1332 4.93703 16.8952 4.61052C16.7292 4.37801 16.6837 4.11901 16.6007 3.8635C16.5477 3.70949 16.4952 3.55199 16.3177 3.52549C16.1252 3.49549 16.0497 3.65699 15.9742 3.792C15.6722 4.34401 15.5552 4.95203 15.5667 5.56805C15.5932 6.95359 16.1782 8.05712 17.3407 8.84215C17.4727 8.93215 17.5067 9.02215 17.4652 9.15366C17.3857 9.42416 17.2917 9.68667 17.2087 9.95718C17.1557 10.1297 17.0767 10.1677 16.8917 10.0922C16.2537 9.82568 15.7027 9.43117 15.2156 8.95465C14.3891 8.15513 13.6416 7.2726 12.7096 6.58158C12.4906 6.42007 12.2716 6.27007 12.045 6.12707C11.094 5.20354 12.1696 4.44502 12.4186 4.35501C12.6791 4.26101 12.5091 3.938 11.6675 3.942C10.826 3.9455 10.056 4.22751 9.07446 4.60302C8.93096 4.65952 8.77995 4.70052 8.62545 4.73452C7.73492 4.56552 6.80989 4.52802 5.84386 4.63702C4.02481 4.83953 2.57177 5.69955 1.50373 7.1676C0.220694 8.93215 -0.0813148 10.9372 0.288196 13.0283C0.676708 15.2323 1.80174 17.0569 3.53029 18.4834C5.32285 19.9625 7.38741 20.6875 9.74298 20.5485C11.1735 20.466 12.7661 20.2745 14.5626 18.7539C15.0156 18.9795 15.4912 19.0695 16.2797 19.137C16.8872 19.1935 17.4722 19.107 17.9252 19.013C18.6347 18.8629 18.5857 18.2059 18.3292 18.0854C16.2497 17.1169 16.7062 17.5109 16.2912 17.1919C17.3477 15.9419 18.9618 13.7198 19.4598 10.6942C19.5088 10.3602 19.5713 9.88968 19.5638 9.61917C19.5598 9.45417 19.5978 9.39016 19.7863 9.37116C20.3073 9.31116 20.8128 9.16866 21.2773 8.91315C22.6249 8.17713 23.1684 6.96809 23.2964 5.51905C23.3154 5.29754 23.2924 5.06853 23.0584 4.95203ZM11.3165 17.9954C9.30097 16.4109 8.32344 15.8894 7.91992 15.9119C7.54241 15.9344 7.61042 16.3664 7.69342 16.6479C7.78042 16.9259 7.89342 17.1174 8.05193 17.3614C8.16143 17.5229 8.23694 17.7629 7.94243 17.9434C7.29341 18.3449 6.16487 17.8084 6.11187 17.7819C4.79833 17.0084 3.7003 15.9874 2.92628 14.5908C2.17875 13.2468 1.74474 11.8047 1.67324 10.2657C1.65424 9.89418 1.76374 9.76267 2.13375 9.69517C2.62077 9.60517 3.12278 9.58617 3.6093 9.65767C5.66636 9.95818 7.41741 10.8777 8.88545 12.3348C9.72348 13.1643 10.3575 14.1558 11.0105 15.1243C11.705 16.1529 12.4521 17.1329 13.4036 17.9364C13.7396 18.2179 14.0076 18.4319 14.2641 18.5899C13.4906 18.6764 12.1996 18.6949 11.3165 17.9964V17.9954ZM12.2826 11.7817C12.2826 11.6167 12.4146 11.4852 12.5806 11.4852C12.6181 11.4852 12.6521 11.4927 12.6826 11.5037C12.7241 11.5187 12.7621 11.5412 12.7921 11.5752C12.8451 11.6277 12.8751 11.7027 12.8751 11.7817C12.8751 11.9467 12.7431 12.0782 12.5771 12.0782C12.4111 12.0782 12.2826 11.9467 12.2826 11.7817ZM15.2831 13.3208C15.0906 13.3998 14.8981 13.4673 14.7131 13.4748C14.4261 13.4898 14.1131 13.3733 13.9431 13.2308C13.6791 13.0093 13.4901 12.8853 13.4111 12.4988C13.3771 12.3338 13.3961 12.0782 13.4261 11.9317C13.4941 11.6162 13.4186 11.4137 13.1961 11.2297C13.0151 11.0797 12.7846 11.0382 12.5316 11.0382C12.4371 11.0382 12.3506 10.9967 12.2861 10.9632C12.1806 10.9107 12.0936 10.7792 12.1766 10.6177C12.2031 10.5652 12.3316 10.4377 12.3616 10.4152C12.7051 10.2197 13.1011 10.2837 13.4676 10.4302C13.8071 10.5692 14.0641 10.8242 14.4336 11.1847C14.8111 11.6202 14.8791 11.7402 15.0941 12.0672C15.2641 12.3228 15.4186 12.5853 15.5247 12.8858C15.5887 13.0733 15.5057 13.2268 15.2831 13.3208Z",
					fill: "currentColor"
				})
			}),
			jsx("rect", {
				x: "129.348",
				y: "5.5",
				width: "52",
				height: "14",
				rx: "2",
				fill: "currentColor"
			}),
			jsxs("g", {
				clipPath: "url(#dsh-wordmark-badge-clip)",
				children: [
					jsx("path", {
						d: "M132.848 8.93205H134.08V16.137H132.848V8.93205ZM136.5 8.93205H137.732V16.137H136.5V8.93205ZM133.365 13.024V11.99H137.193V13.024H133.365Z",
						fill: "var(--dsw-alias-label-primary-inverted)"
					}),
					jsx("path", {
						d: "M140.397 14.432L140.672 13.453H143.202L143.532 14.432H140.397ZM140.287 16.137H139.055L141.277 8.93205H142.201L142.146 9.74605L140.947 13.915H140.969L140.287 16.137ZM145.039 16.137H143.741L143.07 13.948L143.081 13.937L141.871 9.74605L141.926 8.93205H142.817L145.039 16.137Z",
						fill: "var(--dsw-alias-label-primary-inverted)"
					}),
					jsx("path", {
						d: "M146.846 8.93205H149.068C149.852 8.93205 150.443 9.11538 150.839 9.48205C151.235 9.84138 151.433 10.3327 151.433 10.956C151.433 11.22 151.396 11.4657 151.323 11.693C151.249 11.9204 151.125 12.1257 150.949 12.309C150.773 12.4924 150.531 12.65 150.223 12.782C149.922 12.9067 149.541 13.0057 149.079 13.079V13.321H146.846V12.639L148.023 12.485C148.631 12.4044 149.09 12.298 149.398 12.166C149.706 12.034 149.915 11.8764 150.025 11.693C150.135 11.5024 150.19 11.2934 150.19 11.066C150.19 10.6994 150.083 10.417 149.871 10.219C149.658 10.021 149.324 9.92205 148.87 9.92205H146.846V8.93205ZM146.395 8.93205H147.627V16.137H146.395V8.93205ZM151.917 16.093V16.137H150.366L149.024 14.322C148.87 14.1094 148.73 13.9407 148.606 13.816C148.481 13.684 148.345 13.5887 148.199 13.53C148.052 13.464 147.872 13.42 147.66 13.398C147.447 13.3687 147.176 13.3504 146.846 13.343V13.145H149.079C149.233 13.211 149.368 13.2844 149.486 13.365C149.61 13.4457 149.735 13.5447 149.86 13.662C149.992 13.7794 150.138 13.937 150.3 14.135L151.917 16.093Z",
						fill: "var(--dsw-alias-label-primary-inverted)"
					}),
					jsx("path", {
						d: "M153.58 9.57005L153.591 8.93205H154.46L157.584 15.51V16.137H156.704L153.58 9.57005ZM158.024 16.137H156.968L156.88 8.93205H158.024V16.137ZM154.24 16.137H153.096V8.93205H154.152L154.24 16.137Z",
						fill: "var(--dsw-alias-label-primary-inverted)"
					}),
					jsx("path", {
						d: "M159.963 8.93205H161.206V16.137H159.963V8.93205ZM160.095 9.96605V8.93205H164.858V9.96605H160.095ZM160.095 16.137V15.103H164.902V16.137H160.095ZM160.095 13.013V11.99H164.374V13.013H160.095Z",
						fill: "var(--dsw-alias-label-primary-inverted)"
					}),
					jsx("path", {
						d: "M169.052 15.257C169.543 15.257 169.895 15.1654 170.108 14.982C170.328 14.7987 170.438 14.5457 170.438 14.223C170.438 14.047 170.405 13.8967 170.339 13.772C170.273 13.6474 170.152 13.5337 169.976 13.431C169.807 13.321 169.558 13.2147 169.228 13.112L168.491 12.881C167.846 12.6757 167.38 12.4044 167.094 12.067C166.808 11.7297 166.665 11.3007 166.665 10.78C166.665 10.428 166.76 10.1017 166.951 9.80105C167.142 9.50038 167.428 9.25838 167.809 9.07505C168.19 8.89172 168.663 8.80005 169.228 8.80005C169.631 8.80005 169.998 8.82938 170.328 8.88805C170.665 8.93938 171.039 9.01638 171.45 9.11905L171.274 10.175C170.834 10.0504 170.442 9.96238 170.097 9.91105C169.76 9.85238 169.463 9.82305 169.206 9.82305C168.737 9.82305 168.403 9.90738 168.205 10.076C168.007 10.2374 167.908 10.439 167.908 10.681C167.908 10.857 167.941 11.0147 168.007 11.154C168.073 11.286 168.19 11.407 168.359 11.517C168.535 11.627 168.784 11.7334 169.107 11.836L169.866 12.078C170.526 12.276 170.995 12.5327 171.274 12.848C171.553 13.156 171.692 13.585 171.692 14.135C171.692 14.5604 171.589 14.9344 171.384 15.257C171.179 15.5797 170.878 15.8327 170.482 16.016C170.093 16.1994 169.609 16.291 169.03 16.291C168.627 16.291 168.212 16.247 167.787 16.159C167.362 16.071 166.9 15.9427 166.401 15.774L166.665 14.718C167.156 14.894 167.6 15.0297 167.996 15.125C168.399 15.213 168.751 15.257 169.052 15.257Z",
						fill: "var(--dsw-alias-label-primary-inverted)"
					}),
					jsx("path", {
						d: "M175.809 15.257C176.3 15.257 176.652 15.1654 176.865 14.982C177.085 14.7987 177.195 14.5457 177.195 14.223C177.195 14.047 177.162 13.8967 177.096 13.772C177.03 13.6474 176.909 13.5337 176.733 13.431C176.564 13.321 176.315 13.2147 175.985 13.112L175.248 12.881C174.603 12.6757 174.137 12.4044 173.851 12.067C173.565 11.7297 173.422 11.3007 173.422 10.78C173.422 10.428 173.517 10.1017 173.708 9.80105C173.899 9.50038 174.185 9.25838 174.566 9.07505C174.947 8.89172 175.42 8.80005 175.985 8.80005C176.388 8.80005 176.755 8.82938 177.085 8.88805C177.422 8.93938 177.796 9.01638 178.207 9.11905L178.031 10.175C177.591 10.0504 177.199 9.96238 176.854 9.91105C176.517 9.85238 176.22 9.82305 175.963 9.82305C175.494 9.82305 175.16 9.90738 174.962 10.076C174.764 10.2374 174.665 10.439 174.665 10.681C174.665 10.857 174.698 11.0147 174.764 11.154C174.83 11.286 174.947 11.407 175.116 11.517C175.292 11.627 175.541 11.7334 175.864 11.836L176.623 12.078C177.283 12.276 177.752 12.5327 178.031 12.848C178.31 13.156 178.449 13.585 178.449 14.135C178.449 14.5604 178.346 14.9344 178.141 15.257C177.936 15.5797 177.635 15.8327 177.239 16.016C176.85 16.1994 176.366 16.291 175.787 16.291C175.384 16.291 174.969 16.247 174.544 16.159C174.119 16.071 173.657 15.9427 173.158 15.774L173.422 14.718C173.913 14.894 174.357 15.0297 174.753 15.125C175.156 15.213 175.508 15.257 175.809 15.257Z",
						fill: "var(--dsw-alias-label-primary-inverted)"
					})
				]
			}),
			jsxs("defs", { children: [jsx("clipPath", {
				id: "dsh-wordmark-whale-clip",
				children: jsx("rect", {
					width: "23.16",
					height: "17.0435",
					fill: "white",
					transform: "translate(0.141602 3.52185)"
				})
			}), jsx("clipPath", {
				id: "dsh-wordmark-badge-clip",
				children: jsx("rect", {
					width: "46",
					height: "14",
					fill: "white",
					transform: "translate(132.348 5.5)"
				})
			})] })
		]
	});
}
//#endregion
//#region lib/types/Tooltip.js
/**
* Attach a hover/focus tooltip to an anchor element.
* @param props.label - bubble text, or a resolver evaluated only while the bubble is visible.
* @param props.side - placement relative to the anchor (default 'right').
* @param props.delayMs - hover delay in milliseconds; keyboard focus remains immediate.
* @param props.disabled - suppress the bubble while true; the anchor renders identically so
* toggling never remounts it (which would cut its CSS transitions).
* @param props.maxWidth - bubble width cap in pixels, for labels long enough that the default
* half-viewport cap would render a slab wider than the surface the anchor sits on.
* @param props.children - a single anchor element; its own ref (callback or object) is forwarded alongside the tooltip's.
* @returns the cloned anchor plus a fixed-position bubble while hovered/focused.
*/
function Tooltip({ label, side = "right", delayMs = 0, disabled = false, maxWidth, children }) {
	const anchor = useRef(null);
	const childRef = children.ref;
	const mergedRef = useCallback((el) => {
		anchor.current = el;
		if (typeof childRef === "function") childRef(el);
		else if (childRef != null) childRef.current = el;
	}, [childRef]);
	const [pos, setPos] = useState(null);
	const [placement, setPlacement] = useState(side);
	const bubble = useRef(null);
	const resolvedLabel = pos === null ? null : typeof label === "function" ? label() : label;
	const y = pos === null ? 0 : placement === "right" ? pos.top + (pos.bottom - pos.top) / 2 : placement === "top" ? pos.top - 8 : pos.bottom + 8;
	const EDGE_MARGIN = 12;
	useLayoutEffect(() => {
		if (pos === null) return;
		const fit = () => {
			const el = bubble.current;
			/* v8 ignore next -- pos is set only while the bubble is mounted. */
			if (el === null) return;
			el.style.left = `${pos.x}px`;
			const r = el.getBoundingClientRect();
			let dx = 0;
			if (r.right > window.innerWidth - EDGE_MARGIN) dx = window.innerWidth - EDGE_MARGIN - r.right;
			if (r.left + dx < EDGE_MARGIN) dx = EDGE_MARGIN - r.left;
			el.style.left = `${pos.x + dx}px`;
			if (side === "right") return;
			const fitsBelow = pos.bottom + 8 + r.height <= window.innerHeight - EDGE_MARGIN;
			const fitsAbove = pos.top - 8 - r.height >= EDGE_MARGIN;
			if (placement === "bottom" && !fitsBelow && fitsAbove) setPlacement("top");
			if (placement === "top" && !fitsAbove && fitsBelow) setPlacement("bottom");
		};
		fit();
		window.addEventListener("resize", fit);
		return () => {
			window.removeEventListener("resize", fit);
		};
	}, [
		placement,
		pos,
		resolvedLabel,
		side
	]);
	const showTimer = useRef(null);
	const triggers = useRef({
		hover: false,
		focus: false
	});
	const cancelShow = useCallback(() => {
		if (showTimer.current === null) return;
		clearTimeout(showTimer.current);
		showTimer.current = null;
	}, []);
	useEffect(() => {
		if (disabled) {
			cancelShow();
			triggers.current = {
				hover: false,
				focus: false
			};
			setPos(null);
		}
		return cancelShow;
	}, [cancelShow, disabled]);
	const show = () => {
		if (disabled) return;
		if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
		const el = anchor.current;
		/* v8 ignore next -- the ref is attached by event time: events fire on the cloned anchor. */
		if (el === null) return;
		const r = el.getBoundingClientRect();
		setPlacement(side);
		setPos({
			x: side === "right" ? r.right + 10 : r.left + r.width / 2,
			top: r.top,
			bottom: r.bottom
		});
	};
	const showAfterHoverDelay = () => {
		cancelShow();
		if (delayMs <= 0) {
			show();
			return;
		}
		showTimer.current = setTimeout(() => {
			showTimer.current = null;
			show();
		}, delayMs);
	};
	const hide = () => {
		cancelShow();
		if (!triggers.current.hover && !triggers.current.focus) setPos(null);
	};
	/**
	* UNE INFOBULLE NE SURVIT PAS AU CONTEXTE QUI L'A FAITE NAÎTRE.
	*
	* Ses coordonnées sont relevées au moment où elle paraît (elle est en
	* `position: fixed`, sans portail) : tout ce qui bouge sous elle — un
	* défilement, un clavier qui rétrécit la fenêtre, une rotation — la laisse
	* plantée à côté de son ancre. Et sur un écran tactile le survol n'a pas de
	* fin : le `mouseenter` que le doigt fait naître n'est jamais suivi du
	* `mouseleave` qui allait avec, si bien que « copier », « envoyer » et leurs
	* pareils restaient affichés après un changement d'onglet ou d'application,
	* sans qu'aucun geste ne vienne les retirer.
	*
	* Tant qu'une bulle est visible, on écoute donc tout ce qui la périme et on
	* la retire : la page passe en arrière-plan, la fenêtre perd le focus, elle
	* est PIVOTÉE, la page défile, un doigt appuie AILLEURS (c'est le
	* « mouseleave » que le tactile n'envoie jamais), ou Échap.
	*
	* Le redimensionnement, lui, ne la retire pas : la passe de recadrage suit
	* déjà la bulle pour la garder dans la fenêtre (voir `fit`).
	*
	* L'appui sur l'ancre elle-même ne la retire pas : au doigt, c'est le même
	* geste qui vient de l'afficher.
	*/
	useEffect(() => {
		if (pos === null) return;
		const purger = () => {
			triggers.current = {
				hover: false,
				focus: false
			};
			cancelShow();
			setPos(null);
		};
		const surVisibilite = () => {
			if (document.visibilityState === "hidden") purger();
		};
		const surAppui = (e) => {
			const cible = e.target;
			if (cible instanceof Node && anchor.current?.contains(cible) === true) return;
			purger();
		};
		const surTouche = (e) => {
			if (e instanceof KeyboardEvent && e.key === "Escape") purger();
		};
		document.addEventListener("visibilitychange", surVisibilite);
		document.addEventListener("pointerdown", surAppui, true);
		document.addEventListener("keydown", surTouche);
		window.addEventListener("blur", purger);
		window.addEventListener("orientationchange", purger);
		window.addEventListener("scroll", purger, {
			capture: true,
			passive: true
		});
		return () => {
			document.removeEventListener("visibilitychange", surVisibilite);
			document.removeEventListener("pointerdown", surAppui, true);
			document.removeEventListener("keydown", surTouche);
			window.removeEventListener("blur", purger);
			window.removeEventListener("orientationchange", purger);
			window.removeEventListener("scroll", purger, { capture: true });
		};
	}, [cancelShow, pos]);
	return jsxs(Fragment, { children: [cloneElement(children, {
		ref: mergedRef,
		onMouseEnter: (e) => {
			children.props.onMouseEnter?.(e);
			triggers.current.hover = true;
			showAfterHoverDelay();
		},
		onMouseLeave: (e) => {
			children.props.onMouseLeave?.(e);
			triggers.current.hover = false;
			cancelShow();
			setPos(null);
		},
		onFocus: (e) => {
			children.props.onFocus?.(e);
			triggers.current.focus = true;
			cancelShow();
			show();
		},
		onBlur: (e) => {
			children.props.onBlur?.(e);
			triggers.current.focus = false;
			hide();
		}
	}), pos !== null && jsx("span", {
		ref: bubble,
		className: css$11.bubble,
		"data-side": placement,
		style: {
			left: pos.x,
			top: y,
			...maxWidth === void 0 ? {} : { maxWidth }
		},
		role: "tooltip",
		children: resolvedLabel
	})] });
}
//#endregion
//#region lib/types/Toast.js
/**
* Transient top-center banner: slides in, holds at full opacity, fades out,
* then reports done so the owner can unmount it. Re-showing the same text
* restarts the cycle when the owner remounts the component (key it by a
* per-show sequence). Rendered through a body portal so an owner inside a
* transformed or filtered ancestor cannot trap the fixed banner in that
* ancestor's box.
*
* @param props.text - resolved banner copy; the owner passes localized text.
* @param props.icon - optional leading glyph (e.g. a warning icon).
* @param props.anchor - optional element whose horizontal center the banner
* follows (e.g. the composer card, so the banner centers over the chat column
* rather than the whole window); omitted, it centers on the viewport.
* @param props.onDone - called once the fade completes; unmount the toast here.
* @returns the floating banner.
*/
function Toast({ text, icon, anchor, onDone }) {
	useEffect(() => {
		const timer = setTimeout(onDone, 4e3);
		return () => {
			clearTimeout(timer);
		};
	}, [onDone]);
	const [left, setLeft] = useState(null);
	useLayoutEffect(() => {
		if (anchor == null) return;
		const measure = () => {
			const rect = anchor.getBoundingClientRect();
			setLeft(rect.left + rect.width / 2);
		};
		measure();
		window.addEventListener("resize", measure);
		return () => {
			window.removeEventListener("resize", measure);
		};
	}, [anchor]);
	return createPortal(jsxs("div", {
		className: css$12.toast,
		role: "alert",
		style: left === null ? void 0 : { left },
		children: [icon !== void 0 && jsx("span", {
			className: css$12.icon,
			"aria-hidden": true,
			children: icon
		}), jsx("span", {
			className: css$12.text,
			children: text
		})]
	}), document.body);
}
//#endregion
//#region lib/types/JsonTree.js
const OBJECT_PREVIEW_LIMIT = 4;
const ARRAY_PREVIEW_LIMIT = 5;
const PREVIEW_DEPTH_LIMIT = 2;
const DEFAULT_LABELS$1 = {
	copyValue: "Copy value",
	copyJson: "Copy JSON",
	copyPath: "Copy property path",
	copyPrettyJson: "Copy pretty JSON",
	copyCompactJson: "Copy compact JSON",
	copied: "Copied",
	copyFailed: "Copy failed",
	collapseNode: "Collapse JSON node",
	expandNode: "Expand JSON node",
	copyButtonTitle: (action) => `${action}; right-click for copy options`
};
function valueCopyMenuItems(labels) {
	return [
		{
			id: "value",
			label: labels.copyValue
		},
		{
			id: "json",
			label: labels.copyJson
		},
		{
			id: "path",
			label: labels.copyPath
		}
	];
}
function objectCopyMenuItems(labels) {
	return [
		{
			id: "prettyJson",
			label: labels.copyPrettyJson
		},
		{
			id: "json",
			label: labels.copyCompactJson
		},
		{
			id: "path",
			label: labels.copyPath
		}
	];
}
function isExpandableValue(value) {
	return typeof value === "object" && value !== null && !(value instanceof Date);
}
function entriesOf(value) {
	if (Array.isArray(value)) return value.map((item, index) => [String(index), item]);
	return Object.keys(value).map((key) => [key, value[key]]);
}
function bracketOf(value) {
	return Array.isArray(value) ? ["[", "]"] : ["{", "}"];
}
function previewPrimitive(value) {
	if (value === null) return jsx("span", {
		className: css$13.keywordValue,
		children: "null"
	});
	if (typeof value === "string") return jsx("span", {
		className: css$13.stringValue,
		children: JSON.stringify(value)
	});
	if (typeof value === "number") return jsx("span", {
		className: css$13.numberValue,
		children: String(value)
	});
	if (typeof value === "boolean") return jsx("span", {
		className: css$13.keywordValue,
		children: String(value)
	});
	if (typeof value === "bigint") return jsx("span", {
		className: css$13.otherValue,
		children: value.toString()
	});
	if (typeof value === "undefined") return jsx("span", {
		className: css$13.otherValue,
		children: "undefined"
	});
	if (typeof value === "symbol") return jsx("span", {
		className: css$13.otherValue,
		children: value.description ?? "Symbol"
	});
	if (typeof value === "function") return jsx("span", {
		className: css$13.otherValue,
		children: value.name || "Function"
	});
	return null;
}
function previewValue(value, depth) {
	if (!isExpandableValue(value)) return previewPrimitive(value);
	const array = Array.isArray(value);
	const entries = entriesOf(value);
	const limit = array ? ARRAY_PREVIEW_LIMIT : OBJECT_PREVIEW_LIMIT;
	const visible = entries.slice(0, limit);
	const [open, close] = bracketOf(value);
	return jsxs(Fragment, { children: [
		jsx("span", {
			className: css$13.punctuation,
			children: open
		}),
		depth >= PREVIEW_DEPTH_LIMIT ? jsx("span", {
			className: css$13.previewEllipsis,
			children: "…"
		}) : visible.map(([key, item], index) => jsxs("span", { children: [
			index > 0 && jsx("span", {
				className: css$13.punctuation,
				children: ", "
			}),
			!array && jsxs(Fragment, { children: [jsx("span", {
				className: css$13.previewProperty,
				children: key
			}), jsx("span", {
				className: css$13.punctuation,
				children: ": "
			})] }),
			previewValue(item, depth + 1)
		] }, key)),
		depth < PREVIEW_DEPTH_LIMIT && entries.length > limit && jsx("span", {
			className: css$13.previewEllipsis,
			children: ", …"
		}),
		jsx("span", {
			className: css$13.punctuation,
			children: close
		})
	] });
}
function primitiveValue(value) {
	if (value === null) return jsx("span", {
		className: css$13.keywordValue,
		children: "null"
	});
	if (typeof value === "string") return jsx("span", {
		className: css$13.stringValue,
		children: JSON.stringify(value)
	});
	if (typeof value === "boolean") return jsx("span", {
		className: css$13.keywordValue,
		children: String(value)
	});
	if (typeof value === "number") return jsx("span", {
		className: css$13.numberValue,
		children: String(value)
	});
	if (typeof value === "bigint") return jsx("span", {
		className: css$13.numberValue,
		children: `${value.toString()}n`
	});
	if (value instanceof Date) return jsx("span", {
		className: css$13.otherValue,
		children: value.toISOString()
	});
	if (typeof value === "function") return jsxs("span", {
		className: css$13.otherValue,
		children: ["function() ", "{ }"]
	});
	if (typeof value === "undefined") return jsx("span", {
		className: css$13.otherValue,
		children: "undefined"
	});
	return jsx("span", {
		className: css$13.otherValue,
		children: value.toString()
	});
}
function fieldText(field) {
	return field === "" ? "\"\"" : field;
}
function pathId(path) {
	return path.map((part) => typeof part === "number" ? `n${String(part)}` : `s${String(part.length)}:${part}`).join("/");
}
function claimFocus(button) {
	button.focus();
}
function moveFocus(button, direction) {
	const tree = button.closest("[role=\"tree\"]");
	/* v8 ignore next -- JsonTree attaches expander handlers only beneath its owning role=tree. */
	if (tree === null) return;
	const expanders = Array.from(tree.querySelectorAll("[data-json-expander]"));
	const current = expanders.indexOf(button);
	/* v8 ignore next -- the current expander is a member of the queried non-empty set. */
	if (current < 0 || expanders.length === 0) return;
	const nextExpander = expanders[(current + direction + expanders.length) % expanders.length];
	/* v8 ignore next -- modulo over the non-empty expander set always resolves a member. */
	if (nextExpander !== void 0) claimFocus(nextExpander);
}
function NodeField({ field, expandable, onToggle }) {
	if (field === void 0) return null;
	return jsxs("span", {
		className: clsx(css$13.label, expandable && css$13.clickableLabel),
		onClick: expandable ? onToggle : void 0,
		children: [fieldText(field), ":"]
	});
}
function JsonTreeNode({ field, initialExpanded, labels, lastElement, onClaimTabStop, onRowHover, path, tabStopId, value }) {
	const contentsId = useId();
	const expanderRef = useRef(null);
	const [expanded, setExpanded] = useState(initialExpanded);
	const nodeId = pathId(path);
	const container = isExpandableValue(value);
	const entries = container ? entriesOf(value) : [];
	const expandable = entries.length > 0;
	const toggle = () => {
		setExpanded((current) => !current);
		claimFocus(expanderRef.current);
	};
	const onExpanderKeyDown = (event) => {
		if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
			event.preventDefault();
			setExpanded(event.key === "ArrowRight");
			return;
		}
		if (event.key === "ArrowUp" || event.key === "ArrowDown") {
			event.preventDefault();
			moveFocus(event.currentTarget, event.key === "ArrowUp" ? -1 : 1);
		}
	};
	const row = (children, ariaExpanded) => jsx("div", {
		className: css$13.row,
		role: "treeitem",
		"aria-expanded": ariaExpanded,
		onMouseOver: (event) => {
			event.stopPropagation();
			onRowHover(event.currentTarget, {
				path,
				value
			});
		},
		children
	});
	if (!container) return row(jsxs(Fragment, { children: [
		jsx(NodeField, {
			field,
			expandable: false,
			onToggle: toggle
		}),
		primitiveValue(value),
		!lastElement && jsx("span", {
			className: css$13.punctuation,
			children: ","
		})
	] }));
	const [open, close] = bracketOf(value);
	if (!expandable) return row(jsxs(Fragment, { children: [
		jsx(NodeField, {
			field,
			expandable: false,
			onToggle: toggle
		}),
		jsx("span", {
			className: css$13.punctuation,
			children: open
		}),
		jsx("span", {
			className: css$13.punctuation,
			children: close
		}),
		!lastElement && jsx("span", {
			className: css$13.punctuation,
			children: ","
		})
	] }));
	return row(jsxs(Fragment, { children: [
		jsx("span", {
			ref: expanderRef,
			className: clsx(css$13.expander, expanded ? css$13.collapseIcon : css$13.expandIcon),
			"data-json-expander": true,
			role: "button",
			"aria-label": expanded ? labels.collapseNode : labels.expandNode,
			"aria-expanded": expanded,
			"aria-controls": expanded ? contentsId : void 0,
			tabIndex: tabStopId === nodeId ? 0 : -1,
			onFocus: () => {
				onClaimTabStop(nodeId);
			},
			onClick: toggle,
			onKeyDown: onExpanderKeyDown
		}),
		jsx(NodeField, {
			field,
			expandable: true,
			onToggle: toggle
		}),
		jsx("span", {
			className: css$13.preview,
			children: previewValue(value, 0)
		}),
		!lastElement && jsx("span", {
			className: css$13.punctuation,
			children: ","
		}),
		expanded && jsx("ul", {
			id: contentsId,
			role: "group",
			className: css$13.children,
			children: entries.map(([key, item], index) => jsx(JsonTreeNode, {
				field: key,
				value: item,
				path: [...path, Array.isArray(value) ? index : key],
				labels,
				lastElement: index === entries.length - 1,
				initialExpanded: false,
				tabStopId,
				onClaimTabStop,
				onRowHover
			}, key))
		})
	] }), expanded);
}
function formattedPath(path) {
	return path.reduce((result, part) => {
		if (typeof part === "number") return `${result}[${String(part)}]`;
		return /^[A-Za-z_$][\w$]*$/.test(part) ? `${result}.${part}` : `${result}[${JSON.stringify(part)}]`;
	}, "$");
}
function copyText$2(target, mode) {
	if (mode === "path") return formattedPath(target.path);
	if (mode === "prettyJson") return JSON.stringify(target.value, null, 2);
	if (mode === "json") return JSON.stringify(target.value);
	if (typeof target.value === "string") return target.value;
	if (typeof target.value === "undefined") return "undefined";
	if (typeof target.value === "bigint") return target.value.toString();
	if (typeof target.value === "symbol") return target.value.description ?? "Symbol";
	if (typeof target.value === "function") return target.value.name || "Function";
	return JSON.stringify(target.value);
}
/**
* Render parsed JSON as a compact, keyboard-accessible inspector tree.
* @param props - Parsed data, accessible label, and display options.
* @returns A read-only JSON tree with an optionally fixed-open top level.
*/
function JsonTree({ data, label = "JSON", className, copyable = true, expandTopLevel = true, labels }) {
	const copyLabels = useMemo(() => labels === void 0 ? DEFAULT_LABELS$1 : {
		...DEFAULT_LABELS$1,
		...labels
	}, [labels]);
	const rootEntries = entriesOf(data);
	const firstExpandableIndex = rootEntries.findIndex(([, value]) => isExpandableValue(value) && entriesOf(value).length > 0);
	const firstExpandableEntry = rootEntries[firstExpandableIndex];
	const initialTabStopId = expandTopLevel ? firstExpandableEntry === void 0 ? null : pathId([Array.isArray(data) ? firstExpandableIndex : firstExpandableEntry[0]]) : isExpandableValue(data) && rootEntries.length > 0 ? pathId([]) : null;
	const rootRef = useRef(null);
	const activeRowRef = useRef();
	const copyButtonRef = useRef(null);
	const copyMenuOpenRef = useRef(false);
	const resetTimer = useRef();
	const [copyTarget, setCopyTarget] = useState();
	const [copyState, setCopyState] = useState("idle");
	const [copyMenuOpen, setCopyMenuOpen] = useState(false);
	const [tabStopId, setTabStopId] = useState(initialTabStopId);
	const setActiveRow = (row) => {
		activeRowRef.current?.removeAttribute("data-json-copy-active");
		activeRowRef.current = row;
		row?.setAttribute("data-json-copy-active", "");
	};
	const clearCopyTarget = () => {
		setActiveRow(void 0);
		setCopyTarget(void 0);
		setCopyState("idle");
		copyMenuOpenRef.current = false;
		setCopyMenuOpen(false);
	};
	const copyPosition = (row) => {
		const root = rootRef.current;
		/* v8 ignore next -- row events and viewport listeners run only after the root ref mounts. */
		if (root === null) throw new Error("JsonTree root is not mounted");
		const rootRect = root.getBoundingClientRect();
		const rowRect = row.getBoundingClientRect();
		return {
			left: rootRect.left + root.clientWidth - 26,
			side: rowRect.top - rootRect.top > root.clientHeight / 2 ? "top" : "bottom",
			top: rowRect.top
		};
	};
	const positionCopyButton = (row, target) => {
		const position = copyPosition(row);
		setCopyTarget({
			...target,
			...position
		});
	};
	const repositionCopyButton = (row) => {
		const position = copyPosition(row);
		setCopyTarget((current) => {
			/* v8 ignore next -- an active row and its copy target are installed together. */
			if (current === void 0) return current;
			return {
				...current,
				...position
			};
		});
	};
	useEffect(() => () => {
		if (resetTimer.current !== void 0) clearTimeout(resetTimer.current);
		activeRowRef.current?.removeAttribute("data-json-copy-active");
	}, []);
	useEffect(() => {
		activeRowRef.current?.removeAttribute("data-json-copy-active");
		activeRowRef.current = void 0;
		copyMenuOpenRef.current = false;
		setCopyTarget(void 0);
		setCopyState("idle");
		setCopyMenuOpen(false);
		setTabStopId(initialTabStopId);
	}, [
		data,
		expandTopLevel,
		initialTabStopId
	]);
	useEffect(() => {
		const reposition = () => {
			const row = activeRowRef.current;
			if (row !== void 0) repositionCopyButton(row);
		};
		window.addEventListener("scroll", reposition, true);
		window.addEventListener("resize", reposition);
		return () => {
			window.removeEventListener("scroll", reposition, true);
			window.removeEventListener("resize", reposition);
		};
	}, []);
	const handleRowHover = (row, target) => {
		if (!copyable || copyMenuOpenRef.current) return;
		if (activeRowRef.current === row) return;
		setActiveRow(row);
		setCopyState("idle");
		copyMenuOpenRef.current = false;
		setCopyMenuOpen(false);
		positionCopyButton(row, target);
	};
	const handleRootMouseOver = (event) => {
		if (!copyable || copyMenuOpenRef.current) return;
		/* v8 ignore next -- browser mouse events delivered through React target an Element. */
		if (!(event.target instanceof Element)) return;
		if (event.target.closest("[data-json-copy-button]") === null) clearCopyTarget();
	};
	const handleScroll = (_event) => {
		const row = activeRowRef.current;
		if (row !== void 0) repositionCopyButton(row);
	};
	const copy = async (mode) => {
		/* v8 ignore next -- copy controls only render while their target exists. */
		if (copyTarget === void 0) return;
		try {
			await navigator.clipboard.writeText(copyText$2(copyTarget, mode));
			setCopyState("copied");
		} catch {
			setCopyState("failed");
		}
		if (resetTimer.current !== void 0) clearTimeout(resetTimer.current);
		resetTimer.current = setTimeout(() => {
			setCopyState("idle");
		}, 1500);
	};
	const [rootOpen, rootClose] = bracketOf(data);
	const copyTargetIsObject = typeof copyTarget?.value === "object" && copyTarget.value !== null;
	const defaultCopyMode = copyTargetIsObject ? "prettyJson" : "value";
	const copyTitle = copyState === "copied" ? copyLabels.copied : copyState === "failed" ? copyLabels.copyFailed : copyTargetIsObject ? copyLabels.copyPrettyJson : copyLabels.copyValue;
	return jsxs("div", {
		ref: rootRef,
		className: clsx(css$13.root, className),
		onMouseOver: handleRootMouseOver,
		onMouseLeave: () => {
			if (!copyMenuOpenRef.current) clearCopyTarget();
		},
		onScroll: handleScroll,
		children: [expandTopLevel ? jsxs("div", {
			className: css$13.expandedTopLevel,
			children: [
				jsx("div", {
					className: clsx(css$13.row, css$13.topLevelBracket),
					"data-json-root-row": true,
					onMouseOver: (event) => {
						event.stopPropagation();
						handleRowHover(event.currentTarget, {
							path: [],
							value: data
						});
					},
					children: jsx("span", {
						className: css$13.punctuation,
						children: rootOpen
					})
				}),
				jsx("div", {
					"aria-label": label,
					className: clsx(css$13.container, css$13.expandedTopLevelContainer),
					role: "tree",
					children: rootEntries.map(([key, value], index) => jsx(JsonTreeNode, {
						field: key,
						value,
						path: [Array.isArray(data) ? index : key],
						labels: copyLabels,
						lastElement: index === rootEntries.length - 1,
						initialExpanded: false,
						tabStopId,
						onClaimTabStop: setTabStopId,
						onRowHover: handleRowHover
					}, key))
				}),
				jsx("div", {
					className: clsx(css$13.row, css$13.topLevelBracket),
					children: jsx("span", {
						className: css$13.punctuation,
						children: rootClose
					})
				})
			]
		}) : jsx("div", {
			"aria-label": label,
			className: css$13.container,
			role: "tree",
			children: jsx(JsonTreeNode, {
				value: data,
				path: [],
				labels: copyLabels,
				lastElement: true,
				initialExpanded: true,
				tabStopId,
				onClaimTabStop: setTabStopId,
				onRowHover: handleRowHover
			})
		}), copyTarget !== void 0 && jsx("span", {
			className: css$13.copyAnchor,
			style: {
				left: copyTarget.left,
				top: copyTarget.top
			},
			children: jsx(Menu, {
				open: copyMenuOpen,
				compact: true,
				portal: true,
				align: "end",
				side: copyTarget.side,
				anchor: jsx("button", {
					ref: copyButtonRef,
					type: "button",
					className: css$13.copyButton,
					"data-json-copy-button": true,
					"data-state": copyState,
					"aria-label": copyTitle,
					title: copyLabels.copyButtonTitle(copyTitle),
					onClick: () => void copy(defaultCopyMode),
					onContextMenu: (event) => {
						event.preventDefault();
						event.stopPropagation();
						copyMenuOpenRef.current = true;
						setCopyMenuOpen(true);
					},
					children: copyState === "copied" ? jsx(IconCheckOutline16, { size: 12 }) : jsx(IconCopyOutline16, { size: 12 })
				}),
				items: copyTargetIsObject ? objectCopyMenuItems(copyLabels) : valueCopyMenuItems(copyLabels),
				onSelect: (id) => {
					copy(id);
					copyMenuOpenRef.current = false;
					setCopyMenuOpen(false);
				},
				onClose: clearCopyTarget,
				getAnchorRect: () => copyButtonRef.current.getBoundingClientRect()
			})
		})]
	});
}
//#endregion
//#region lib/types/ansi.js
/**
* The 8/16 basic ANSI colors, keyed by the whitespace-free `r,g,b` triple
* anser emits for them, mapped onto the theme tokens that carry the same
* semantic. Black and white both resolve to the primary label color so text
* stays legible under either theme instead of matching the surface it sits
* on; bright black takes the tertiary label color (the muted-gray role).
* Magenta and cyan have no token equivalent in this design system and fall
* through to anser's literal rgb, as do all 256-palette and truecolor values.
*/
const TOKEN_BY_BASIC_RGB = {
	"0,0,0": "var(--dsw-alias-label-primary)",
	"255,255,255": "var(--dsw-alias-label-primary)",
	"85,85,85": "var(--dsw-alias-label-tertiary)",
	"187,0,0": "var(--dsw-alias-state-error-primary)",
	"255,85,85": "var(--dsw-alias-state-error-secondary)",
	"0,187,0": "var(--dsw-alias-state-success-primary)",
	"0,255,0": "var(--dsw-alias-state-success-secondary)",
	"187,187,0": "var(--dsw-alias-state-warn-primary)",
	"255,255,85": "var(--dsw-alias-state-warn-secondary)",
	"0,0,187": "var(--dsw-alias-state-business-primary)",
	"85,85,255": "var(--dsw-static-blue-400)"
};
/**
* CSS for each SGR attribute anser reports. `blink` is deliberately absent —
* animated text is not reproduced. `reverse` never arrives here: anser
* consumes it by swapping the run's foreground and background. Underline and
* strikethrough share `textDecoration`, so in a run declaring both, the
* later declaration wins.
*/
const STYLE_BY_DECORATION = {
	bold: { fontWeight: 700 },
	dim: { opacity: .7 },
	italic: { fontStyle: "italic" },
	underline: { textDecoration: "underline" },
	strikethrough: { textDecoration: "line-through" },
	hidden: { visibility: "hidden" }
};
/** OSC strings (window title, hyperlinks), with or without their terminator. */
const OSC_SEQUENCE = /\u001b\][^\u0007\u001b]*(?:\u0007|\u001b\\)?/g;
/** Escape sequences other than CSI: charset selection, single-shift, reset. */
const NON_CSI_ESCAPE = /\u001b(?!\[)[\u0020-\u002f]*[\u0030-\u007e]?/g;
/**
* C0 controls with no display meaning here. Tab, newline, backspace and ESC
* survive: the first two for layout, backspace for the cursor replay, ESC
* for anser's CSI split.
*/
const INERT_CONTROL = /[\u0000-\u0007\u000b-\u001a\u001c-\u001f\u007f]/g;
/**
* Lines whose cursor movements have to be replayed: a carriage return, a
* backspace, or an erase-in-line. The erase pattern matches the SAME CSI shape
* `replayLine` parses (parameters may carry `;` and intermediate bytes), so a
* form like `\x1b[1;2K` cannot slip past this guard and skip its own erase.
*/
const NEEDS_REPLAY = /\r|\u0008|\u001b\[[\u0030-\u003f]*[\u0020-\u002f]*K/;
/** SGR sequences alone, for folding state through a line that needs no replay. */
const SGR_SEQUENCE = /\u001b\[([\u0030-\u003f]*)[\u0020-\u002f]*m/g;
/** Terminal tab stop width; a tab advances to the next multiple of this. */
const TAB_WIDTH = 8;
/**
* Combining marks and other zero-width code points: a terminal advances no
* column for them, so `e` + U+0301 occupies one cell and a two-column redraw
* covers both code points.
*/
const ZERO_WIDTH = /^[\p{Mn}\p{Me}\p{Cf}\u200b-\u200f\u2060]$/u;
/**
* Characters a terminal advances two columns for: CJK scripts, fullwidth forms,
* CJK punctuation, and characters with emoji presentation. Text-presentation
* symbols (`\u2713`, `\u26a0` and the rest of U+2600-U+27BF) are ONE column and
* must stay out of this set.
*/
const WIDE_CHAR = /* @__PURE__ */ new RegExp("\\p{Script=Han}|\\p{Script=Hiragana}|\\p{Script=Katakana}|\\p{Script=Hangul}|\\p{Emoji_Presentation}|[\\uff01-\\uff60\\u3000-\\u303e]", "u");
/**
* Whether a character occupies two terminal columns (CJK, fullwidth forms,
* emoji). Covers the ranges a command's output realistically carries; a
* narrower guess would misalign the columns this card exists to preserve.
* @param char - one character from the output.
* @returns true when the terminal advances two columns for it.
*/
function isWide(char) {
	const code = char.codePointAt(0);
	if (code === void 0 || code < 4352) return false;
	return WIDE_CHAR.test(char);
}
/** The default state: no color, no attributes. */
const SGR_NONE = {
	fg: "",
	bg: "",
	attrs: []
};
/** Attribute closers, mapped to the opener parameters each one turns off. */
const ATTR_CLOSERS = {
	22: ["1", "2"],
	23: ["3"],
	24: ["4"],
	25: ["5", "6"],
	27: ["7"],
	28: ["8"],
	29: ["9"]
};
/**
* Fold one SGR sequence's parameters into the state it produces.
* @param state - state in force before the sequence.
* @param params - the sequence's raw parameter string (`31`, `1;4`, `38;5;208`).
* @returns the state the sequence leaves in force.
*/
function foldSgr(state, params) {
	const codes = params === "" ? ["0"] : params.split(";");
	let next = state;
	for (let index = 0; index < codes.length; index++) {
		const code = String(codes[index]);
		if (code === "" || code === "0") {
			next = SGR_NONE;
			continue;
		}
		if (code === "38" || code === "48") {
			const kind = codes[index + 1] ?? "";
			const span = kind === "2" ? 4 : kind === "5" ? 2 : 0;
			const value = codes.slice(index, index + span + 1).join(";");
			next = code === "38" ? {
				...next,
				fg: value
			} : {
				...next,
				bg: value
			};
			index += span;
			continue;
		}
		const closes = ATTR_CLOSERS[code];
		if (closes !== void 0) {
			next = {
				...next,
				attrs: next.attrs.filter((attr) => !closes.includes(attr))
			};
			continue;
		}
		const numeric = Number(code);
		if (code === "39") {
			next = {
				...next,
				fg: ""
			};
			continue;
		}
		if (code === "49") {
			next = {
				...next,
				bg: ""
			};
			continue;
		}
		if (numeric >= 30 && numeric <= 37 || numeric >= 90 && numeric <= 97) {
			next = {
				...next,
				fg: code
			};
			continue;
		}
		if (numeric >= 40 && numeric <= 47 || numeric >= 100 && numeric <= 107) {
			next = {
				...next,
				bg: code
			};
			continue;
		}
		if (!next.attrs.includes(code)) next = {
			...next,
			attrs: [...next.attrs, code]
		};
	}
	return next;
}
/**
* Render a state as the one canonical sequence that establishes it from the
* default, so a boundary emits a bounded string no matter how the state was
* reached.
* @param state - the state to open.
* @returns the SGR sequence, or the empty string for the default state.
*/
function openSgr(state) {
	const codes = [...state.attrs];
	if (state.fg !== "") codes.push(state.fg);
	if (state.bg !== "") codes.push(state.bg);
	return codes.length === 0 ? "" : `\u001b[${codes.join(";")}m`;
}
/** Whether two states are the same, so a boundary is only emitted on a change. */
function sameSgr(a, b) {
	return a.fg === b.fg && a.bg === b.bg && a.attrs.length === b.attrs.length && a.attrs.every((attr, index) => attr === b.attrs[index]);
}
/**
* Replay one line's cursor movements the way a terminal paints it, into a
* column buffer. Carriage return and backspace only MOVE the cursor — neither
* erases anything — so what a reader sees is whatever each column last had
* written to it. That distinction is the whole point of doing this as a buffer
* rather than as string surgery: `100%\rOK` shows `OK0%` because the redraw is
* shorter than the frame beneath it, and a trailing `abc\b` still shows `abc`
* because nothing ever overwrote the `c`.
*
* A CSI sequence occupies no column; it changes the state that the NEXT writes
* are stamped with, which is how a terminal stores color per cell. `red bad`
* then three backspaces then `ok` therefore shows `okd` with the `d` still red:
* `ok` overwrote two cells and the third kept the state it was written with.
* The columns are re-emitted as runs, so anser sees that same styling.
* @param line - one output line, still carrying its CSI sequences.
* @param entrySgr - SGR state in force when the line begins, since a newline
*   does not reset it.
* @returns the line as the terminal would have it after every movement, plus the
*   SGR state at its end for the next line to enter with.
*/
function replayLine(line, entrySgr) {
	const csi = /\u001b\[([\u0030-\u003f]*)[\u0020-\u002f]*([\u0040-\u007e])/g;
	/** Per column: the state in force when it was written, and its character. */
	const columns = [];
	let cursor = 0;
	let sgr = entrySgr;
	let at = 0;
	/** Clear a cell and, for a wide pair, its partner: a terminal erases both. */
	const clear = (index, fill) => {
		const cell = columns[index];
		if (cell?.spacer === true && index > 0) columns[index - 1] = {
			sgr,
			char: fill
		};
		else if (cell !== void 0 && isWide(cell.char) && columns[index + 1]?.spacer === true) columns[index + 1] = {
			sgr,
			char: fill
		};
		columns[index] = {
			sgr,
			char: fill
		};
	};
	const consume = (chunk) => {
		for (const char of chunk) {
			if (char === "\r") {
				cursor = 0;
				continue;
			}
			if (char === "\b") {
				cursor = Math.max(0, cursor - 1);
				continue;
			}
			if (char === "	") {
				const stop = cursor + TAB_WIDTH - cursor % TAB_WIDTH;
				for (; cursor < stop; cursor++) columns[cursor] ??= {
					sgr,
					char: " "
				};
				continue;
			}
			if (ZERO_WIDTH.test(char)) {
				const base = cursor > 0 ? columns[cursor - 1] : void 0;
				if (base !== void 0) columns[cursor - 1] = {
					sgr: base.sgr,
					char: base.char + char
				};
				continue;
			}
			clear(cursor, " ");
			columns[cursor] = {
				sgr,
				char
			};
			cursor++;
			if (isWide(char)) {
				columns[cursor] = {
					sgr,
					char: "",
					spacer: true
				};
				cursor++;
			}
		}
	};
	for (const match of line.matchAll(csi)) {
		consume(line.slice(at, match.index));
		at = match.index + match[0].length;
		const params = String(match[1]);
		const final = String(match[2]);
		if (final === "K") {
			const mode = String(params.split(";")[0]);
			if (mode === "1") for (let index = 0; index <= cursor; index++) clear(index, " ");
			else columns.length = mode === "2" ? 0 : cursor;
			continue;
		}
		if (final !== "m") continue;
		sgr = foldSgr(sgr, params);
	}
	consume(line.slice(at));
	let out = "";
	let active = entrySgr;
	for (let index = 0; index < columns.length; index++) {
		const column = columns[index] ?? {
			sgr: SGR_NONE,
			char: " "
		};
		if (!sameSgr(column.sgr, active)) {
			if (!sameSgr(active, SGR_NONE)) out += "\x1B[0m";
			out += openSgr(column.sgr);
			active = column.sgr;
		}
		const leadIntact = index > 0 && isWide(columns[index - 1]?.char ?? "");
		out += column.spacer === true && !leadIntact ? " " : column.char;
	}
	if (!sameSgr(active, sgr)) {
		if (!sameSgr(active, SGR_NONE)) out += "\x1B[0m";
		out += openSgr(sgr);
	}
	return {
		text: out,
		sgr
	};
}
/**
* Replay every line's cursor movements. A `\r` that only terminates a CRLF line
* is dropped first, so those lines keep their text instead of being redrawn onto
* themselves. SGR state threads across lines: a newline does not reset it, so a
* run opened before a redraw still colors the lines after it.
* @param text - output text, already free of OSC and non-CSI escapes.
* @returns the text with each line painted as the terminal would.
*/
function applyCursorMovements(text) {
	const replayed = [];
	let sgr = SGR_NONE;
	for (const raw of text.split("\n")) {
		const line = raw.replace(/\r+$/, "");
		if (NEEDS_REPLAY.test(line)) {
			const result = replayLine(line, sgr);
			replayed.push(result.text);
			sgr = result.sgr;
			continue;
		}
		replayed.push(line);
		for (const match of line.matchAll(SGR_SEQUENCE)) sgr = foldSgr(sgr, String(match[1]));
	}
	return replayed.join("\n");
}
/**
* Remove every escape sequence and control character that carries no color,
* leaving CSI sequences for anser and `\n`/`\t` for layout. Cursor movements
* (carriage return, backspace) replay first, since their effect on the visible
* text must land before the characters that expressed them are dropped.
* @param text - raw command output.
* @returns text whose only remaining escapes are CSI sequences.
*/
function sanitize(text) {
	return applyCursorMovements(text.replace(OSC_SEQUENCE, "").replace(NON_CSI_ESCAPE, "")).replace(INERT_CONTROL, "");
}
/**
* Resolve one run's colors and decorations.
* @param chunk - the anser chunk to style.
* @returns the run's inline style, or undefined when it carries no SGR state.
*/
function resolveStyle(chunk) {
	const style = {};
	const background = chunk.bg === null ? void 0 : `rgb(${chunk.bg})`;
	if (background !== void 0) style.backgroundColor = background;
	if (chunk.fg !== null) {
		const literal = `rgb(${chunk.fg})`;
		style.color = background === void 0 ? TOKEN_BY_BASIC_RGB[chunk.fg.replace(/\s+/g, "")] ?? literal : literal;
	}
	for (const decoration of chunk.decorations) Object.assign(style, STYLE_BY_DECORATION[decoration]);
	return Object.keys(style).length === 0 ? void 0 : style;
}
/**
* Parse command output into styled spans grouped by line.
* @param text - raw output text, which may contain ANSI escape sequences.
* @returns one entry per output line (always at least one, possibly empty).
*/
function parseAnsiLines(text) {
	let current = [];
	const lines = [current];
	for (const chunk of Anser.ansiToJson(sanitize(text), {
		json: true,
		remove_empty: true
	})) {
		const style = resolveStyle(chunk);
		for (const [index, part] of chunk.content.split("\n").entries()) {
			if (index > 0) {
				current = [];
				lines.push(current);
			}
			if (part !== "") current.push({
				text: part,
				style
			});
		}
	}
	return lines;
}
//#endregion
//#region lib/types/head-tail-cap.js
/**
* Compute the head/tail cap metrics for a list of `total` rows against `maxLines`,
* given whether the surface is expanded. Pure arithmetic; the caller slices its
* own rows with `headLines`/`tailLines` so a block can layer its own concerns
* (SearchBlock restores a tail file header) on top.
* @param total - the list's row count.
* @param maxLines - the collapsed-height cap in rows.
* @param expanded - whether the surface is expanded (uncaps the list).
* @returns the split metrics.
*/
function headTailCap(total, maxLines, expanded) {
	const hidden = total - maxLines;
	const headLines = Math.ceil(maxLines / 2);
	return {
		hidden,
		capped: hidden > 0 && !expanded,
		headLines,
		tailLines: maxLines - headLines
	};
}
//#endregion
//#region lib/types/use-copy-feedback.js
/** How long the `copied` flag stays true after a successful write, in ms. */
const COPIED_FEEDBACK_MS = 1e3;
/**
* Copy `text` to the clipboard with one-second success feedback.
* @param text - the text to write on copy.
* @returns the `copied` flag and the `onCopy` handler.
*/
function useCopyFeedback(text) {
	const [copied, setCopied] = useState(false);
	return {
		copied,
		onCopy: useCallback(() => {
			if (copied) return;
			writeClipboard(text).then((ok) => {
				if (!ok) return;
				setCopied(true);
				window.setTimeout(() => {
					setCopied(false);
				}, COPIED_FEEDBACK_MS);
			});
		}, [copied, text])
	};
}
//#endregion
//#region lib/types/TerminalBlock.js
/**
* Output lines shown before the height cap collapses the middle. Matches the
* TUI transcript's default tool-output budget so both front ends cut a long
* command's output at the same place.
*/
const DEFAULT_TERMINAL_MAX_LINES = 16;
const DEFAULT_LABELS = {
	signal: (signal) => `信号 ${signal}`,
	exitCode: (exitCode) => `退出码 ${exitCode}`,
	running: "运行中",
	failed: "失败",
	done: "已完成",
	copy: "复制",
	copied: "复制成功",
	noOutput: "无输出",
	collapseAria: "收起输出",
	collapse: "收起",
	expandAria: (hidden) => `展开其余 ${hidden} 行输出`,
	expand: (hidden) => `… 其余 ${hidden} 行`
};
/**
* Prompt label for a working directory: `~` for the home directory itself,
* otherwise the path's last segment (both separators accepted, trailing
* separators ignored), falling back to the path itself when it has no
* segment.
* @param cwd - the working directory path.
* @param home - absolute home directory, when the caller knows it.
* @returns the prompt label.
*/
function promptLabel(cwd, home) {
	const trimmed = cwd.replace(/[/\\]+$/, "");
	if (home !== void 0 && trimmed === home.replace(/[/\\]+$/, "")) return "~";
	const segment = trimmed.split(/[/\\]/).pop();
	return segment === void 0 || segment === "" ? cwd : segment;
}
/**
* Status pill text for a settled command, or undefined when the command
* settled cleanly (exit 0, no signal) and needs no pill — the same
* distinction the bash tool's own exit-status markers draw.
* @param exitCode - settled exit code, when known.
* @param signal - settled terminating signal name, when known.
* @param labels - display copy for the pill text.
* @returns the pill text, or undefined for a clean exit.
*/
function statusText(exitCode, signal, labels) {
	if (signal !== void 0) return labels.signal(signal);
	if (exitCode !== void 0 && exitCode !== 0) return labels.exitCode(exitCode);
}
/**
* Run-state indicator for the command, shown at the head of the prompt line so
* the card states whether the command is still running without the reader
* having to infer it from the presence of output. Three of {@link StateDotState}'s
* four states are reachable: the running chase (the same
* indicator a running tool row's leading icon uses, so the row and its card
* never disagree), green for a clean settle, red for a signal or a non-zero
* exit — the same status distinction {@link statusText} draws for the pill. A
* settled command whose exit status never reached the view counts as a clean
* settle: the view says it finished and says nothing went wrong.
* @param running - the command has not settled.
* @param exitCode - settled exit code, when known.
* @param signal - settled terminating signal name, when known.
* @param labels - display copy for the text label.
* @returns the dot's state and its text label, since the dot is aria-hidden.
*/
function runState(running, exitCode, signal, labels) {
	if (running) return {
		state: "ongoing",
		label: labels.running
	};
	if (statusText(exitCode, signal, labels) !== void 0) return {
		state: "error",
		label: labels.failed
	};
	return {
		state: "done",
		label: labels.done
	};
}
/**
* Render one parsed output line. Runs without SGR state render as bare text,
* so uncolored output carries no span wrappers.
* @param line - the line's styled runs.
* @returns the line's children.
*/
function renderLine(line) {
	return line.map((span, index) => span.style === void 0 ? span.text : jsx("span", {
		style: span.style,
		children: span.text
	}, index));
}
/**
* Render a shell command as a terminal surface.
* @param props - see {@link TerminalBlockProps}.
* @returns the terminal block element.
*/
function TerminalBlock({ command, cwd, home, output, exitCode, signal, running = false, maxLines = 16, className, labels }) {
	const copy = useMemo(() => labels === void 0 ? DEFAULT_LABELS : {
		...DEFAULT_LABELS,
		...labels
	}, [labels]);
	const text = output ?? "";
	const lines = useMemo(() => {
		const parsed = parseAnsiLines(text);
		const last = parsed[parsed.length - 1];
		return parsed.length > 1 && last !== void 0 && last.every((span) => span.text === "") ? parsed.slice(0, -1) : parsed;
	}, [text]);
	const [expanded, setExpanded] = useState(false);
	const { copied, onCopy } = useCopyFeedback(text);
	const onToggle = useCallback(() => {
		setExpanded((value) => !value);
	}, []);
	const status = statusText(exitCode, signal, copy);
	const state = runState(running, exitCode, signal, copy);
	const commandLines = useMemo(() => {
		return (command.endsWith("\n") ? command.slice(0, -1) : command).split("\n");
	}, [command]);
	const empty = lines.every((line) => line.every((span) => span.text.trim() === ""));
	const { hidden, capped, headLines, tailLines } = headTailCap(lines.length, maxLines, expanded);
	return jsxs("div", {
		className: clsx(css$14.block, className),
		"data-terminal": "",
		"data-running": running ? "" : void 0,
		children: [jsxs("div", {
			className: css$14.header,
			children: [
				jsxs("div", {
					className: css$14.prompt,
					children: [jsx("span", {
						className: css$14.runStateLabel,
						children: state.label
					}), commandLines.map((line, index) => jsxs("div", {
						className: css$14.promptLine,
						children: [
							index === 0 && jsx(StateDot, {
								state: state.state,
								className: css$14.runState
							}),
							jsx("span", {
								className: css$14.cwd,
								children: index > 0 || cwd === void 0 ? "$" : promptLabel(cwd, home)
							}),
							jsx("span", {
								className: css$14.command,
								children: line
							})
						]
					}, index))]
				}),
				status !== void 0 && jsx(Pill, {
					className: css$14.status,
					children: status
				}),
				!running && !empty && jsx("button", {
					type: "button",
					className: css$14.copyButton,
					onClick: onCopy,
					children: copied ? copy.copied : copy.copy
				})
			]
		}), !running && (empty ? jsx("div", {
			className: css$14.empty,
			children: copy.noOutput
		}) : jsxs("div", {
			className: css$14.output,
			children: [
				(capped ? lines.slice(0, headLines) : lines).map((line, index) => jsx("div", {
					className: css$14.line,
					children: renderLine(line)
				}, index)),
				hidden > 0 && jsx("button", {
					type: "button",
					className: css$14.expand,
					"aria-expanded": expanded,
					"aria-label": expanded ? copy.collapseAria : copy.expandAria(hidden),
					onClick: onToggle,
					children: expanded ? copy.collapse : copy.expand(hidden)
				}),
				capped && lines.slice(lines.length - tailLines).map((line, index) => jsx("div", {
					className: css$14.line,
					children: renderLine(line)
				}, index))
			]
		}))]
	});
}
//#endregion
//#region lib/types/markdown/highlight.js
/**
* The client's ONE syntax highlighter: a synchronous fine-grained shiki core
* (JavaScript regex engine — no oniguruma WASM, bundle-friendly) with an
* explicit grammar allowlist and a CSS-variables theme. Colors live in the
* theme package's token sheets as `--shiki-*` custom properties (light and
* dark blocks), never here — the repo's tokens-only styling rule.
*
* Only the three markdown-fence and `run_code` grammars (TypeScript, shell,
* JSON) load into the singleton at boot — the set every session renders. The
* read card's wider extension set (the file-extension language hints the read
* tool's `langFromPath` emits — `packages/fs/tool-fs`: python, rust, yaml,
* markup, …) is imported lazily and registered the first time such a language
* is requested, so a session that never opens a read card in one of those
* languages pays neither the ~1.6 MB of grammar modules nor their synchronous
* init. The first render of a lazy language falls back to plain text while its
* grammar loads, then {@link onGrammarLoaded} notifies subscribers to re-render
* with highlighting. An unknown or absent language falls back to plain text (no
* highlighting, still monospace) — never an error.
*/
/**
* Grammars the singleton loads at boot; each entry's own `name` is the id
* `codeToTokens`/`codeToHtml` resolve. The JS-family aliases (js/jsx/ts/tsx)
* resolve to the TypeScript grammar rather than a separate one: it tokenizes
* plain TS/JS exactly, and JSX/TSX approximately (shiki's TS grammar is not the
* dedicated TSX grammar, so JSX elements tokenize imperfectly) — an accepted
* trade to keep the boot set to one JS-family grammar. The read card's wider
* set loads lazily through {@link LAZY_GRAMMARS}.
*/
const LANGS = [
	langTs,
	langBash,
	langJson
];
/**
* The read card's extension grammars, each behind a dynamic import so its
* module stays out of the boot chunk until a read of that language renders.
* Keyed by the grammar id (`LanguageRegistration.name`) the aliases resolve to.
* `@shikijs/langs`' default export is a `LanguageRegistration[]`; the loader
* hands the whole array to `loadLanguageSync`, which registers each entry
* (including embedded sub-grammars). The three boot grammars are absent —
* already loaded, so no alias value ever points at a missing entry here.
*/
const LAZY_GRAMMARS = new Map([
	["python", () => import("@shikijs/langs/python")],
	["ruby", () => import("@shikijs/langs/ruby")],
	["go", () => import("@shikijs/langs/go")],
	["rust", () => import("@shikijs/langs/rust")],
	["java", () => import("@shikijs/langs/java")],
	["c", () => import("@shikijs/langs/c")],
	["cpp", () => import("@shikijs/langs/cpp")],
	["csharp", () => import("@shikijs/langs/csharp")],
	["kotlin", () => import("@shikijs/langs/kotlin")],
	["swift", () => import("@shikijs/langs/swift")],
	["php", () => import("@shikijs/langs/php")],
	["yaml", () => import("@shikijs/langs/yaml")],
	["toml", () => import("@shikijs/langs/toml")],
	["ini", () => import("@shikijs/langs/ini")],
	["markdown", () => import("@shikijs/langs/markdown")],
	["mdx", () => import("@shikijs/langs/mdx")],
	["html", () => import("@shikijs/langs/html")],
	["css", () => import("@shikijs/langs/css")],
	["scss", () => import("@shikijs/langs/scss")],
	["less", () => import("@shikijs/langs/less")],
	["sql", () => import("@shikijs/langs/sql")],
	["xml", () => import("@shikijs/langs/xml")],
	["lua", () => import("@shikijs/langs/lua")]
]);
/**
* Language ids (and aliases) the highlighter accepts; everything else renders
* plain. A Map, not an object: fence info strings are assistant-authored, so
* a label like `constructor` or `__proto__` must miss instead of resolving an
* inherited property and crashing the renderer inside shiki. Keys cover both
* the markdown-fence aliases `CodeBlock` uses and the file-extension hint ids
* the read tool's `langFromPath` emits, so both callers resolve the same
* grammars. The JS family maps to the TypeScript grammar (see {@link LANGS} for
* the JSX/TSX approximation). A value not in {@link LANGS} names a
* {@link LAZY_GRAMMARS} entry loaded on first use.
*/
const LANG_ALIASES = new Map([
	["typescript", "typescript"],
	["ts", "typescript"],
	["tsx", "typescript"],
	["javascript", "typescript"],
	["js", "typescript"],
	["jsx", "typescript"],
	["shellscript", "shellscript"],
	["bash", "shellscript"],
	["sh", "shellscript"],
	["shell", "shellscript"],
	["zsh", "shellscript"],
	["json", "json"],
	["jsonc", "json"],
	["py", "python"],
	["python", "python"],
	["rb", "ruby"],
	["ruby", "ruby"],
	["go", "go"],
	["rs", "rust"],
	["rust", "rust"],
	["java", "java"],
	["c", "c"],
	["cpp", "cpp"],
	["cs", "csharp"],
	["csharp", "csharp"],
	["kotlin", "kotlin"],
	["swift", "swift"],
	["php", "php"],
	["yaml", "yaml"],
	["yml", "yaml"],
	["toml", "toml"],
	["ini", "ini"],
	["md", "markdown"],
	["markdown", "markdown"],
	["mdx", "mdx"],
	["html", "html"],
	["css", "css"],
	["scss", "scss"],
	["less", "less"],
	["sql", "sql"],
	["xml", "xml"],
	["lua", "lua"]
]);
/** All token colors resolve through `--shiki-*` custom properties (theme package sheets). */
const cssVariablesTheme = createCssVariablesTheme({
	name: "css-variables",
	variablePrefix: "--shiki-",
	fontStyle: true
});
/**
* The client regex engine compiles each TextMate pattern when its scanner is
* created. Shiki otherwise defers patterns longer than 3,000 characters until
* their first match; that compilation counts against Shiki's 500 ms per-line
* budget and can return a partial token stream under host contention. Eager
* compilation leaves the same budget in place for scanning user content.
*/
const regexEngine = createJavaScriptRegexEngine({
	forgiving: true,
	regexConstructor: (pattern) => defaultJavaScriptRegexConstructor(pattern, { lazyCompileLength: Number.POSITIVE_INFINITY })
});
let singleton;
/** Representative paths through every boot grammar, compiled before user content is timed. */
const BOOT_GRAMMAR_WARMUPS = [
	{
		lang: "typescript",
		code: "const answer: number = 42"
	},
	{
		lang: "shellscript",
		code: "printf '%s\\n' \"$HOME\""
	},
	{
		lang: "json",
		code: "{\"ready\":true}"
	}
];
/** Construct and pre-tokenize the boot grammars outside the user-content scan budget. */
function createHighlighter() {
	const instance = createHighlighterCoreSync({
		themes: [cssVariablesTheme],
		langs: LANGS,
		engine: regexEngine
	});
	for (const sample of BOOT_GRAMMAR_WARMUPS) instance.codeToTokens(sample.code, {
		lang: sample.lang,
		theme: "css-variables",
		tokenizeTimeLimit: 0
	});
	return instance;
}
/** The synchronous highlighter (one instance per document); pre-warmed below, lazy as the fallback. */
function highlighter() {
	singleton ??= createHighlighter();
	return singleton;
}
/** Grammar ids whose lazy import is in flight or done, so it is requested once. */
const requested = /* @__PURE__ */ new Set();
/** Subscribers re-rendered after a lazy grammar registers (React callers). */
const listeners = /* @__PURE__ */ new Set();
/** Bumped on each lazy-grammar load; the `useSyncExternalStore` snapshot. */
let loadCount = 0;
/**
* Subscribe to lazy-grammar load completions; `listener` fires after a
* {@link LAZY_GRAMMARS} grammar finishes registering on the singleton, so a
* caller that rendered its plain fallback while the grammar loaded can
* re-highlight. Uses the `useSyncExternalStore` subscribe signature; pair it with
* {@link grammarLoadCount} as the snapshot. Returns an unsubscribe function.
* @param listener - invoked (no args) on each grammar-load completion.
* @returns a disposer that removes the listener.
*/
function subscribeGrammarLoaded(listener) {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}
/**
* The lazy-grammar load counter — a value that changes on every load, so a
* `useSyncExternalStore` snapshot re-renders the subscriber when a grammar
* registers. Opaque: only its identity across renders matters.
* @returns the current load count.
*/
function grammarLoadCount() {
	return loadCount;
}
/**
* Ensure the grammar `resolved` names is registered. A boot grammar (not in
* {@link LAZY_GRAMMARS}) and an already-loaded lazy grammar report ready
* synchronously; a lazy grammar not yet loaded starts its import (once) and
* reports not-ready, so the caller renders plain until a
* {@link subscribeGrammarLoaded} listener fires.
* @param resolved - the grammar id an alias resolved to.
* @returns whether the grammar is registered and ready to tokenize now.
*/
function ensureGrammar(resolved) {
	const load = LAZY_GRAMMARS.get(resolved);
	if (load === void 0) return true;
	if (highlighter().getLoadedLanguages().includes(resolved)) return true;
	if (!requested.has(resolved)) {
		requested.add(resolved);
		load().then((mod) => {
			highlighter().loadLanguageSync(mod.default);
			loadCount += 1;
			for (const listener of listeners) listener();
		});
	}
	return false;
}
setTimeout(() => {
	highlighter();
}, 0).unref?.();
/**
* Highlight `code` into shiki's HTML (a single `<pre class="shiki">` tree)
* when `lang` maps to a registered grammar; `undefined` means the caller
* renders its plain fallback. A lazy grammar not yet loaded returns `undefined`
* for this call and loads in the background; subscribe with
* {@link onGrammarLoaded} to re-highlight once it registers.
* @param code - the source text.
* @param lang - the language hint (a markdown fence info string or a fixed caller id).
* @returns the highlighted HTML, or `undefined` for unknown or not-yet-loaded languages.
*/
function highlightToHtml(code, lang) {
	const resolved = lang === void 0 ? void 0 : LANG_ALIASES.get(lang.toLowerCase());
	if (resolved === void 0) return void 0;
	if (!ensureGrammar(resolved)) return void 0;
	return highlighter().codeToHtml(code, {
		lang: resolved,
		theme: "css-variables"
	});
}
/**
* Tokenize `code` into per-line highlighted runs when `lang` maps to a
* registered grammar; `undefined` means the caller renders its plain fallback.
* A line-numbered view needs the token runs split per line (one gutter number
* per line), which the single-`<pre>` {@link highlightToHtml} does not expose,
* so this returns shiki's own 2D line/token structure narrowed to what a run
* renders. Each run's color is a `--shiki-*` custom property, keeping token
* colors on the theme package's sheets exactly as the HTML path does; the
* css-variables theme carries no font-style bits, matching that path's
* color-only output. The trailing newline shiki appends as a final empty line
* is dropped so the run count matches the caller's own line array.
* @param code - the source text.
* @param lang - the language hint (a file-extension-derived language id).
* @returns one entry per source line (each an array of runs), or `undefined` for unknown or not-yet-loaded languages.
*/
function highlightLines(code, lang) {
	const resolved = lang === void 0 ? void 0 : LANG_ALIASES.get(lang.toLowerCase());
	if (resolved === void 0) return void 0;
	if (!ensureGrammar(resolved)) return void 0;
	const { tokens } = highlighter().codeToTokens(code, {
		lang: resolved,
		theme: "css-variables"
	});
	const last = tokens[tokens.length - 1];
	return (tokens.length > 1 && last !== void 0 && last.length === 0 ? tokens.slice(0, -1) : tokens).map((line) => line.map((token) => ({
		text: token.content,
		style: { color: token.color }
	})));
}
//#endregion
//#region lib/types/ReadBlock.js
/**
* Content lines shown before the height cap collapses the middle. Matches
* TerminalBlock's default so a long read and a long command output cut at the
* same place in the same flow.
*/
const DEFAULT_READ_MAX_LINES = 16;
/**
* Render one line's highlighted runs. The css-variables theme colors every run,
* so each run is a styled span; a line with no highlighting at all takes the
* bare-text path in the caller instead (an unknown or absent language).
* @param spans - the line's styled runs.
* @returns the line's children.
*/
function renderSpans(spans) {
	return spans.map((span, index) => jsx("span", {
		style: span.style,
		children: span.text
	}, index));
}
/**
* Render a read tool result as a line-numbered, optionally syntax-highlighted
* file view.
* @param props - see {@link ReadBlockProps}.
* @returns the read block element.
*/
function ReadBlock({ label, lines, totalLines, lang, maxLines = 16, className }) {
	const raw = useMemo(() => lines.map((line) => line.text).join("\n"), [lines]);
	const highlighted = useMemo(() => highlightLines(raw, lang), [
		raw,
		lang,
		useSyncExternalStore(subscribeGrammarLoaded, grammarLoadCount, grammarLoadCount)
	]);
	const [expanded, setExpanded] = useState(false);
	const [copied, setCopied] = useState(false);
	const onCopy = useCallback(() => {
		if (copied) return;
		writeClipboard(raw).then((ok) => {
			if (!ok) return;
			setCopied(true);
			window.setTimeout(() => {
				setCopied(false);
			}, 1e3);
		});
	}, [copied, raw]);
	const onToggle = useCallback(() => {
		setExpanded((value) => !value);
	}, []);
	const hidden = lines.length - maxLines;
	const capped = hidden > 0 && !expanded;
	const headLines = Math.ceil(maxLines / 2);
	const tailLines = maxLines - headLines;
	const windowed = lines.length < totalLines;
	/**
	* Render a slice of the line array as gutter-numbered rows.
	* @param slice - the lines to draw, each with its aligned run array.
	* @returns the row elements.
	*/
	const rows = (slice) => slice.map(([line, spans]) => jsxs("div", {
		className: css$15.line,
		children: [jsx("span", {
			className: css$15.gutter,
			"aria-hidden": true,
			children: line.number
		}), jsx("span", {
			className: css$15.content,
			children: spans === void 0 ? line.text : renderSpans(spans)
		})]
	}, line.number));
	const paired = lines.map((line, index) => [line, highlighted?.[index]]);
	return jsxs("div", {
		className: clsx(css$15.block, className),
		"data-read": "",
		children: [jsxs("div", {
			className: css$15.banner,
			children: [jsx("div", {
				className: css$15.label,
				children: label ?? ""
			}), jsxs("div", {
				className: css$15.action,
				children: [
					windowed && jsx("span", {
						className: css$15.count,
						children: `显示 ${lines.length} / ${totalLines} 行`
					}),
					jsx("span", {
						className: css$15.lang,
						children: lang ?? ""
					}),
					lines.length > 0 && jsx("button", {
						type: "button",
						className: css$15.copyButton,
						onClick: onCopy,
						children: copied ? "复制成功" : "复制"
					})
				]
			})]
		}), jsxs("div", {
			className: css$15.body,
			children: [
				rows(capped ? paired.slice(0, headLines) : paired),
				hidden > 0 && jsx("button", {
					type: "button",
					className: css$15.expand,
					"aria-expanded": expanded,
					"aria-label": expanded ? "收起内容" : `展开其余 ${hidden} 行`,
					onClick: onToggle,
					children: expanded ? "收起" : `… 其余 ${hidden} 行`
				}),
				capped && rows(paired.slice(paired.length - tailLines))
			]
		})]
	});
}
//#endregion
//#region lib/types/DiffBlock.js
/**
* Output lines shown before the height cap collapses the middle. Matches
* {@link DEFAULT_TERMINAL_MAX_LINES} so a diff card and a terminal card cut a
* long body at the same place.
*/
const DEFAULT_DIFF_MAX_LINES = 16;
/** Local exhaustiveness helper — this package does not depend on `dsh-llm`. */
/* v8 ignore next 3 -- closed-union backstop; only reached if a row kind is forged */
function assertNever(value) {
	throw new Error(`unreachable diff row kind: ${String(value)}`);
}
/** The dim class per row kind (path/gap chrome vs the diff's own +/- colors). */
const ROW_CLASS = {
	path: css$16.path,
	del: css$16.del,
	add: css$16.add,
	gap: css$16.gap
};
/**
* Flatten the hunks into the body's rows plus the footer counts. A path header
* opens each new file; a same-file second hunk (a scattered edit) opens with a
* `⋯` gap instead of repeating the path. Every old-side line counts toward
* `removed` and every new-side line toward `added`. The file count is of
* DISTINCT paths, matching the TUI diff card's footer, so two hunks in one file
* read as `1 file` on both front ends.
* @param diffs - the hunks to render.
* @returns the body rows, the +/- totals, and the distinct-file count.
*/
function buildRows(diffs) {
	const rows = [];
	const paths = /* @__PURE__ */ new Set();
	let added = 0;
	let removed = 0;
	let prevPath;
	for (const diff of diffs) {
		paths.add(diff.path);
		if (diff.path !== prevPath) rows.push({
			kind: "path",
			text: diff.path
		});
		else rows.push({
			kind: "gap",
			text: "⋯"
		});
		prevPath = diff.path;
		if (diff.oldText !== null) for (const line of contentLines(diff.oldText)) {
			rows.push({
				kind: "del",
				text: line
			});
			removed++;
		}
		for (const line of contentLines(diff.newText)) {
			rows.push({
				kind: "add",
				text: line
			});
			added++;
		}
	}
	return {
		rows,
		added,
		removed,
		files: paths.size
	};
}
/**
* Split a side's text into its content lines. Empty text is zero lines (a full
* deletion's `newText` or a create's absent `oldText` side draws nothing), and a
* single trailing newline is a line terminator rather than an extra empty line —
* the same terminator rule TerminalBlock applies to command output. An interior
* blank line (a genuine `\n\n`) survives.
* @param text - the removed or added side's text.
* @returns the content lines, without the terminating newline.
*/
function contentLines(text) {
	if (text === "") return [];
	return (text.endsWith("\n") ? text.slice(0, -1) : text).split("\n");
}
/**
* The diff text a reader copies: each row's `-`/`+`/path/gap prefix and its
* content, exactly what the card shows. The removed and added blocks are the
* change; the path headers keep a multi-file copy attributable.
* @param rows - the flattened body rows.
* @returns the diff as plain text.
*/
function copyText$1(rows) {
	return rows.map((row) => {
		switch (row.kind) {
			case "del": return `- ${row.text}`;
			case "add": return `+ ${row.text}`;
			case "path": return row.text;
			case "gap": return row.text;
			/* v8 ignore next -- closed-union backstop; only reached if a row kind is forged */
			default: return assertNever(row.kind);
		}
	}).join("\n");
}
/**
* Render a file mutation as an inline diff surface.
* @param props - see {@link DiffBlockProps}.
* @returns the diff block element.
*/
function DiffBlock({ diffs, maxLines = 16, className }) {
	const { rows, added, removed, files } = useMemo(() => buildRows(diffs), [diffs]);
	const [expanded, setExpanded] = useState(false);
	const [copied, setCopied] = useState(false);
	const onCopy = useCallback(() => {
		if (copied) return;
		writeClipboard(copyText$1(rows)).then((ok) => {
			if (!ok) return;
			setCopied(true);
			window.setTimeout(() => {
				setCopied(false);
			}, 1e3);
		});
	}, [copied, rows]);
	const onToggle = useCallback(() => {
		setExpanded((value) => !value);
	}, []);
	if (rows.length === 0) return null;
	const hidden = rows.length - maxLines;
	const capped = hidden > 0 && !expanded;
	const headLines = Math.ceil(maxLines / 2);
	const tailLines = maxLines - headLines;
	const head = capped ? rows.slice(0, headLines) : rows;
	const tail = capped ? rows.slice(rows.length - tailLines) : [];
	return jsxs("div", {
		className: clsx(css$16.block, className),
		"data-diff": "",
		children: [
			jsx("button", {
				type: "button",
				className: css$16.copyButton,
				onClick: onCopy,
				children: copied ? "复制成功" : "复制"
			}),
			jsxs("div", {
				className: css$16.body,
				children: [
					head.map((row, index) => jsx("div", {
						className: clsx(css$16.line, ROW_CLASS[row.kind]),
						children: row.text
					}, index)),
					hidden > 0 && jsx("button", {
						type: "button",
						className: css$16.expand,
						"aria-expanded": expanded,
						"aria-label": expanded ? "收起差异" : `展开其余 ${hidden} 行差异`,
						onClick: onToggle,
						children: expanded ? "收起" : `… 其余 ${hidden} 行`
					}),
					tail.map((row, index) => jsx("div", {
						className: clsx(css$16.line, ROW_CLASS[row.kind]),
						children: row.text
					}, index))
				]
			}),
			jsxs("div", {
				className: css$16.footer,
				children: [
					"└ +",
					added,
					" -",
					removed,
					" · ",
					files,
					" file",
					files === 1 ? "" : "s"
				]
			})
		]
	});
}
//#endregion
//#region lib/types/SearchBlock.js
/**
* Result rows shown before the height cap collapses the middle. Matches
* {@link DEFAULT_TERMINAL_MAX_LINES} so a search card and a terminal card cut a
* long result at the same place.
*/
const DEFAULT_SEARCH_MAX_LINES = 16;
/**
* The plain-text form the copy control writes: the whole structured result
* regardless of the height cap or which groups are collapsed, so the clipboard
* carries the result rather than what the card happens to be showing.
* @param props - the card's props.
* @returns the copyable text, or the empty string for an empty result.
*/
function copyText(props) {
	if (props.kind === "paths") return props.paths.join("\n");
	return props.files.map((file) => [file.path, ...file.matches.map((m) => `${m.lineNumber}: ${m.line}`)].join("\n")).join("\n\n");
}
/**
* Number of retained results the card holds: the matched-line count across all
* files for a matches card, the path count for a paths card. This is the count
* the banner summary reports against `total` when the result was capped.
* @param props - the card's props.
* @returns the retained result count.
*/
function shownCount(props) {
	return props.kind === "paths" ? props.paths.length : props.files.reduce((sum, file) => sum + file.matches.length, 0);
}
/**
* The banner summary. When the search was capped it reads `显示 X / 共 N …` so
* the retained count and the pre-cap total sit in one clause (mirroring the read
* card's `显示 X / Y 行`); when it was not capped it is a plain count of what the
* card holds. The unit — `处匹配 · K 个文件` for grep, `个路径` for glob — trails
* the count either way.
* @param props - the card's props.
* @param shown - the retained result count from {@link shownCount}.
* @param truncated - whether the search was capped.
* @param total - the pre-cap total the truncation clause reports.
* @returns the summary text.
*/
function summaryText(props, shown, truncated, total) {
	const count = truncated ? `显示 ${shown} / 共 ${total}` : `${shown}`;
	return props.kind === "paths" ? `${count} 个路径` : `${count} 处匹配 · ${props.files.length} 个文件`;
}
/**
* Flatten a card's shape into its render rows, dropping a collapsed file
* group's match rows.
* @param props - the card's props.
* @param collapsed - the set of collapsed file-group indices (matches only).
* @returns the flattened rows in output order.
*/
function toRows(props, collapsed) {
	if (props.kind === "paths") return props.paths.map((path) => ({
		type: "path",
		path
	}));
	const rows = [];
	props.files.forEach((file, index) => {
		const isCollapsed = collapsed.has(index);
		rows.push({
			type: "file",
			path: file.path,
			count: file.matches.length,
			index,
			collapsed: isCollapsed
		});
		if (isCollapsed) return;
		for (const match of file.matches) rows.push({
			type: "match",
			lineNumber: match.lineNumber,
			line: match.line,
			key: `${index}:${match.lineNumber}`,
			fileIndex: index
		});
	});
	return rows;
}
/**
* A stable React key for a flattened render row: the group-scoped match key, a
* file-index-scoped header key, or the path itself. Rows of different types
* never collide, since each key carries its type prefix or the group index.
* @param row - the flattened row.
* @returns the key.
*/
function rowKey(row) {
	switch (row.type) {
		case "match": return `match:${row.key}`;
		case "file": return `file:${row.index}`;
		case "path": return `path:${row.path}`;
	}
}
/**
* Render a completed search as a grouped-matches or flat-path card.
* @param props - see {@link SearchBlockProps}.
* @returns the search block element.
*/
function SearchBlock(props) {
	const { truncated, total, maxLines = 16, className } = props;
	const [expanded, setExpanded] = useState(false);
	const [collapsed, setCollapsed] = useState(() => /* @__PURE__ */ new Set());
	const rows = toRows(props, collapsed);
	const shown = shownCount(props);
	const empty = rows.length === 0;
	const { copied, onCopy } = useCopyFeedback(copyText(props));
	const onToggle = useCallback(() => {
		setExpanded((value) => !value);
	}, []);
	const toggleFile = useCallback((index) => {
		setCollapsed((prev) => {
			const next = new Set(prev);
			if (next.has(index)) next.delete(index);
			else next.add(index);
			return next;
		});
	}, []);
	const { hidden, capped, headLines, tailLines } = headTailCap(rows.length, maxLines, expanded);
	const head = capped ? rows.slice(0, headLines) : rows;
	const naturalTail = capped ? rows.slice(rows.length - tailLines) : [];
	const tailLead = naturalTail[0];
	const tailHeader = tailLead?.type === "match" && !head.some((row) => row.type === "file" && row.index === tailLead.fileIndex) ? rows.find((row) => row.type === "file" && row.index === tailLead.fileIndex) : void 0;
	const tail = tailHeader === void 0 ? naturalTail : naturalTail.slice(1);
	const renderRow = (row) => {
		if (row.type === "path") return jsx("div", {
			className: css$17.line,
			children: row.path
		});
		if (row.type === "match") return jsxs("div", {
			className: css$17.line,
			children: [jsxs("span", {
				className: css$17.lineNumber,
				children: [row.lineNumber, ": "]
			}), row.line]
		});
		return jsxs("button", {
			type: "button",
			className: css$17.fileHeader,
			"aria-expanded": !row.collapsed,
			onClick: () => {
				toggleFile(row.index);
			},
			children: [jsx("span", {
				className: css$17.filePath,
				children: row.path
			}), jsx("span", {
				className: css$17.fileCount,
				children: row.count
			})]
		});
	};
	return jsxs("div", {
		className: clsx(css$17.block, className),
		"data-search": props.kind,
		children: [jsxs("div", {
			className: css$17.header,
			children: [jsx("span", {
				className: css$17.summary,
				children: summaryText(props, shown, truncated, total)
			}), !empty && jsx("button", {
				type: "button",
				className: css$17.copyButton,
				onClick: onCopy,
				children: copied ? "复制成功" : "复制"
			})]
		}), empty ? jsx("div", {
			className: css$17.empty,
			children: "无结果"
		}) : jsxs("div", {
			className: css$17.body,
			children: [
				head.map((row) => jsx("div", { children: renderRow(row) }, rowKey(row))),
				hidden > 0 && jsx("button", {
					type: "button",
					className: css$17.expand,
					"aria-expanded": expanded,
					"aria-label": expanded ? "收起结果" : `展开其余 ${hidden} 行结果`,
					onClick: onToggle,
					children: expanded ? "收起" : `… 其余 ${hidden} 行`
				}),
				tailHeader !== void 0 && jsx("div", { children: renderRow(tailHeader) }, `tailHeader:${rowKey(tailHeader)}`),
				tail.map((row) => jsx("div", { children: renderRow(row) }, rowKey(row)))
			]
		})]
	});
}
//#endregion
//#region lib/types/markdown/incremental.js
/**
* Incremental block-level markdown parsing for an append-only text stream.
*
* Re-parsing the whole accumulated document on every streaming chunk is
* quadratic in the final reply length. CommonMark block parsing is line-based
* and appended text can only reshape the parse frontier — the last top-level
* block (a paragraph becoming a setext heading or a table, a list continuing
* after a blank line, an unclosed fence swallowing lines) — so earlier blocks
* are final. This parser therefore freezes all but the trailing
* {@link UNSTABLE_TAIL_BLOCKS} blocks and re-parses only the source tail
* behind them: each source region is parsed O(1) times over the stream
* instead of once per chunk.
*
* The freeze boundary comes from the parser's own `position` offsets, never
* from custom source scanning. The cut sits at the *end offset* of the last
* frozen block (not the next block's start): a following block's start offset
* excludes up to three spaces of insignificant leading indentation, which is
* harmless to drop, but cutting at the previous end also keeps the
* inter-block blank lines in the tail so the sliced source stays verbatim.
*
* Known deviation, shared with any prefix-freeze scheme: micromark resolves
* reference-style links and footnotes document-wide at parse time, so a
* reference whose definition lands on the other side of the freeze boundary
* renders literally until the settled full parse self-heals it.
*/
/**
* Trailing blocks kept unstable. Appended text reshapes at most the last
* block; the second-to-last is retained as safety margin so a freeze decision
* never has to reason about the parse frontier.
*/
const UNSTABLE_TAIL_BLOCKS = 2;
/**
* A block's render key: its absolute source start offset. A position-less
* node (a grammar is free to omit positions) falls back to a negative
* list-index key — unique within one update's tail, which is the only place
* the fallback can occur: freezing requires the cut block's position, so a
* position-less parse keeps every block in the tail (real grammars always
* stamp positions and never take this path).
*/
function blockKey(node, base, index) {
	const offset = node.position?.start.offset;
	return offset === void 0 ? -(index + 1) : base + offset;
}
/**
* Append-only incremental parser over a caller-supplied grammar. One instance
* accumulates one streaming document; non-append input resets it.
*/
var IncrementalMarkdownParser = class {
	parse;
	prevText = "";
	tailStart = 0;
	frozen = [];
	generation = 0;
	cached = null;
	/** @param parse - Grammar shared with whatever renders the blocks, so boundaries agree. */
	constructor(parse) {
		this.parse = parse;
	}
	/**
	* Fold the current accumulated text and return the frozen/tail split.
	* Idempotent for identical input (the previous result is returned as-is),
	* so callers may invoke it from render paths that re-execute.
	* @param text - The full accumulated markdown source.
	* @returns Frozen and tail blocks with stream-stable render keys.
	*/
	update(text) {
		if (this.cached !== null && text === this.prevText) return this.cached;
		if (!text.startsWith(this.prevText)) {
			this.prevText = "";
			this.tailStart = 0;
			this.frozen = [];
			this.generation += 1;
		}
		this.prevText = text;
		const base = this.tailStart;
		const blocks = this.parse(text.slice(base)).children;
		let firstUnstable = Math.max(0, blocks.length - UNSTABLE_TAIL_BLOCKS);
		if (firstUnstable > 0) {
			const cutEnd = blocks[firstUnstable - 1]?.position?.end.offset;
			if (cutEnd === void 0) firstUnstable = 0;
			else {
				for (const node of blocks.slice(0, firstUnstable)) this.frozen.push({
					node,
					key: blockKey(node, base, this.frozen.length)
				});
				this.tailStart = base + cutEnd;
			}
		}
		const tail = blocks.slice(firstUnstable).map((node, index) => ({
			node,
			key: blockKey(node, base, index)
		}));
		this.cached = {
			frozen: [...this.frozen],
			tail,
			generation: this.generation
		};
		return this.cached;
	}
};
//#endregion
//#region lib/types/markdown/cjkFriendlyStrong.js
/** Let asterisk strong emphasis close after punctuation when CJK prose continues without whitespace. */
const cjkCharacter = new RegExp([
	"\\p{Script_Extensions=Han}",
	"\\p{Script_Extensions=Hiragana}",
	"\\p{Script_Extensions=Katakana}",
	"\\p{Script_Extensions=Hangul}",
	"\\p{Script_Extensions=Bopomofo}"
].join("|"), "u");
function isCjkCharacter(code) {
	return code !== null && code >= 0 && cjkCharacter.test(String.fromCodePoint(code));
}
const tokenizeCjkFriendlyAttention = function(effects, ok, nok) {
	const configuredAttentionMarkers = this.parser.constructs.attentionMarkers.null;
	if (configuredAttentionMarkers === void 0) throw new Error("micromark CommonMark attention markers are unavailable");
	const attentionMarkers = configuredAttentionMarkers;
	const previous = this.previous;
	const before = classifyCharacter(previous);
	let marker = codes.eof;
	return start;
	function start(code) {
		/* v8 ignore next -- this text construct is dispatched only for an asterisk. */
		if (code !== codes.asterisk) return nok(code);
		marker = code;
		effects.enter("attentionSequence");
		return inside(code);
	}
	function inside(code) {
		if (code === marker) {
			effects.consume(code);
			return inside;
		}
		const token = effects.exit("attentionSequence");
		const after = classifyCharacter(code);
		const open = !after || after === constants.characterGroupPunctuation && Boolean(before) || attentionMarkers.includes(code);
		const commonMarkClose = !before || before === constants.characterGroupPunctuation && Boolean(after) || attentionMarkers.includes(previous);
		const cjkStrongClose = token.end.offset - token.start.offset >= 2 && unicodePunctuation(previous) && isCjkCharacter(code);
		const close = commonMarkClose || cjkStrongClose;
		token._open = open;
		token._close = close;
		return ok(code);
	}
};
const cjkFriendlyAttention = {
	name: "cjkFriendlyAttention",
	resolveAll: attention.resolveAll,
	tokenize: tokenizeCjkFriendlyAttention
};
const cjkFriendlyStrongExtension = { text: { [codes.asterisk]: cjkFriendlyAttention } };
/**
* Extend CommonMark asterisk strong emphasis for punctuation-delimited CJK
* prose, as a micromark syntax extension for `fromMarkdown`.
* @returns The micromark syntax extension.
*/
function cjkFriendlyStrong() {
	return cjkFriendlyStrongExtension;
}
//#endregion
//#region lib/types/markdown/mathCompatibility.js
/** Extend upstream dollar-only math syntax with TeX delimiters while reusing its token vocabulary. */
const previousBackslash = function(code) {
	if (code !== codes.backslash) return true;
	const tail = this.events.at(-1);
	/* v8 ignore next -- a previous code necessarily has a preceding event. */
	if (tail === void 0) return false;
	return tail[1].type === types.characterEscape;
};
const tokenizeBackslashMathText = function(effects, ok, nok) {
	return start;
	function start(code) {
		/* v8 ignore next -- the text construct is dispatched only for a backslash. */
		if (code !== codes.backslash) return nok(code);
		effects.enter("mathText");
		effects.enter("mathTextSequence");
		effects.consume(code);
		return open;
	}
	function open(code) {
		if (code !== codes.leftParenthesis) return nok(code);
		effects.consume(code);
		effects.exit("mathTextSequence");
		return between;
	}
	function between(code) {
		if (code === codes.eof) return nok(code);
		if (code === codes.backslash) return effects.attempt({
			partial: true,
			tokenize: tokenizeClose
		}, close, afterCloseAttempt)(code);
		if (markdownLineEnding(code)) {
			effects.enter(types.lineEnding);
			effects.consume(code);
			effects.exit(types.lineEnding);
			return between;
		}
		return dataStart(code);
	}
	function afterCloseAttempt(code) {
		return effects.check({
			partial: true,
			tokenize: tokenizeOpen
		}, nok, dataStart)(code);
	}
	function dataStart(code) {
		effects.enter("mathTextData");
		effects.consume(code);
		return code === codes.backslash ? afterDataBackslash : data;
	}
	function afterDataBackslash(code) {
		if (code === codes.backslash) {
			effects.consume(code);
			return data;
		}
		return data(code);
	}
	function data(code) {
		if (code === codes.eof || code === codes.backslash || markdownLineEnding(code)) {
			effects.exit("mathTextData");
			return between(code);
		}
		effects.consume(code);
		return data;
	}
	function close(code) {
		effects.exit("mathText");
		return ok(code);
	}
	function tokenizeClose(closeEffects, closeOk, closeNok) {
		return slash;
		function slash(code) {
			/* v8 ignore next -- this partial construct is attempted only at a backslash. */
			if (code !== codes.backslash) return closeNok(code);
			closeEffects.enter("mathTextSequence");
			closeEffects.consume(code);
			return parenthesis;
		}
		function parenthesis(code) {
			if (code !== codes.rightParenthesis) return closeNok(code);
			closeEffects.consume(code);
			closeEffects.exit("mathTextSequence");
			return closeOk;
		}
	}
	function tokenizeOpen(openEffects, openOk, openNok) {
		return slash;
		function slash(code) {
			/* v8 ignore next -- the opening check follows a failed close attempt at a backslash. */
			if (code !== codes.backslash) return openNok(code);
			openEffects.enter(types.chunkString);
			openEffects.consume(code);
			return parenthesis;
		}
		function parenthesis(code) {
			if (code !== codes.leftParenthesis) return openNok(code);
			openEffects.consume(code);
			openEffects.exit(types.chunkString);
			return openOk;
		}
	}
};
function createMathFlow(marker, openMarker, closeMarker, multiline) {
	const tokenize = function(effects, ok, nok) {
		const self = this;
		let oddBackslashRun = false;
		const tail = self.events.at(-1);
		const initialSize = tail?.[1].type === types.linePrefix ? tail[2].sliceSerialize(tail[1], true).length : 0;
		return start;
		function start(code) {
			/* v8 ignore next -- the flow construct is dispatched only for its marker. */
			if (code !== marker) return nok(code);
			effects.enter("mathFlow");
			effects.enter("mathFlowFence");
			effects.enter("mathFlowFenceSequence");
			effects.consume(code);
			return open;
		}
		function open(code) {
			if (code !== openMarker) return nok(code);
			effects.consume(code);
			effects.exit("mathFlowFenceSequence");
			effects.exit("mathFlowFence");
			return marker === codes.dollarSign ? afterDollarOpen : content;
		}
		function afterDollarOpen(code) {
			return code === codes.dollarSign ? nok(code) : content(code);
		}
		function content(code) {
			if (code === codes.eof) return nok(code);
			if (code === marker && (marker !== codes.dollarSign || !oddBackslashRun)) return effects.attempt({
				partial: true,
				tokenize: tokenizeClosingFence
			}, closed, afterClosingFenceAttempt)(code);
			if (markdownLineEnding(code)) return multiline ? effects.attempt(nonLazyContinuation, afterContinuation, nok)(code) : nok(code);
			return valueStart(code);
		}
		function afterClosingFenceAttempt(code) {
			return marker === codes.backslash ? effects.check({
				partial: true,
				tokenize: tokenizeOpeningFence
			}, nok, markerValueStart)(code) : markerValueStart(code);
		}
		function afterContinuation(code) {
			return effects.attempt({
				partial: true,
				tokenize: tokenizeClosingFence
			}, closed, initialSize ? factorySpace(effects, content, types.linePrefix, initialSize + 1) : content)(code);
		}
		function valueStart(code) {
			effects.enter("mathFlowValue");
			oddBackslashRun = code === codes.backslash;
			effects.consume(code);
			return value;
		}
		function markerValueStart(code) {
			effects.enter("mathFlowValue");
			oddBackslashRun = false;
			effects.consume(code);
			return valueAfterMarker;
		}
		function valueAfterMarker(code) {
			if (code === marker) {
				effects.consume(code);
				return value;
			}
			return value(code);
		}
		function value(code) {
			if (code === codes.eof || code === marker || markdownLineEnding(code)) {
				effects.exit("mathFlowValue");
				return content(code);
			}
			oddBackslashRun = code === codes.backslash ? !oddBackslashRun : false;
			effects.consume(code);
			return value;
		}
		function closed(code) {
			effects.exit("mathFlow");
			return ok(code);
		}
		function tokenizeClosingFence(closeEffects, closeOk, closeNok) {
			return factorySpace(closeEffects, sequenceStart, types.linePrefix, constants.tabSize);
			function sequenceStart(code) {
				if (code !== marker) return closeNok(code);
				closeEffects.enter("mathFlowFence");
				closeEffects.enter("mathFlowFenceSequence");
				closeEffects.consume(code);
				return sequenceEnd;
			}
			function sequenceEnd(code) {
				if (code !== closeMarker) return closeNok(code);
				closeEffects.consume(code);
				closeEffects.exit("mathFlowFenceSequence");
				return factorySpace(closeEffects, after, types.whitespace);
			}
			function after(code) {
				if (code !== codes.eof && !markdownLineEnding(code)) return closeNok(code);
				closeEffects.exit("mathFlowFence");
				return closeOk(code);
			}
		}
		function tokenizeOpeningFence(openEffects, openOk, openNok) {
			return sequenceStart;
			function sequenceStart(code) {
				/* v8 ignore next -- the opening check follows a failed close attempt at the marker. */
				if (code !== marker) return openNok(code);
				openEffects.enter(types.chunkString);
				openEffects.consume(code);
				return sequenceEnd;
			}
			function sequenceEnd(code) {
				if (code !== openMarker) return openNok(code);
				openEffects.consume(code);
				openEffects.exit(types.chunkString);
				return openOk;
			}
		}
	};
	return {
		concrete: true,
		name: marker === codes.dollarSign ? "sameLineDollarMathFlow" : "backslashMathFlow",
		tokenize
	};
}
const tokenizeNonLazyContinuation = function(effects, ok, nok) {
	const self = this;
	return start;
	function start(code) {
		/* v8 ignore next -- continuation constructs are attempted only after a line ending. */
		if (code === codes.eof) return ok(code);
		/* v8 ignore next -- continuation constructs are attempted only after a line ending. */
		if (!markdownLineEnding(code)) return nok(code);
		effects.enter(types.lineEnding);
		effects.consume(code);
		effects.exit(types.lineEnding);
		return lineStart;
	}
	function lineStart(code) {
		return self.parser.lazy[self.now().line] ? nok(code) : ok(code);
	}
};
const nonLazyContinuation = {
	partial: true,
	tokenize: tokenizeNonLazyContinuation
};
const backslashMathText = {
	name: "backslashMathText",
	previous: previousBackslash,
	tokenize: tokenizeBackslashMathText
};
const backslashMathFlow = createMathFlow(codes.backslash, codes.leftSquareBracket, codes.rightSquareBracket, true);
const sameLineDollarMathFlow = createMathFlow(codes.dollarSign, codes.dollarSign, codes.dollarSign, false);
const backslashMath = {
	flow: {
		[codes.backslash]: backslashMathFlow,
		[codes.dollarSign]: sameLineDollarMathFlow
	},
	text: { [codes.backslash]: backslashMathText }
};
/**
* TeX backslash delimiters and same-line display-dollar blocks as a micromark
* syntax extension reusing `micromark-extension-math`'s token vocabulary; the
* caller must also register `math()` on the same parse so the emitted tokens
* compile to standard math nodes.
* @returns The micromark syntax extension.
*/
function mathCompatibility() {
	return backslashMath;
}
//#endregion
//#region lib/types/markdown/parse.js
/**
* The markdown renderer's two mdast grammars, one per rendering arm. Each
* arm is internally consistent — the incremental tail parses, the one-shot
* parses, and the plain-text projection of a given grammar always agree on
* where blocks start and end — and the settled grammar is the streaming one
* plus the math extensions, so the arms differ only where TeX delimiters
* begin a math construct (a `$$` block is a paragraph while streaming and a
* math block once settled, by design).
*/
/**
* Parse GFM markdown (the streaming arm's grammar: no math, so incomplete
* TeX never flashes KaTeX errors mid-stream).
* @param text - Markdown source.
* @returns The mdast root.
*/
function parseGfm(text) {
	return fromMarkdown(text, {
		extensions: [gfm(), cjkFriendlyStrong()],
		mdastExtensions: [gfmFromMarkdown()]
	});
}
/**
* Parse GFM markdown plus TeX math with the compatibility delimiters
* (the settled arm's grammar).
* @param text - Markdown source.
* @returns The mdast root.
*/
function parseGfmWithMath(text) {
	return fromMarkdown(text, {
		extensions: [
			gfm(),
			cjkFriendlyStrong(),
			mathCompatibility(),
			math()
		],
		mdastExtensions: [gfmFromMarkdown(), mathFromMarkdown()]
	});
}
//#endregion
//#region lib/types/markdown/CodeBlock.js
function CodeBlock({ code, lang, className, copyLabel = "复制", copiedLabel = "复制成功" }) {
	const trimmed = code.endsWith("\n") ? code.slice(0, -1) : code;
	const html = useMemo(() => highlightToHtml(trimmed, lang), [
		trimmed,
		lang,
		useSyncExternalStore(subscribeGrammarLoaded, grammarLoadCount, grammarLoadCount)
	]);
	const rootRef = useRef(null);
	const [copied, setCopied] = useState(false);
	const onCopy = useCallback(() => {
		if (copied) return;
		writeClipboard(rootRef.current?.querySelector("pre")?.textContent ?? trimmed).then((ok) => {
			if (!ok) return;
			setCopied(true);
			window.setTimeout(() => {
				setCopied(false);
			}, 1e3);
		});
	}, [copied, trimmed]);
	const body = html === void 0 ? jsx("pre", {
		className: css$18.plain,
		children: jsx("code", { children: trimmed })
	}) : jsx("div", { dangerouslySetInnerHTML: { __html: html } });
	return jsxs("div", {
		ref: rootRef,
		className: clsx(css$18.block, "md-code-block", className),
		children: [jsx("div", {
			className: css$18.bannerWrap,
			children: jsxs("div", {
				className: css$18.banner,
				children: [jsx("div", {
					className: css$18.infostring,
					children: lang ?? ""
				}), jsx("div", {
					className: css$18.action,
					children: jsx("button", {
						type: "button",
						className: css$18.copyButton,
						onClick: onCopy,
						children: copied ? copiedLabel : copyLabel
					})
				})]
			})
		}), body]
	});
}
//#endregion
//#region lib/types/markdown/katex.js
/**
* TeX-to-React via KaTeX, replicating the rehype-katex pipeline this renderer
* replaced: the same three-arm error chain (strict render, `strict: 'ignore'`
* retry, error span) and a DOM-identical element tree, so settled math keeps
* its exact markup. KaTeX emits an HTML string; the browser's own HTML parser
* (`DOMParser`, applying the spec's SVG/MathML foreign-content attribute
* adjustments KaTeX output relies on) turns it into a tree this module maps
* onto React elements — KaTeX output is a static span/MathML/SVG vocabulary
* with no raw user HTML, the same trust shiki's tree gets in CodeBlock.
*
* React 18 has no MathML support, so the `.katex-mathml` subtree's elements
* land in the HTML namespace — exactly as they did under the replaced
* hast-util-to-jsx-runtime pipeline. The visual arm is the `.katex-html`
* span tree; the MathML arm serves assistive technology, which reads it by
* tag name regardless of namespace.
*/
/**
* Convert one inline `style` attribute string into React's style object.
* KaTeX emits only plain kebab-case declarations (no custom properties and no
* nameless declarations), so camel-casing the property is the whole mapping.
*/
function styleObject(css) {
	const style = {};
	for (const declaration of css.split(";")) {
		const colon = declaration.indexOf(":");
		if (colon === -1) continue;
		const key = declaration.slice(0, colon).trim().replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
		style[key] = declaration.slice(colon + 1).trim();
	}
	return style;
}
/** Map one parsed DOM node onto a React element (text nodes pass through). */
function domToReact(node, key) {
	if (node.nodeType === Node.TEXT_NODE) return node.textContent;
	/* v8 ignore next 2 -- KaTeX output holds only elements and text; other
	node kinds cannot appear in its serialized vocabulary. */
	if (node.nodeType !== Node.ELEMENT_NODE) return null;
	const element = node;
	const props = { key };
	for (const attribute of element.attributes) if (attribute.name === "class") props["className"] = attribute.value;
	else if (attribute.name === "style") props["style"] = styleObject(attribute.value);
	else props[attribute.name] = attribute.value;
	const children = [...element.childNodes].map(domToReact);
	return children.length === 0 ? createElement(element.localName, props) : createElement(element.localName, props, ...children);
}
/**
* Render TeX source to React elements through KaTeX.
* @param value - The TeX source (math node value; fenced `math` blocks append
* their trailing newline to match the replaced pipeline's text extraction).
* @param displayMode - Display (block) versus inline rendering.
* @returns KaTeX's element tree, or the error span when the source does not
* parse (colored with KaTeX's stock `errorColor`, matching rehype-katex).
*/
function renderTexToReact(value, displayMode) {
	let html;
	try {
		html = katex.renderToString(value, {
			displayMode,
			throwOnError: true
		});
	} catch (error) {
		try {
			html = katex.renderToString(value, {
				displayMode,
				strict: "ignore",
				throwOnError: false
			});
		} catch {
			/* v8 ignore next 8 */
			return jsx("span", {
				className: "katex-error",
				style: { color: "#cc0000" },
				title: String(error),
				children: value
			});
		}
	}
	return [...new DOMParser().parseFromString(html, "text/html").body.childNodes].map(domToReact);
}
//#endregion
//#region lib/types/markdown/render.js
/**
* Direct mdast→React markdown renderer. Replaces the react-markdown /
* remark-rehype pipeline with one switch over parsed nodes so streaming can
* cache frozen blocks as React elements; the rendered DOM is pinned
* byte-for-byte by `tests/fixtures/markdown-dom` and must not drift.
*
* Untrusted-output policy (unchanged from the replaced pipeline): link and
* image destinations pass a protocol allowlist, images additionally require
* absolute HTTP(S), raw HTML renders as literal text (no HTML enters the
* DOM), and KaTeX runs without trusted commands. Fragment-anchor URLs fail
* the allowlist, so footnote references and back-references render as plain
* text rather than in-page links.
*
* Merge-extensible node unions fall through the documented default (render
* nothing) rather than ending in assertNever: grammars registered elsewhere
* may add node types this renderer has no mapping for.
*/
function sanitizeUrl(url) {
	try {
		switch (new URL(url).protocol) {
			case "http:":
			case "https:":
			case "mailto:": return url;
			default: return "";
		}
	} catch {
		return "";
	}
}
function remoteImageUrl(url) {
	try {
		const protocol = new URL(url).protocol;
		return protocol === "http:" || protocol === "https:" ? url : void 0;
	} catch {
		return;
	}
}
/**
* Create an empty {@link ReferenceTargets}.
* @returns Fresh empty maps.
*/
function createReferenceTargets() {
	return {
		definitions: /* @__PURE__ */ new Map(),
		footnotes: /* @__PURE__ */ new Map()
	};
}
/**
* Record every definition and footnote definition under `nodes` into
* `targets`, depth-first, keeping the first definition per identifier.
* @param nodes - Subtrees to walk (top-level blocks or any nested children).
* @param targets - Accumulator, typically shared across incremental segments.
*/
function collectReferenceTargets(nodes, targets) {
	for (const node of nodes) {
		if (node.type === "definition") {
			const id = node.identifier.toUpperCase();
			if (!targets.definitions.has(id)) targets.definitions.set(id, node);
		} else if (node.type === "footnoteDefinition") {
			const id = node.identifier.toUpperCase();
			if (!targets.footnotes.has(id)) targets.footnotes.set(id, node);
		}
		if ("children" in node) collectReferenceTargets(node.children, targets);
	}
}
/**
* Render top-level blocks. Nodes that render nothing (definitions, unmapped
* types) are dropped rather than kept as null placeholders, matching the
* replaced pipeline's child lists so separator newlines land identically.
* @param blocks - Blocks with their stream-stable render keys.
* @param context - The pass state; footnote numbering mutates in document order.
* @returns One React node per rendered block.
*/
function renderBlocks(blocks, context) {
	return blocks.map((block) => renderNode(block.node, block.key, context)).filter((element) => element !== null);
}
/**
* Interleave the newline text nodes the replaced pipeline emitted between
* block-level children. They are invisible between elements but coalesce
* into adjacent literal raw-HTML text, where the DOM parity fixtures pin
* them.
* @param elements - Rendered block children with empty renders already dropped.
* @param edges - Also emit the leading and trailing newline (hast's loose wrap).
* @returns The interleaved children.
*/
function wrapBlockChildren(elements, edges) {
	const wrapped = [];
	for (const element of elements) {
		if (edges || wrapped.length > 0) wrapped.push("\n");
		wrapped.push(element);
	}
	if (edges && elements.length > 0) wrapped.push("\n");
	return wrapped;
}
/** Render container children into {@link BlockEntry} values, dropping empty renders. */
function renderBlockEntries(blocks, context) {
	const entries = [];
	for (const [index, block] of blocks.entries()) if (block.type === "paragraph") entries.push({ paragraph: renderChildren(block.children, context) });
	else {
		const element = renderNode(block, index, context);
		if (element !== null) entries.push({ element });
	}
	return entries;
}
function renderChildren(nodes, context) {
	return nodes.map((node, index) => renderNode(node, index, context));
}
function renderNode(node, key, context) {
	switch (node.type) {
		case "text": return node.value;
		case "paragraph": return jsx("p", { children: renderChildren(node.children, context) }, key);
		case "heading": return createElement(`h${node.depth}`, { key }, ...renderChildren(node.children, context));
		case "blockquote": return jsx("blockquote", { children: wrapBlockChildren(renderChildren(node.children, {
			...context,
			inBlockquote: true
		}).filter((child) => child !== null), true) }, key);
		case "thematicBreak": return jsx("hr", {}, key);
		case "break": return jsxs(Fragment$1, { children: [jsx("br", {}), "\n"] }, key);
		case "strong": return jsx("strong", { children: renderChildren(node.children, context) }, key);
		case "emphasis": return jsx("em", { children: renderChildren(node.children, context) }, key);
		case "delete": return jsx("del", { children: renderChildren(node.children, context) }, key);
		case "inlineCode": {
			const value = node.value.replace(/\r?\n|\r/g, " ");
			const href = inlineCodeHttpUrl(value);
			if (href !== void 0) return jsx("code", { children: renderSafeLink(href, [value], "link") }, key);
			const mention = context.inLink === true ? void 0 : context.fileMentions?.resolve(value);
			if (mention !== void 0) return jsx("code", { children: jsx("button", {
				type: "button",
				className: css$19.fileMention,
				title: mention.title,
				"aria-label": mention.label,
				onClick: mention.open,
				children: value
			}) }, key);
			return jsx("code", { children: value }, key);
		}
		case "html": return node.value;
		case "code": return renderCode(node, key, context);
		case "math": return jsx(Fragment$1, { children: renderTexToReact(node.value, true) }, key);
		case "inlineMath": return jsx(Fragment$1, { children: renderTexToReact(node.value, false) }, key);
		case "list": return renderList(node, key, context);
		case "listItem": return renderListItem(node, listItemLoose(node), key, context);
		case "table": return renderTable(node, key, context);
		case "link": return renderAnchor(node.url, renderChildren(node.children, {
			...context,
			inLink: true
		}), key);
		case "linkReference": return renderLinkReference(node, key, context);
		case "image": return renderImage(node.url, node.alt ?? "", key);
		case "imageReference": return renderImageReference(node, key, context);
		case "footnoteReference": return renderFootnoteReference(node, key, context);
		case "definition":
		case "footnoteDefinition": return null;
		default: return null;
	}
}
function renderCode(node, key, context) {
	const language = node.lang ?? void 0;
	if (node.value === "") return jsx("pre", { children: jsx("code", { className: language === void 0 ? void 0 : `language-${language}` }) }, key);
	const lang = language === void 0 ? void 0 : /^[\w-]+/.exec(language)?.[0];
	if (!context.streaming && lang === "math") return jsx(Fragment$1, { children: renderTexToReact(`${node.value}\n`, true) }, key);
	return jsx(CodeBlock, {
		code: `${node.value}\n`,
		lang: context.streaming ? void 0 : lang,
		copyLabel: context.codeLabels?.copyLabel,
		copiedLabel: context.codeLabels?.copiedLabel
	}, key);
}
/** A list is loose when it or any of its items is spread; every item then keeps its paragraphs. */
function listLoose(list) {
	return (list.spread ?? false) || list.children.some(listItemLoose);
}
function listItemLoose(item) {
	return item.spread ?? item.children.length > 1;
}
function renderList(node, key, context) {
	const loose = listLoose(node);
	const properties = {};
	if (typeof node.start === "number" && node.start !== 1) properties.start = node.start;
	if (node.children.some((item) => typeof item.checked === "boolean")) properties.className = "contains-task-list";
	return createElement(node.ordered === true ? "ol" : "ul", {
		key,
		...properties
	}, ...node.children.map((item, index) => renderListItem(item, loose, index, context)));
}
function renderListItem(item, loose, key, context) {
	const entries = renderBlockEntries(item.children, context);
	const task = typeof item.checked === "boolean";
	if (task) {
		const checkbox = jsx("input", {
			type: "checkbox",
			checked: item.checked === true,
			disabled: true
		}, "task-checkbox");
		const head = entries[0];
		if (head !== void 0 && "paragraph" in head) head.paragraph = head.paragraph.length > 0 ? [
			checkbox,
			" ",
			...head.paragraph
		] : [checkbox];
		else entries.unshift({ paragraph: [checkbox] });
	}
	const parts = [];
	for (const [index, entry] of entries.entries()) {
		const isParagraph = "paragraph" in entry;
		if (loose || index !== 0 || !isParagraph) parts.push("\n");
		if (!isParagraph) parts.push(entry.element);
		else if (loose) parts.push(jsx("p", { children: entry.paragraph }, `p-${index}`));
		else parts.push(jsx(Fragment$1, { children: entry.paragraph }, `p-${index}`));
	}
	const tail = entries[entries.length - 1];
	if (tail !== void 0 && (loose || !("paragraph" in tail))) parts.push("\n");
	return jsx("li", {
		className: task ? "task-list-item" : void 0,
		children: parts
	}, key);
}
function renderTable(node, key, context) {
	const align = node.align ?? null;
	const [headRow, ...bodyRows] = node.children;
	const wide = (align === null ? headRow?.children.length ?? 0 : align.length) >= 4 && context.inBlockquote !== true;
	return jsx("div", {
		className: clsx(css$19.tableScroll, wide ? "md-table-wide" : css$19.tableFill),
		tabIndex: wide ? 0 : void 0,
		children: jsxs("table", { children: [headRow !== void 0 && jsx("thead", { children: renderTableRow(headRow, "th", align, 0, context) }), bodyRows.length > 0 && jsx("tbody", { children: bodyRows.map((row, index) => renderTableRow(row, "td", align, index + 1, context)) })] })
	}, key);
}
function renderTableRow(row, cellTag, align, key, context) {
	const length = align === null ? row.children.length : align.length;
	const cells = [];
	for (let index = 0; index < length; index++) {
		const cell = row.children[index];
		const alignValue = align?.[index];
		cells.push(createElement(cellTag, {
			key: index,
			style: alignValue == null ? void 0 : { textAlign: alignValue }
		}, ...cell === void 0 ? [] : renderChildren(cell.children, context)));
	}
	return jsx("tr", { children: cells }, key);
}
/** Anchor over an already-authored href: allowlisted or unwrapped, external links get the safe attributes. */
function renderSafeLink(href, children, key) {
	const safeHref = sanitizeUrl(href);
	if (safeHref === "") return jsx(Fragment$1, { children }, key);
	return jsx("a", {
		href: safeHref,
		...["http:", "https:"].includes(new URL(safeHref).protocol) ? {
			target: "_blank",
			rel: "noopener noreferrer"
		} : {},
		children
	}, key);
}
/** Anchor over a parsed markdown destination, which hast normalized before the allowlist saw it. */
function renderAnchor(url, children, key) {
	return renderSafeLink(normalizeUri(url), children, key);
}
/**
* The complete inline-code value when it is exactly an absolute HTTP(S) URL
* (no surrounding whitespace); anything else stays inert code.
*/
function inlineCodeHttpUrl(value) {
	if (value.trim() !== value) return void 0;
	try {
		const protocol = new URL(value).protocol;
		return protocol === "http:" || protocol === "https:" ? value : void 0;
	} catch {
		return;
	}
}
function renderImage(url, alt, key) {
	const imageSrc = remoteImageUrl(sanitizeUrl(normalizeUri(url)));
	if (imageSrc === void 0) return jsx("span", {
		className: css$19.imageAlt,
		children: alt
	}, key);
	return jsx("img", {
		className: css$19.image,
		src: imageSrc,
		alt,
		loading: "lazy",
		decoding: "async",
		referrerPolicy: "no-referrer"
	}, key);
}
/** The bracketed source text a reference reverts to when its definition is missing. */
function referenceSuffix(node) {
	if (node.referenceType === "collapsed") return "][]";
	if (node.referenceType === "full") return `][${node.label ?? node.identifier}]`;
	return "]";
}
function renderLinkReference(node, key, context) {
	const definition = context.targets.definitions.get(node.identifier.toUpperCase());
	if (definition === void 0) return jsxs(Fragment$1, { children: [
		"[",
		renderChildren(node.children, context),
		referenceSuffix(node)
	] }, key);
	return renderAnchor(definition.url, renderChildren(node.children, {
		...context,
		inLink: true
	}), key);
}
function renderImageReference(node, key, context) {
	const definition = context.targets.definitions.get(node.identifier.toUpperCase());
	if (definition === void 0) return `![${node.alt ?? ""}${referenceSuffix(node)}`;
	return renderImage(definition.url, node.alt ?? "", key);
}
function renderFootnoteReference(node, key, context) {
	const id = node.identifier.toUpperCase();
	const seen = context.footnoteCounts.get(id);
	if (seen === void 0) context.footnoteOrder.push(id);
	context.footnoteCounts.set(id, (seen ?? 0) + 1);
	return jsx("sup", { children: String(context.footnoteOrder.indexOf(id) + 1) }, key);
}
/**
* Render the trailing footnote section for every footnote referenced during
* the pass, in first-reference order, with one plain-text back-reference
* marker per rendered reference.
* @param context - The pass state after all blocks rendered.
* @returns The section, or null when no referenced footnote has a definition.
*/
function renderFootnoteSection(context) {
	const items = [];
	for (const id of context.footnoteOrder) {
		const definition = context.targets.footnotes.get(id);
		if (definition === void 0) continue;
		const count = context.footnoteCounts.get(id) ?? 0;
		const backrefs = [];
		for (let reference = 1; reference <= count; reference++) {
			if (backrefs.length > 0) backrefs.push(" ");
			backrefs.push("↩");
			if (reference > 1) backrefs.push(jsx("sup", { children: String(reference) }, `re-${reference}`));
		}
		const entries = renderBlockEntries(definition.children, context);
		const tail = entries[entries.length - 1];
		const body = entries.map((entry, index) => "paragraph" in entry ? jsxs("p", { children: [entry.paragraph, entry === tail && jsxs(Fragment, { children: [" ", backrefs] })] }, `p-${index}`) : entry.element);
		if (tail === void 0 || !("paragraph" in tail)) body.push(...backrefs);
		items.push(jsx("li", {
			id: `user-content-fn-${normalizeUri(id.toLowerCase())}`,
			children: wrapBlockChildren(body, true)
		}, id));
	}
	if (items.length === 0) return null;
	return jsxs("section", {
		"data-footnotes": true,
		className: "footnotes",
		children: [jsx("h2", {
			id: "footnote-label",
			className: "sr-only",
			children: "Footnotes"
		}), jsx("ol", { children: items })]
	}, "footnotes");
}
//#endregion
//#region lib/types/markdown/MarkdownText.js
/**
* Untrusted assistant-Markdown renderer over the direct mdast pipeline:
* `parse.ts` grammars, the incremental streaming parser, and `render.tsx`.
* While a message streams, all but the trailing two blocks freeze as cached
* React elements and only the source tail behind them re-parses per chunk,
* so per-chunk work tracks the tail size instead of the whole reply. Frozen
* blocks keep their source-offset keys when they cross the freeze boundary,
* so React reconciles instead of remounting. Known deviation while
* streaming: a reference-style link or footnote whose definition sits on the
* other side of the freeze boundary renders literally until the settled
* full parse self-heals it.
*/
/** One settled full render: parse with math, resolve references, append the footnote section. */
function renderSettled(text, codeLabels, fileMentions) {
	const root = parseGfmWithMath(text);
	const targets = createReferenceTargets();
	collectReferenceTargets(root.children, targets);
	const context = {
		streaming: false,
		codeLabels,
		fileMentions,
		targets,
		footnoteOrder: [],
		footnoteCounts: /* @__PURE__ */ new Map()
	};
	const blocks = wrapBlockChildren(renderBlocks(root.children.map((node, index) => ({
		node,
		key: index
	})), context), false);
	const section = renderFootnoteSection(context);
	return section === null ? blocks : [
		...blocks,
		"\n",
		section
	];
}
/**
* Streaming render state for one growing message: the incremental parser,
* the frozen blocks' cached elements, and the reference/footnote state their
* rendering consumed (footnote numbering assigned to frozen references is
* final, so the tail continues from a copy of it each frame).
*/
var StreamingRenderer = class {
	codeLabels;
	parser = new IncrementalMarkdownParser(parseGfm);
	generation = -1;
	frozenCount = 0;
	frozenElements = [];
	frozenTargets = createReferenceTargets();
	frozenFootnoteOrder = [];
	frozenFootnoteCounts = /* @__PURE__ */ new Map();
	lastText = null;
	lastRendered = [];
	/** @param codeLabels - Fence copy labels baked into cached elements; the owner replaces the renderer when they change. */
	constructor(codeLabels) {
		this.codeLabels = codeLabels;
	}
	/**
	* Render the current accumulated text. Idempotent per text value, so React
	* may re-execute the calling render freely.
	* @param text - The full accumulated markdown source.
	* @returns Frozen elements, re-rendered tail, and the footnote section.
	*/
	render(text) {
		if (text === this.lastText) return this.lastRendered;
		const { frozen, tail, generation } = this.parser.update(text);
		if (generation !== this.generation) {
			this.generation = generation;
			this.frozenCount = 0;
			this.frozenElements = [];
			this.frozenTargets = createReferenceTargets();
			this.frozenFootnoteOrder = [];
			this.frozenFootnoteCounts = /* @__PURE__ */ new Map();
		}
		const newlyFrozen = frozen.slice(this.frozenCount);
		collectReferenceTargets(newlyFrozen.map((block) => block.node), this.frozenTargets);
		const frameTargets = {
			definitions: new Map(this.frozenTargets.definitions),
			footnotes: new Map(this.frozenTargets.footnotes)
		};
		collectReferenceTargets(tail.map((block) => block.node), frameTargets);
		if (newlyFrozen.length > 0) {
			const frozenContext = {
				streaming: true,
				codeLabels: this.codeLabels,
				fileMentions: void 0,
				targets: frameTargets,
				footnoteOrder: this.frozenFootnoteOrder,
				footnoteCounts: this.frozenFootnoteCounts
			};
			const batch = [...this.frozenElements];
			for (const element of renderBlocks(newlyFrozen, frozenContext)) {
				if (batch.length > 0) batch.push("\n");
				batch.push(element);
			}
			this.frozenElements = batch;
			this.frozenCount = frozen.length;
		}
		const tailContext = {
			streaming: true,
			codeLabels: this.codeLabels,
			fileMentions: void 0,
			targets: frameTargets,
			footnoteOrder: [...this.frozenFootnoteOrder],
			footnoteCounts: new Map(this.frozenFootnoteCounts)
		};
		const children = [...this.frozenElements];
		for (const element of renderBlocks(tail, tailContext)) {
			if (children.length > 0) children.push("\n");
			children.push(element);
		}
		const section = renderFootnoteSection(tailContext);
		if (section !== null) children.push("\n", section);
		this.lastText = text;
		this.lastRendered = children;
		return this.lastRendered;
	}
};
/**
* Render untrusted assistant-authored Markdown as semantic React elements.
* @param props - Markdown source text preserved by the session projection;
* `streaming` renders fences and TeX plain (highlighting and KaTeX land on
* the finalize swap) and parses incrementally across chunks; `codeLabels`
* forwards localized copy-button labels to fence CodeBlocks — pass a
* reference-stable object (memoized per locale revision), because a new
* identity discards the streaming render cache mid-message. `fileMentions`
* links inline-code tokens its resolver recognizes as real files; this is
* the single streaming gate — it applies to settled renders only, because a
* streaming message's vocabulary is not final and frozen cached elements
* must not bake in handlers that could go stale.
* @returns A GFM document with TeX math rendered through KaTeX; raw HTML,
* relative links, and unsafe protocols are disabled, while absolute HTTP(S)
* images render directly.
*/
const MarkdownText = memo(function MarkdownText({ text, streaming = false, codeLabels, fileMentions }) {
	const streamRef = useRef(null);
	const streamLabelsRef = useRef(codeLabels);
	const children = useMemo(() => {
		if (!streaming) {
			streamRef.current = null;
			return renderSettled(text, codeLabels, fileMentions);
		}
		if (streamRef.current === null || streamLabelsRef.current !== codeLabels) {
			streamRef.current = new StreamingRenderer(codeLabels);
			streamLabelsRef.current = codeLabels;
		}
		return streamRef.current.render(text);
	}, [
		text,
		streaming,
		codeLabels,
		fileMentions
	]);
	return jsx("div", {
		className: css$19.markdown,
		children
	});
});
//#endregion
//#region lib/types/WebBlock.js
/**
* The URL to link to, or undefined when the URL must render as plain text. Only
* http(s) becomes a navigable external anchor, so a `javascript:`/`data:`/`file:`
* URL or an unparseable string never reaches the DOM as an href. This is the
* http(s) subset of the allowlist MarkdownText applies to untrusted links —
* MarkdownText also permits `mailto:`, deliberately excluded here since a
* retrieval URL is never a mail address.
* @param url - the source or fetch URL, from tool result content.
* @returns the href to use, or undefined for plain text.
*/
function safeHref(url) {
	try {
		const { protocol } = new URL(url);
		return protocol === "http:" || protocol === "https:" ? url : void 0;
	} catch {
		return;
	}
}
/**
* The link's visible label: the title when the provider gave one, otherwise the
* URL's hostname, falling back to the raw URL when it does not parse OR parses
* to an empty hostname (a `file:`/`data:`/`javascript:` URL), so a label is
* never blank.
* @param url - the source URL.
* @param title - the provider title, if any.
* @returns the label text.
*/
function linkLabel(url, title) {
	if (title !== void 0 && title !== "") return title;
	try {
		const { hostname } = new URL(url);
		return hostname === "" ? url : hostname;
	} catch {
		return url;
	}
}
/**
* A single URL rendered as a safe external anchor, or as plain text when the
* URL is not an http(s) link.
* @param props.url - the URL to render.
* @param props.label - the visible label.
* @param props.className - class for the anchor or the plain span.
* @returns the anchor or span element.
*/
function SafeLink({ url, label, className }) {
	const href = safeHref(url);
	if (href === void 0) return jsx("span", {
		className,
		children: label
	});
	return jsx("a", {
		className,
		href,
		target: "_blank",
		rel: "noopener noreferrer",
		children: label
	});
}
/**
* One source row in a search card: the safe link plus its snippet and date. The
* `<li value>` pins the source's 1-based citation index explicitly rather than
* relying on the `<ol>`'s implicit numbering, so a row reads by its real index
* even inside the scroll container.
* @param props.source - the source to render.
* @param props.ordinal - the source's 1-based position in the full list.
* @returns the source list item.
*/
function SourceItem({ source, ordinal }) {
	return jsxs("li", {
		className: css$20.source,
		value: ordinal,
		children: [
			jsx(SafeLink, {
				url: source.url,
				label: linkLabel(source.url, source.title),
				className: css$20.sourceLink
			}),
			source.snippet !== void 0 && source.snippet !== "" && jsx("div", {
				className: css$20.snippet,
				children: source.snippet
			}),
			source.publishedAt !== void 0 && source.publishedAt !== "" && jsx("div", {
				className: css$20.published,
				children: source.publishedAt
			})
		]
	});
}
/**
* The search card body: the answer over the full source list, which scrolls in
* place once it exceeds the `.sources` container height.
* @param props - see {@link WebSearchBlockProps}.
* @returns the search card element.
*/
function WebSearchBlock({ answer, sources, truncated, className }) {
	const empty = (answer === void 0 || answer === "") && sources.length === 0;
	return jsxs("div", {
		className: clsx(css$20.block, className),
		"data-web": "search",
		children: [
			answer !== void 0 && answer !== "" && jsx("div", {
				className: css$20.answer,
				children: jsx(MarkdownText, { text: answer })
			}),
			empty ? jsx("div", {
				className: css$20.empty,
				children: "未找到结果"
			}) : jsx("ol", {
				className: css$20.sources,
				children: sources.map((source, index) => jsx(SourceItem, {
					source,
					ordinal: index + 1
				}, index))
			}),
			truncated && jsx("div", {
				className: css$20.truncated,
				children: "来源列表已截断"
			})
		]
	});
}
/**
* The fetch card body: the linked URL and its HTTP status.
* @param props - see {@link WebFetchBlockProps}.
* @returns the fetch card element.
*/
function WebFetchBlock({ url, statusCode, truncated, className }) {
	return jsxs("div", {
		className: clsx(css$20.block, css$20.fetch, className),
		"data-web": "fetch",
		children: [jsx(SafeLink, {
			url,
			label: url,
			className: css$20.fetchUrl
		}), jsxs("div", {
			className: css$20.fetchMeta,
			children: [jsxs("span", {
				className: css$20.status,
				children: ["HTTP ", statusCode]
			}), truncated && jsx("span", {
				className: css$20.truncated,
				children: "内容已截断"
			})]
		})]
	});
}
/**
* Render a completed web retrieval as a structured card.
* @param props - see {@link WebBlockProps}; `kind` selects the search or fetch body.
* @returns the web card element.
*/
function WebBlock(props) {
	return props.kind === "search" ? jsx(WebSearchBlock, { ...props }) : jsx(WebFetchBlock, { ...props });
}
//#endregion
//#region lib/types/markdown/JsonBlock.js
const MAX_CHARS = 2e4;
/** Default truncation footer; the owner passes a localized formatter. */
function defaultTruncatedLabel(total) {
	return `… 已截断，共 ${total} 字符`;
}
function JsonBlock({ label, payload, defaultOpen = false, truncatedLabel = defaultTruncatedLabel }) {
	const [open, setOpen] = useState(defaultOpen);
	const body = useMemo(() => {
		if (!open) return "";
		let s;
		try {
			s = JSON.stringify(payload, null, 2) ?? String(payload);
		} catch {
			s = String(payload);
		}
		return s.length > MAX_CHARS ? `${s.slice(0, MAX_CHARS)}\n${truncatedLabel(s.length)}` : s;
	}, [
		open,
		payload,
		truncatedLabel
	]);
	return jsxs("div", {
		className: css$21.root,
		children: [jsxs("button", {
			type: "button",
			className: css$21.toggle,
			onClick: () => {
				setOpen((v) => !v);
			},
			children: [
				open ? "▾" : "▸",
				" ",
				label
			]
		}), open && jsx("pre", {
			className: css$21.body,
			children: body
		})]
	});
}
//#endregion
//#region lib/types/markdown/MessageText.js
function MessageText({ text }) {
	return jsx("div", {
		className: css$22.text,
		children: text
	});
}
//#endregion
//#region lib/types/markdown/plain-text.js
/**
* Markdown-to-plain-text projection for compact summaries and labels.
* Parsing shares the renderer's streaming GFM grammar ({@link parseGfm}), so
* the projection strips exactly the markup the renderer would draw; raw HTML
* stays literal, links keep their labels, images keep alt text, and code
* keeps its source text.
*/
function inlineText(node) {
	switch (node.type) {
		case "text":
		case "inlineCode":
		case "code": return node.value ?? "";
		case "image":
		case "imageReference": return node.alt ?? "";
		case "break": return "\n";
		case "html": return node.value ?? "";
		default: return node.children?.map(inlineText).join("") ?? "";
	}
}
function compactInline(text) {
	return text.replace(/\s+/g, " ").trim();
}
function blockText(node) {
	switch (node.type) {
		case "root":
		case "blockquote": return node.children?.map(blockText).filter(Boolean).join("\n\n") ?? "";
		case "paragraph":
		case "heading": return compactInline(inlineText(node));
		case "code": return node.value?.trim() ?? "";
		case "list": return node.children?.map(blockText).filter(Boolean).join("\n") ?? "";
		case "listItem": return node.children?.map(blockText).filter(Boolean).join(" ") ?? "";
		case "table": return node.children?.map(blockText).filter(Boolean).join("\n") ?? "";
		case "tableRow": return node.children?.map(blockText).join("	") ?? "";
		case "tableCell": return compactInline(inlineText(node));
		case "html": return node.value ?? "";
		case "thematicBreak":
		case "definition": return "";
		default: return compactInline(inlineText(node));
	}
}
function findFirstParagraph(node) {
	if (node.type === "paragraph") {
		const text = compactInline(inlineText(node));
		if (text !== "") return text;
	}
	for (const child of node.children ?? []) {
		const text = findFirstParagraph(child);
		if (text !== void 0) return text;
	}
}
function fullText(root) {
	return blockText(root).split("\n").map((line) => line.trim()).join("\n").replace(/\n{3,}/g, "\n\n").trim();
}
/**
* Parse GFM Markdown, remove its presentation markup, and preserve raw HTML literally.
* @param markdown - Markdown source.
* @param options - Optional extraction boundary.
* @returns Plain text for the whole document, first visible line, or first semantic paragraph.
*/
function extractMarkdownPlainText(markdown, options = {}) {
	const { mode = "all" } = options;
	const root = parseGfm(markdown);
	const all = fullText(root);
	switch (mode) {
		case "all": return all;
		case "first-line": return all.split("\n").find((line) => line !== "") ?? "";
		case "first-paragraph": return findFirstParagraph(root) ?? all.split("\n").find((line) => line !== "") ?? "";
	}
}
//#endregion
export { BrandWordmark, Button, CodeBlock, ConnectionBanner, DEFAULT_DIFF_MAX_LINES, DEFAULT_READ_MAX_LINES, DEFAULT_SEARCH_MAX_LINES, DEFAULT_TERMINAL_MAX_LINES, DiffBlock, DisclosureRow, FishLogo, HoverCard, IconAgentPresetOutline16, IconApiOutline14, IconArchiveOutline20, IconBranchOutline16, IconBrowseOutline16, IconCheckOutline14, IconCheckOutline16, IconChecklistOutline14, IconChevronDownOutline14, IconChevronLeftOutline14, IconChevronRightOutline14, IconChevronUpOutline14, IconCloseFill14, IconCloseOutline16, IconCodeOutline16, IconCopyOutline16, IconCordisPluginOutline14, IconDarkOutline16, IconDataOutline16, IconDislikeFill16, IconDislikeOutline16, IconDownloadOutline16, IconEditOutline16, IconEllipsisOutline16, IconEnhanceOutline16, IconFolderClose16, IconFolderOpen16, IconFolderOpenOutline16, IconFollowsystemOutline16, IconFullscreenOutline16, IconGlobeOutline14, IconGoalOutline16, IconInspectOutline12, IconLightOutline16, IconLikeFill16, IconLikeOutline16, IconLinkOutline14, IconLinkOutline16, IconListPenOutline16, IconLoadingOutline16, IconNewChatOutline16, IconPanelLeftOutline16, IconPaperclipOutline16, IconPauseOutline16, IconPersonalizationOutline16, IconPlayOutline16, IconPlusOutline16, IconProjectAddOutline16, IconQuestionOutline14, IconQueueOutline14, IconRefreshOutline14, IconRefreshOutline16, IconRightUpOutline14, IconRightUpOutline16, IconSearchOutline16, IconSendOutline14, IconSendOutline16, IconSettingsOutline14, IconSettingsOutline16, IconShareOutline16, IconSkillOutline16, IconSparkle16, IconStopFill16, IconThinkOutline14, IconThinkOutline16, IconTrashOutline16, IconTreeCorner8x10, IconTriangleRightFill14, IconUserOutline16, IconWarningOutline16, Input, JsonBlock, JsonTree, MarkdownText, Menu, MessageText, Modal, OnboardingSurface, Pill, ReadBlock, RiskConfirmation, SearchBlock, StateDot, TerminalBlock, Toast, Tooltip, WebBlock, extractMarkdownPlainText, useAnchoredMaxHeight, useAnchoredPosition, useDismissOnOutsidePointer, writeClipboard };

//# sourceMappingURL=index.js.map